export interface User {
  id: number;
  username: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  is_active: boolean;
  is_admin: boolean;
}

// A user row as seen by an admin reviewing signups.
// Derived server-side from is_active/rejected_at/disabled_at — see
// jcseo/api/admin_users.py. Don't re-derive it in the UI.
export type AdminUserStatus = 'unverified' | 'pending' | 'approved' | 'rejected' | 'disabled';

export interface AdminUser {
  id: number;
  username: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  is_active: boolean;
  is_admin: boolean;
  created_at: string;
  rejected_at: string | null;
  disabled_at: string | null;
  email_verified_at: string | null;
  status: AdminUserStatus;
}

/** Why a sign-in was refused (a login 403's `code`), or what a redirect back
 *  to /login wants said. Mirrors `_inactive_reason` in jcseo/api/auth.py. */
export type LoginNoticeCode = 'disabled' | 'rejected' | 'unverified' | 'pending' | 'google_failed';

export type AdminUserStatusFilter = AdminUserStatus | 'all';

// What deleting an account would cost, read before the confirmation dialog so
// nobody deletes one blind. `destroys` carries only non-zero counts, so an empty
// list means the account has nothing on it — the common case for stray signups.
// `blocked_by` is what RESTRICTs the delete (built pages); empty => it will go
// through. The backend owns both labels; the UI just renders them.
export interface AdminUserDeletionItem {
  label: string;
  count: number;
}

export interface AdminUserDeletionPreview {
  user_id: number;
  email: string;
  status: AdminUserStatus;
  destroys: AdminUserDeletionItem[];
  blocked_by: AdminUserDeletionItem[];
  deletable: boolean;
}

export interface AdminUserDeletionResult {
  user_id: number;
  email: string;
  destroyed: AdminUserDeletionItem[];
}

/**
 * Google Ads connection state. `connected` resolves the same way the backend
 * pipeline does, so it's true when a borrowed "house" connection is in play —
 * `is_borrowed` then says the current user can't disconnect or reconnect it.
 */
export interface GoogleAdsStatus {
  connected: boolean;
  auth_failed_at: string | null;
  is_borrowed: boolean;
  is_house: boolean;
  can_manage_house: boolean;
}

export interface GoogleAdsHouseGrant {
  user_id: number;
  username: string;
  email: string;
  granted: boolean;
  has_own_connection: boolean;
}

export interface RegisterData {
  email: string;
  first_name: string;
  last_name: string;
  password: string;
}

export interface ProfileUpdate {
  first_name: string;
  last_name: string;
}

/**
 * Which families of evidence the page builder may cite for a project.
 * Mirrors the backend `ResearchSourcePolicy`.
 *  - ALL: ranking competitors + the project's own knowledge base
 *  - KNOWLEDGE_ONLY: knowledge base only; no SERP crawl, no live web access
 *  - CRAWLED_ONLY: competitor facts only; the knowledge base isn't queried
 */
export type ResearchSourcePolicy = 'ALL' | 'KNOWLEDGE_ONLY' | 'CRAWLED_ONLY';

export interface Project {
  id: number;
  user_id: number;
  name: string;
  description: string | null;
  is_active: boolean;
  trusted_source_urls?: string[] | null;
  outline_use_angle_raw?: boolean;
  image_generation_enabled?: boolean;
  verify_citation_faithfulness?: boolean;
  research_sources?: ResearchSourcePolicy;
  /** Let a build that would deploy cleanly promote itself to READY_TO_DEPLOY. */
  auto_ready_on_clean_build?: boolean;
  /** Overrides the account-default brand for artifacts shared from this project. */
  brand_profile_id?: number | null;
  /** The boardroom this subject reads, if any. Set from the SUBJECT — the room
   *  has no control for it. While it is set the subject reads that room LIVE;
   *  nothing is copied across, so leaving the link in place is the point rather
   *  than a hand-off you complete. `null` unlinks. */
  boardroom_id?: number | null;
  created_at: string;
  updated_at: string;
  category_count: number;
}

export interface Category {
  id: number;
  project_id: number;
  name: string;
  description: string | null;
  slug: string | null;
  is_active: boolean;
  trusted_source_urls?: string[] | null;
  created_at: string;
  updated_at: string;
  topic_count: number;
}

export interface Topic {
  /** The node above this one. null = a root of the subject. */
  parent_id?: number | null;
  /** Which subject this node belongs to, carried on every node. */
  project_id?: number | null;
  /** What this node's id was as a category, so old `?category=N` links resolve. */
  legacy_category_id?: number | null;
  id: number;
  category_id: number;
  name: string;
  description: string | null;
  slug: string | null;
  is_active: boolean;
  content_guidelines: string | null;
  discovery_guidelines: string | null;
  include_keywords_in_build_context: boolean;
  trusted_source_urls?: string[] | null;
  created_at: string;
  updated_at: string;
  keyword_count: number;
}

export interface Keyword {
  id: number;
  value: string;
  source: string;
  description: string | null;
  is_ymyl: boolean;
  created_at: string;
  updated_at: string;
}

export interface ManagedPageSummary {
  id: number;
  status: PageBuildStatus | null;
  deployment_plan_id: number | null;
  deployment_plan_name: string | null;
  managed_site_id: number | null;
  managed_site_name: string | null;
}

/** Why a keyword's last search failed. See backend serps/search_failures.py. */
export interface SerpFailure {
  at: string;
  engines: string[];
  reason: string;
}

export interface AliasedChild {
  id: number;
  keyword_id: number;
  keyword_value: string;
  avg_monthly_searches: number | null;
  topic_relevance_score: number | null;
  serp_count: number;
  latest_serp_date: string | null;
  latest_serp_engine: string | null;
  has_pending_serp_request: boolean;
  /** The last search failed: when, on which engines, and why. */
  serp_failure?: SerpFailure | null;
  monthly_data: MonthlyDataPoint[];
  growth_pct: number | null;
  /** A person put this term on this page; the rebuild leaves it alone. */
  alias_locked: boolean;
}


/** One top-10 position, judged on how much the page there targets the
 *  keyword. See backend keywords/serp_gap.py. */
export interface SerpGapPosition {
  /** 1 = top result. */
  position: number;
  url: string;
  domain: string;
  /** Places using the keyword: title | url | h1 | opening | snippet - only
   *  the places your keyword settings check. */
  used: string[];
  /** Places we could check (H1 and opening need the page downloaded). */
  checked: string[];
  /** Share of checked places using the keyword; null when none were checked. */
  targeting_pct: number | null;
  big_site: boolean;
  home_page?: boolean;
  forum?: boolean;
  is_open: boolean;
  /** Why it is or is not open: big site · home page · forum · not checked ·
   *  uses it · does not use it. */
  why?: string | null;
}

/** Which of one engine's top-10 positions are open. */
export interface SerpGap {
  search_engine: string;
  serp_id: number;
  open_positions: number;
  best_open_position: number | null;
  pages_pending: number;
  computed_at: string;
  positions: SerpGapPosition[];
}

/** Filters on open positions. A keyword passes when one engine passes all. */
export interface SerpGapFilter {
  minOpen: number | null;
  /** An open position at this rank or higher (1 = top result). */
  bestWithin: number | null;
  engine: string | null;
}

export interface TopicKeyword {
  id: number;
  topic_id: number;
  keyword_id: number;
  keyword_value: string;
  topic_relevance_score: number | null;
  page_build_run_id: number | null;
  avg_monthly_searches: number | null;
  avg_cpc: number | null;
  competition_index: number | null;
  competition_level: string | null;
  // The ★ — a small, not-heavily-contested term. Decided server-side in
  // keywords/winnable.py so the star and the "★ only" filter agree.
  winnable?: boolean;
  winnable_reason?: string | null;
  /** Per engine, most winnable first. Empty until a SERP is measured. */
  serp_gaps?: SerpGap[];
  serp_count: number | null;
  monthly_data: MonthlyDataPoint[];
  growth_pct: number | null;
  // SERP info
  latest_serp_date: string | null;
  latest_serp_engine: string | null;
  has_pending_serp_request: boolean;
  /** The last search failed: when, on which engines, and why. */
  serp_failure?: SerpFailure | null;
  created_at: string;
  updated_at: string;
  managed_pages: ManagedPageSummary[];
  // Alias/map parent relationships
  alias_parent_id: number | null;
  map_parent_id: number | null;
  aliased_children: AliasedChild[];
  /** A person decided this row's grouping — merged onto a page, or split off. */
  alias_locked: boolean;
  user_pinned: boolean;
  move_count: number;
  last_moved_at: string | null;
  // Nested objects returned by detailed endpoint
  keyword?: { id: number; value: string };
  topic?: { id: number; name: string };
}

export interface SERPRequestResponse {
  id: number;
  keyword_id: number;
  user_id: number;
  granted_at: string | null;
  created_at: string;
}

export interface TopicKeywordPaginatedResponse {
  items: TopicKeyword[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface KeywordCreateResponse extends Keyword {
  topic_keyword: TopicKeyword;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
}

export interface ProjectFormData {
  name: string;
  description: string;
  is_active: boolean;
}

export interface CategoryFormData {
  name: string;
  description: string;
  slug: string;
  project_id: string;
  is_active: boolean;
}

// ============= Coverage Score Types =============
export interface CoverageScore {
  score: number;
  linked_count: number;
  unlinked_relevant_count: number;
  total_relevant_count: number;
}

// ============= Keyword Matching Types (Paste Ideas) =============
export interface KeywordMatchResult {
  input_keyword: string;
  match_type: 'exact' | 'semantic' | 'no_match';
  matched_keyword?: Keyword;
  similarity_score?: number;
  already_linked: boolean;
}

export interface KeywordMatchResponse {
  results: KeywordMatchResult[];
  recommendation_id?: number;
}

// ============= AI Suggestions Types =============
export interface AISuggestion {
  keyword: string;
  keyword_id?: number;
  source: 'semantic_match' | 'llm_generated';
  similarity_score?: number;
  why_suggested: string;
}

// ============= Unlinked Keywords Types =============
export interface UnlinkedKeyword {
  id: number;
  value: string;
  computed_relevance: number;
  avg_monthly_searches?: number;
  source?: string;
}

// ============= Topic Label Types =============
export interface TopicLabel {
  id: number;
  label_id: number;
  topic_id: number;
  label_value: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface TopicLabelsResponse {
  labels: TopicLabel[];
}

// ============= SERP Types =============
export interface SERPEntry {
  id: number;
  serp_id: number;
  page_id: number;
  position: number;
  title: string;
  description: string | null;
  is_ad: boolean;
  page_url: string | null;
}

export interface SERP {
  id: number;
  keyword_id: number;
  keyword_value: string | null;
  search_engine: string;
  url: string | null;
  collected_at: string;
  created_at: string;
  updated_at: string;
}

// ============= Domain Types =============
export interface Domain {
  id: number;
  name: string;
  registered_name: string;
  suffix: string;
  registration_date: string | null;
  first_seen: string;
  created_at: string;
  updated_at: string;
}

// ============= Domain Metrics Types =============
export interface DomainMetrics {
  id: number;
  domain_id: number;
  total_pages: number | null;
  referring_domains: number | null;
  opr_overall_rank: number;
  opr_page_rank: number;
  created_at: string;
  updated_at: string;
}

// ============= Page Types =============
export interface Page {
  id: number;
  domain_id: number;
  domain_name: string | null;
  url: string;
  title: string | null;
  ai_summary: string | null;
  facts: string[] | null;
  processing_state: number;
  page_type: string | null;
  first_seen: string;
  last_crawled: string | null;
  last_status_code: number | null;
  domain_metrics?: DomainMetrics | null;
  created_at: string;
  updated_at: string;
}

export interface PageDetail extends Page {
  content: string | null;
  extracted_text: string | null;
  schema_data: string | null;
  has_embedding: boolean;
}

export interface PageExtractFactsResponse {
  page_id: number;
  queued: boolean;
}

export interface HeroCandidate {
  prompt: string;
  alt_text: string;
  // 'angle' = a live option's brief; 'generated' = a prompt that outlived the
  // angle that produced it, kept alive by the image it made.
  origin: 'angle' | 'generated';
  angle_id: number | null;
  strategic_move: string | null;
  is_selected_angle: boolean;
  image_id: number | null;
  image_url: string | null;
  pending: boolean;
  generation_error: string | null;
  is_current_hero: boolean;
}

export interface HeroImageState {
  page_id: number;
  // What the SELECTED angle asks for. Empty when the angle predates the field.
  brief: string;
  alt_text: string;
  image_id: number | null;
  image_url: string | null;
  source: 'angle' | 'user' | null;
  // image_id set + image_url null = generation still running.
  pending: boolean;
  generation_error: string | null;
  // The image came from a different brief than the selected angle now asks
  // for. Informational — the phase is NOT stale because of it.
  from_earlier_angle: boolean;
  candidates: HeroCandidate[];
}

export interface EnsureExtractedResponse {
  keyword_id: number;
  // Exactly the pages the sweep will work on — mark these busy and let each
  // page's terminal page_facts_extracted event clear it.
  page_ids: number[];
  queued: boolean;
}

export interface PagePaginatedResponse {
  items: Page[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

// ============= Page Keyword SERP Info =============
export interface PageKeywordSerpInfo {
  keyword_id: number;
  keyword_value: string;
  serp_count: number;
  latest_serp_date: string | null;
  latest_position: number | null;
  search_engine: string | null;
}

// ============= Enhanced SERP with Page Details =============
export interface SERPEntryWithPage {
  id: number;
  serp_id: number;
  page_id: number;
  position: number;
  title: string;
  description: string | null;
  is_ad: boolean;
  page: Page | null;
}

export interface SERPDetailWithPages extends SERP {
  entries: SERPEntryWithPage[];
}

// ============= Keyword Cluster Types =============
export interface TopicKeywordClusterMember {
  id: number;
  topic_keyword_id: number;
  keyword_value: string | null;
  created_at: string;
  updated_at: string;
}

export interface TopicKeywordCluster {
  id: number;
  topic_id: number;
  topic_name: string | null;
  intent: string | null;
  members: TopicKeywordClusterMember[];
  created_at: string;
  updated_at: string;
}

export interface TopicKeywordClusterEdge {
  id: number;
  parent_cluster_id: number;
  child_cluster_id: number;
  is_bidirectional: boolean;
  created_at: string;
  updated_at: string;
}

export interface TopicKeywordClusterDetail extends TopicKeywordCluster {
  parent_edges: TopicKeywordClusterEdge[];
  child_edges: TopicKeywordClusterEdge[];
}

export interface TopicKeywordClusterCreate {
  topic_id: number;
  intent?: string;
}

export interface TopicKeywordClusterPaginatedResponse {
  items: TopicKeywordCluster[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

// ============= Managed Site Types =============
export type ManagedSiteType = 'WORDPRESS' | 'HUGO' | 'MANUAL';

export interface ManagedSite {
  id: number;
  key: string;
  name: string;
  registered_name: string;
  description: string | null;
  registration_date: string | null;
  site_type: ManagedSiteType;
  /**
   * Where the site is SERVED, e.g. "https://news4u.com" or
   * "https://news4u.com/blog". Distinct from `registered_name`, which is the
   * domain we track for SEO and may be bare, and from a repo's `path_prefix`,
   * which is a directory in the repo and not part of any URL.
   *
   * Null means nobody has said. Pages on your other sites then cannot link
   * here at all — there is nothing to build an absolute URL out of, so those
   * links are dropped rather than guessed at.
   */
  public_base_url: string | null;
  /** Must a person read a page before it can publish here? Off by default:
   *  turning it on globally would block every page already marked ready. */
  require_review: boolean;
  settings: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface ManagedSiteCreate {
  name: string;
  registered_name: string;
  description?: string;
  registration_date?: string;
  site_type?: ManagedSiteType;
  public_base_url?: string;
  settings?: Record<string, unknown>;
}

export interface ManagedSiteUpdate {
  name?: string;
  registered_name?: string;
  description?: string;
  registration_date?: string;
  site_type?: ManagedSiteType;
  public_base_url?: string;
  require_review?: boolean;
  settings?: Record<string, unknown>;
}

// ============= SERP Competition Types =============
export interface CompetitiveSiteInfo {
  site_id: number;
  site_name: string;
  opr: number;
  opr_ratio: number;
  pr: number;
  pr_ratio: number;
  competitive_score: number;
}

export interface SERPCompetitionResponse {
  serp_id: number;
  competitions: Record<string, CompetitiveSiteInfo[]>;
}

// ============= Site Type Settings =============
/**
 * Publishing policy every deploy target shares. Mirrors
 * `services/publish_options.py`, which reads it off the same `settings` blob.
 * Absent means the default, which reproduces today's output exactly.
 */
export interface PublishSettings {
  citation_style?: CitationStyle;
}

export interface WordPressSettings extends PublishSettings {
  url: string;
  username: string;
  password: string;
}

/**
 * Hugo target settings.
 *
 * There is deliberately no repo URI or deploy key here. Deployment goes through
 * the GitHub App: the repo linked with role=PRIMARY supplies owner/name/branch/
 * path_prefix and an installation token, and content is written via the GitHub
 * REST API — no clone, no SSH. An SSH-based path existed before that
 * integration; its fields survived in this interface long after anything read
 * them, so the form was collecting a live private key that no deploy used.
 */
export interface HugoSettings extends PublishSettings {
  // Front matter keys this site's theme reads for the page's header image.
  // Themes disagree (`image`, `featured_image`, `cover`, …), so the key is
  // configured per target rather than guessed. Blank = don't emit it.
  hero_image_key?: string;
  hero_alt_key?: string;
}

export interface ManualSettings {
  notes: string;
}

export type SiteSettings =
  WordPressSettings | HugoSettings | ManualSettings | null;

// ============= Deployment Plan Types =============
export type DeploymentPlanState =
  'EDITING' | 'ACTIVE' | 'INACTIVE' | 'ERROR' | 'DEPLOYING';

export interface DeploymentPlan {
  id: number;
  name: string;
  description: string | null;
  plan: string | null;
  managed_site_id: number | null;
  managed_site_name: string | null;
  state: DeploymentPlanState | null;
  last_run_details: string | null;
  created_at: string;
  updated_at: string;
  /** Every page ever attached. A plan is never emptied — a deployed page stays
   *  in it as history, so this only ever grows. */
  page_count?: number;
  /** READY_TO_DEPLOY: exactly what the next deploy would send. */
  ready_count?: number;
  /** Already published to the site. */
  live_count?: number;
  /** MAX(page.last_deployed_at) — derived, since the plan has no such column
   *  and `updated_at` moves whenever somebody renames it. */
  last_deployed_at?: string | null;
}

export interface DeploymentValidationIssue {
  page_id: number;
  keyword_value: string | null;
  reason: string;
}

export interface DeploymentValidationResponse {
  plan_id: number;
  ready_count: number;
  valid_count: number;
  issues: DeploymentValidationIssue[];
  content_repo_configured: boolean;
  error: string | null;
  /**
   * Things that will not stop this deploy but cost something afterwards.
   * Deliberately separate from `issues`: those are pages that will FAIL, and
   * mixing the two makes the failures easier to miss.
   */
  advisories: string[];
}

export interface DeploymentStartResponse {
  plan_id: number;
  message: string;
  state: DeploymentPlanState;
}

export interface DeploymentPlanCreate {
  managed_site_id: number;
  name: string;
  description?: string;
  plan?: string;
}

export interface DeploymentPlanUpdate {
  name?: string;
  description?: string;
  plan?: string;
  managed_site_id?: number;
}

export interface DeploymentPlanPaginatedResponse {
  items: DeploymentPlan[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

// ============= Managed Page Types =============
export type PageStyle =
  | 'ARTICLE'
  | 'GUIDE'
  | 'CHECKLIST'
  | 'LISTICLE'
  | 'FAQ'
  | 'COMPARISON'
  | 'REVIEWS'
  | 'PRODUCT_PAGE'
  | 'PRICING_PAGE'
  | 'SUPPORT_ARTICLE'
  | 'DOCUMENTATION'
  | 'TOOL'
  | 'FORUM_DISCUSSION'
  | 'VIDEO'
  | 'TEMPLATE';

export type PageBuildStatus =
  | 'IN_PROGRESS'
  | 'WAITING_FOR_USER_ACTION'
  | 'WAITING_FOR_SYSTEM_ACTION'
  | 'READY_TO_DEPLOY'
  | 'COMPLETED'
  | 'FAILED'
  | 'CANCELLED'
  | 'PENDING'
  | 'QUEUED'
  | 'SKIPPED'
  | 'ERROR'
  | 'DRAFT';

export interface ManagedPage {
  id: number;
  topic_keyword_id: number;
  keyword_value: string | null;
  topic_id: number | null;
  topic_name: string | null;
  topic_content_guidelines: string | null; // Guidelines from topic level
  category_id: number | null;
  category_name: string | null;
  category_slug: string | null;
  project_id: number | null;
  project_name: string | null;
  topic_slug: string | null;
  /**
   * Where the page publishes on the SITE — ancestor slugs, root first.
   *
   * Distinct from category_slug/topic_slug above, which say where it came from
   * in the research outline. They start identical and diverge the moment either
   * one is edited: the outline seeds this once, and the page owns it after.
   */
  path_segments: string[];
  /** "build" (seeded from the node) | "user" (edited). A rebuild won't overwrite "user". */
  path_source: string | null;
  title: string | null;
  slug: string | null;
  description: string | null;
  page_style: PageStyle;
  // "angle" when the Angle phase chose it, "user" when a human pinned it,
  // null when nobody has decided (the ARTICLE default).
  page_style_source?: 'angle' | 'user' | null;
  status: PageBuildStatus | null;
  prd: Record<string, unknown> | null;
  content: string | null;
  front_matter: string | null;
  content_guidelines: string | null; // Page-specific guidelines
  outline: string | null;
  current_content_version_id: number | null; // ID of the current content version
  hil_approved_at: string | null;
  deployment_plan_id: number | null;
  deployment_plan_name: string | null;
  managed_site_id: number | null;
  managed_site_name: string | null;
  /** Only HUGO publishes under the path — see pathRules. */
  managed_site_type: ManagedSiteType | null;
  /** Set once the page is live. A path change after this breaks inbound links. */
  last_deployed_at: string | null;
  /** Where it stands in search — see SiteSearchPanel. Null until published. */
  search_state?: PageSearchState | null;
  deployment_result: string | null;
  content_generation_output: string | null;
  research_evidence: ResearchEvidenceEntry[] | null;
  created_at: string;
  updated_at: string;
}

export interface ResearchEvidenceEntry {
  marker_number: number;
  fact: string;
  source_description?: string | null;
  source_url?: string | null;
  method?: 'crawl' | 'search' | 'recorded' | string;
  fetched_at?: string | null;
}

// ============= Phased page builder =============

// 'final' is a UI-only phase — not an agent phase. It surfaces the
// rendered article (title + description + slug + body) for review and
// optional manual editing, after the agent's write/ground phases have
// produced something. The backend doesn't return a status row for it;
// the frontend synthesizes one from whether content exists.
// Order matters — it drives the stepper and the Next button. `image` sits last
// among the real phases (its input is the angle, but slotting it there would
// interrupt the writing flow); `final` is UI-only, appended client-side.
export type BuildPhase =
  'research' | 'angle' | 'outline' | 'write' | 'ground' | 'seo' | 'image' | 'final';

export type StrategicMove =
  | 'match_and_beat'
  | 'aggregate_up'
  | 'educate_down'
  | 'synthesize_across'
  | 'differentiate';

export interface AngleFraming {
  audience: string;
  intent: string;
  north_star: string;
  subject: string;
  entity: string;
  keyword_shape: string;
}

export interface AngleKeyPoint {
  key_point: string;
  source_url?: string;
  source_title?: string;
}

export interface AngleSection {
  h2: string;
  points: AngleKeyPoint[];
}

export interface Angle {
  strategic_move: StrategicMove | string;
  reasoning: string;
  // The PageStyle we will author, chosen by the Angle phase from what the
  // system can generate. `recommended_page_type` is the legacy key, still
  // present on angles committed before the switch.
  page_style?: string;
  recommended_page_type?: string;
  framing: AngleFraming;
  grouped_key_points: AngleSection[];
  differentiators: string[];
  generated_at?: string;
  model?: string;
}

export interface AngleGeneratedEvent {
  event_type: 'angle_generated';
  page_id: number;
  status: 'started' | 'completed' | 'failed';
  strategic_move?: string;
  page_style?: string;
  section_count?: number;
  evidence_rows?: number;
  framing_updated?: boolean;
  message?: string;
}

export interface PhaseStatus {
  phase: BuildPhase;
  state: 'pending' | 'ready' | 'stale';
  last_run_at?: string | null;
  summary?: string | null;
  // Why this phase can't run right now, in words fit for a tooltip. Null or
  // absent = runnable. Set when a phase has no input at all, so running it
  // could only fail — the header image with no angle brief, for instance.
  blocked_reason?: string | null;
}

export type KeywordShape =
  'category' | 'specific_product' | 'comparison' | 'informational' | 'transactional';

export const KEYWORD_SHAPES: KeywordShape[] = [
  'category',
  'specific_product',
  'comparison',
  'informational',
  'transactional',
];

export interface FramingArtifact {
  keyword_shape?: KeywordShape | string | null;
  subject?: string | null;
  entity?: string | null;
  audience?: string | null;
  intent?: string | null;
  north_star?: string | null;
}

export interface InjectionMapEntry {
  label: string;
  description: string;
  where: string;
  count?: number | null;
}

export interface BuildStateResponse {
  page_id: number;
  keyword?: string | null;
  // Numeric ids for the primary keyword + topic-keyword cluster — drive the
  // SERP slideout (keyed by keyword_id) in the workflow header.
  keyword_id?: number | null;
  topic_keyword_id?: number | null;
  page_status?: string | null;
  // Which phase is currently running ("research" | "angle" | "outline" |
  // "write" | "ground"), else null. Set for individually-triggered phases
  // AND for each phase a full end-to-end build reaches, so the stepper can
  // show live progress either way.
  current_phase?: string | null;
  // ISO timestamp of when that phase started. Durable, so elapsed time is
  // correct after a refresh instead of restarting from zero.
  current_phase_started_at?: string | null;
  // A phase is marked running but the row has gone quiet for longer than a
  // live worker ever does — the worker probably died (crash / restart /
  // deploy). Not auto-cleared server-side; the UI offers the fix.
  current_phase_stalled?: boolean;
  current_phase_silent_seconds?: number | null;
  // Duration of each phase's LAST run, keyed by phase name.
  phase_runtimes?: Record<string, { seconds: number; finished_at: string }>;
  // The page's EFFECTIVE style and who chose it ("angle" | "user" | null).
  // The angle artifact has its own page_style recording what THAT phase
  // picked; these say what the page will actually be authored as.
  page_style?: string | null;
  page_style_source?: string | null;
  // Who chose the deployed slug: "user" (pinned — builds leave it alone),
  // "build", or null (derived from the keyword).
  slug_source?: string | null;
  // The rest of the URL — ancestor slugs, root first. The panel showed the
  // slug alone and called it "URL", which is only true at the site root.
  path_segments?: string[];
  path_source?: string | null;
  // Only HUGO puts the path in the URL — see pathRules.
  managed_site_type?: ManagedSiteType | null;
  // Scopes the path editor's suggestions to this site's existing structure.
  managed_site_id?: number | null;
  // Live? Moving a published page breaks every link to its old URL.
  last_deployed_at?: string | null;
  // Parent-chain ids + names resolved server-side so the UI can render
  // breadcrumbs / the sibling sidebar without a second page fetch.
  topic_id?: number | null;
  topic_name?: string | null;
  category_id?: number | null;
  category_name?: string | null;
  project_id?: number | null;
  project_name?: string | null;
  // Project-level toggle: when True, outline phase skips the LLM and
  // projects page.angle directly. Surfaced here so the OutlinePanel
  // checkbox doesn't need a separate /projects/{id} fetch.
  outline_use_angle_raw?: boolean;
  phases: PhaseStatus[];

  angle?: Angle | null;
  framing?: FramingArtifact | null;
  outline?: string | null;
  content?: string | null;
  front_matter?: string | null;
  title?: string | null;
  description?: string | null;
  slug?: string | null;
  research_evidence?: ResearchEvidenceEntry[] | null;
  compliance_report?: { passed?: boolean; violations?: unknown[] } | null;
  // Grounding output — the fact-check, as opposed to compliance_report's
  // structural spec check. Absent until a ground pass has run.
  grounding_flags?: {
    flags?: Array<{ claim?: string; issue?: string }>;
    approved?: boolean;
    checked_at?: string;
    // Per-citation entailment counts, e.g. {SUPPORTED: 18, NOT_SUPPORTED: 2}.
    faithfulness?: Record<string, number> | null;
    // Sections the revision pass rewrote. 0 = the draft is unchanged.
    revised_sections?: number;
    // The version the revision started from, so the UI can diff old -> new.
    revised_from_version_id?: number | null;
    // False when the run had no structured document and re-parsed
    // citation-stripped prose, so nothing could be checked against a source.
    citations_visible?: boolean;
  } | null;
  // SEO phase output. corrections were committed to the document; suggestions
  // are eval-rejected edits kept for review. Absent until an SEO pass has run.
  seo_report?: {
    score_before?: number;
    score_after?: number;
    checks_before?: SEOCheck[];
    checks_after?: SEOCheck[];
    corrections?: SEOCorrection[];
    suggestions?: SEOCorrection[];
  } | null;
  current_content_version_id?: number | null;
  injection_map?: InjectionMapEntry[] | null;
  // Resolved model labels keyed by StageEnum value
  // ("GENERATE_ARTICLE" / "EDIT_ARTICLE"). Null when the user has no
  // model configured for that stage and no DEFAULT fallback either.
  // Drives the model-chip pills in the workflow header.
  // Extraction coverage for the page's keyword — drives the crawl gate.
  crawl_readiness?: CrawlReadiness | null;
  // How many angle options exist (>1 → the angle picker is offered).
  angle_option_count?: number | null;
}

export interface CrawlReadiness {
  has_serps: boolean;
  total: number;
  extracted: number;
  extracting: number;
  pending: number;
  failed: number;
}

export interface AngleOption {
  id: number;
  is_selected: boolean;
  sort_order: number;
  angle: Angle;
}

export interface ManagedPageCreate {
  topic_keyword_id?: number;
  title?: string;
  slug?: string;
  description?: string;
  page_style?: PageStyle;
  deployment_plan_id?: number;
  content_guidelines?: string;
}

export interface ManagedPageBulkCreate {
  topic_keyword_ids: number[];
  page_style?: PageStyle;
  deployment_plan_id?: number;
  /**
   * One path for the whole batch — omit to seed each page from its own node.
   * `[]` means the site root and is different from omitting.
   */
  path_segments?: string[];
}

export interface SiteStructurePageOut {
  id: number;
  /** True for the page that IS this section — it publishes AT the path. */
  is_section_index?: boolean;
  title: string | null;
  slug: string | null;
  path_segments: string[];
  last_deployed_at: string | null;
  state: string | null;
  subject_name: string | null;
}

export interface SiteStructure {
  site_id: number;
  site_name: string;
  /** False for WordPress / Manual — the path is filed but not in the URL. */
  nested: boolean;
  pages: SiteStructurePageOut[];
}

export interface SiteStructureMoveResult {
  moved: number;
  published_moved: number;
  to_path: string[];
}

export interface TopicPagePath {
  segments: string[];
  /** `A / B` in node NAMES, for explaining where the seed came from. */
  display: string;
}

export interface PathSegmentSuggestion {
  segment: string;
  page_count: number;
}

export interface PathSegmentSuggestions {
  prefix: string[];
  suggestions: PathSegmentSuggestion[];
}

export interface ManagedPageBulkCreateByFilter {
  topic_id: number;
  page_style?: PageStyle;
  path_segments?: string[];
  deployment_plan_id?: number;
  keyword_search?: string;
  semantic_search?: string;
  min_sem_similarity?: number;
}

export interface ManagedPageUpdate {
  /**
   * Where the page publishes — ancestor slugs, root first.
   *
   * Send it ONLY when it changed: the server stamps path_source="user", which
   * stops a later rebuild refiling the page. Sending it on every save would
   * pin pages nobody deliberately moved.
   */
  path_segments?: string[];
  title?: string;
  slug?: string;
  description?: string;
  // null CLEARS the override and hands the style back to the Angle phase.
  // Distinct from omitting the field, which leaves it untouched.
  page_style?: PageStyle | null;
  status?: PageBuildStatus;
  prd?: Record<string, unknown>;
  content?: string;
  front_matter?: string;
  content_guidelines?: string;
  outline?: string | null;
  deployment_plan_id?: number;
  managed_site_id?: number;
}

export interface ManagedPageBulkCreateResponse {
  created_count: number;
  pages: ManagedPage[];
}

/**
 * Result of a bulk page update. `skipped_count` is non-zero when the server
 * declined part of the batch — currently only pages already mid-build, which
 * must not be handed back to the queue while a worker holds them. Surface it,
 * or a batch that did less than asked reads as a clean success.
 */
export interface BulkPageUpdateResult {
  updated_count: number;
  skipped_count?: number;
  skipped_reason?: string;
}

export interface ManagedPagePaginatedResponse {
  items: ManagedPage[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

// ============= LLM Settings Types =============
/** `OLLAMA_CLOUD` is the HOSTED service, which speaks OpenAI at a fixed endpoint —
 *  not the same as pointing `CHAT_OLLAMA` at ollama.com, which loses native tool
 *  calls. The base URL is not user-configurable for it. */
export type LLMInterfaceType =
  'CHAT_OLLAMA' | 'CHAT_ANTHROPIC' | 'CHAT_OPENAI' | 'GROQ' | 'OLLAMA_CLOUD' | 'GEMINI';

// A provider owns the connection (interface type / base URL / API key) shared
// by all its models. Enter the key once, select the provider per model.
export interface LLMProvider {
  id: number;
  user_id: number | null;
  name: string;
  llm_interface_type: LLMInterfaceType;
  base_url: string | null;
  api_key?: string | null; // decrypted, owner/admin only
  has_api_key: boolean;
  is_system: boolean;
  // Paused keys stay on file but drop out of the pool.
  enabled: boolean;
  /** The stage bench may run models on it — even while `enabled` is off. */
  bench_enabled?: boolean;
  model_count: number;
  // Account/tier-wide caps applied to every model on this provider; the run
  // uses the lower of these and the model's own limits.
  rate_limit_rpm: number | null;
  rate_limit_rpd: number | null;
  rate_limit_tpm: number | null;
  rate_limit_tpd: number | null;
  // The cost book, provider side (admin): how it costs us, the plan fee for a
  // flat plan, and the quotas the margin screen gauges against.
  cost_basis: CostBasis;
  plan_monthly_usd: number | null;
  quota_tokens_day: number | null;
  quota_tokens_week: number | null;
  quota_tokens_month: number | null;
  created_at: string;
  updated_at: string;
}

export type CostBasis = 'metered' | 'flat_plan' | 'self_hosted';

export interface LLMProviderCreate {
  name: string;
  llm_interface_type: LLMInterfaceType;
  base_url?: string | null;
  api_key?: string | null;
  is_system?: boolean;
  enabled?: boolean;
  bench_enabled?: boolean;
  rate_limit_rpm?: number | null;
  rate_limit_rpd?: number | null;
  rate_limit_tpm?: number | null;
  rate_limit_tpd?: number | null;
  cost_basis?: CostBasis;
  plan_monthly_usd?: number | null;
  quota_tokens_day?: number | null;
  quota_tokens_week?: number | null;
  quota_tokens_month?: number | null;
}

export interface LLMProviderUpdate {
  name?: string;
  llm_interface_type?: LLMInterfaceType;
  base_url?: string | null;
  api_key?: string | null;
  enabled?: boolean;
  bench_enabled?: boolean;
  rate_limit_rpm?: number | null;
  rate_limit_rpd?: number | null;
  rate_limit_tpm?: number | null;
  rate_limit_tpd?: number | null;
  cost_basis?: CostBasis;
  plan_monthly_usd?: number | null;
  quota_tokens_day?: number | null;
  quota_tokens_week?: number | null;
  quota_tokens_month?: number | null;
}

export interface LLMProviderListResponse {
  items: LLMProvider[];
}

export interface LLMSettings {
  id: number;
  user_id: number | null;
  // Display label derived from provider + model_name.
  display_name: string;
  model_name: string;
  // Connection fields below are derived from the linked provider.
  provider_id: number | null;
  llm_interface_type: LLMInterfaceType | null;
  base_url: string | null;
  description: string | null;
  is_system: boolean;
  cost_per_input_token: number | null;
  cost_per_output_token: number | null;
  // What a customer pays for this model's tokens: the rate card's tier.
  price_tier: ModelPriceTier | string;
  api_key?: string | null;
  provider?: string | null;
  rate_limit_rpm?: number | null;
  rate_limit_rpd?: number | null;
  rate_limit_tpm?: number | null;
  rate_limit_tpd?: number | null;
  supports_tools: boolean;
  supports_images: boolean; // image generation (output)
  supports_vision: boolean; // image input (vision)
  /** False: listed but never run — out of the pool and the stage bench. */
  enabled?: boolean;
  per_image_cost?: number | null;
  max_tokens?: number | null;
  is_highly_quantized_model: boolean;
  // Auto-tuned by the recalibrate-extraction endpoint. Read-only in the UI.
  extraction_chunk_max_chars?: number | null;
  extraction_require_evidence?: boolean | null;
  extraction_parallel_chunks?: number | null;
  extraction_max_chunks_per_page?: number | null;
  extraction_last_calibrated_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface LLMSettingsTestRequest {
  provider_id?: number | null;
  model_name: string;
  llm_interface_type?: LLMInterfaceType | null;
  base_url?: string | null;
  api_key?: string | null;
}

export interface LLMSettingsTestResponse {
  ok: boolean;
  latency_ms: number;
  response?: string | null;
  error?: string | null;
}

export interface LLMSettingsCreate {
  provider_id: number;
  model_name: string;
  description?: string;
  cost_per_input_token?: number | null;
  cost_per_output_token?: number | null;
  price_tier?: ModelPriceTier;
  rate_limit_rpm?: number | null;
  rate_limit_rpd?: number | null;
  rate_limit_tpm?: number | null;
  rate_limit_tpd?: number | null;
  supports_tools?: boolean;
  supports_images?: boolean;
  supports_vision?: boolean;
  per_image_cost?: number | null;
  max_tokens?: number | null;
  is_highly_quantized_model?: boolean;
  enabled?: boolean;
}

export interface LLMSettingsUpdate {
  model_name?: string;
  provider_id?: number;
  description?: string;
  cost_per_input_token?: number | null;
  cost_per_output_token?: number | null;
  price_tier?: ModelPriceTier;
  rate_limit_rpm?: number | null;
  rate_limit_rpd?: number | null;
  rate_limit_tpm?: number | null;
  rate_limit_tpd?: number | null;
  supports_tools?: boolean;
  supports_images?: boolean;
  supports_vision?: boolean;
  per_image_cost?: number | null;
  max_tokens?: number | null;
  is_highly_quantized_model?: boolean;
  enabled?: boolean;
}

export interface LLMUsageReportRow {
  period_label: string;
  llm_id: number;
  llm_name: string;
  model_name: string;
  provider: string | null;
  call_count: number;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  cached_input_tokens: number; // prompt-cache reads within input, priced ~0.1x
  cost_usd: number | null;
}

export interface LLMUsageReport {
  rows: LLMUsageReportRow[];
  period_start: string | null; // "YYYY-MM-DD" of the queried period
  total_input_tokens: number;
  total_output_tokens: number;
  total_tokens: number;
  total_cached_input_tokens: number;
  total_cost_usd: number | null;
}

export interface LLMUsageTimeseriesBucket {
  label: string;
  call_count: number;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
}

export interface LLMUsageCallRow {
  id: number;
  created_at: string;
  llm_name: string;
  model_name: string;
  provider: string | null;
  stage: string;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  cached_input_tokens: number;
  cost_usd: number | null;
  duration_seconds: number | null;
}

export interface LLMUsageCalls {
  calls: LLMUsageCallRow[];
  total_count: number;
}

export interface LLMUsageTimeseries {
  buckets: LLMUsageTimeseriesBucket[];
  period_start: string | null;
  rate_limit_rpm: number | null;
  rate_limit_rpd: number | null;
  rate_limit_tpm: number | null;
  rate_limit_tpd: number | null;
}

export interface LLMSettingsListResponse {
  items: LLMSettings[];
}

export interface LLMSettingsOption {
  id: number;
  display_name: string;
  model_name: string;
  provider_id: number | null;
  llm_interface_type: LLMInterfaceType | null;
  description: string | null;
  is_system: boolean;
  provider: string | null;
  supports_tools: boolean;
  supports_images: boolean;
  supports_vision: boolean;
}

// ============= User LLM Stage Types =============
// A stage's wire value. Deliberately NOT a hand-mirrored union of the backend
// StageEnum: every value here arrives at runtime from /llm-settings/stages/enum
// (typed `string` on StageEnumOption.value) and is cast on the way into the
// form, so the union constrained nothing while demanding a hand-sync on every
// stage rename — and had already drifted by five phantom members before anyone
// noticed. The backend enum is the one source of truth.
export type StageEnum = string;

export type PipelineVersionStatus = 'DRAFT' | 'STABLE' | 'RETIRED';

export interface PipelineVersion {
  id: number;
  version: number | null;
  name: string;
  status: PipelineVersionStatus;
  promoted_at: string | null;
  notes: string | null;
  created_at: string | null;
  stage_count: number;
  copied_from_id: number | null;
}

export interface PipelineVersionList {
  items: PipelineVersion[];
  /** The house version the admin is testing (their active profile), if any. */
  testing_id: number | null;
  stable_id: number | null;
}

export interface PipelineDiffRow {
  stage: string;
  archetype: string | null;
  change: 'added' | 'removed' | 'changed';
  before: string | null;
  after: string | null;
}

/** One line of a house version: "for this stage (and seat), this model name
 * works". A pattern is an exact name or a glob (`gemma4:*`). */
export interface StageModelRule {
  id: number;
  stage: string;
  archetype: string | null;
  model_pattern: string;
  enabled: boolean;
  /** Null = the model's own default. Per rule, not per job. */
  temperature: number | null;
  top_p: number | null;
  notes: string | null;
  /** House model names the pattern matches right now (any provider). */
  matches: string[];
}

export interface StageModelRuleIn {
  stage: string;
  archetype?: string | null;
  model_pattern: string;
  enabled?: boolean;
  temperature?: number | null;
  top_p?: number | null;
  notes?: string | null;
}

export interface PoolCandidate {
  llm_settings_id: number;
  model_name: string;
  display_name: string;
  provider_id: number;
  provider_name: string;
  llm_interface_type: string;
  own: boolean;
}

export interface PoolCandidates {
  patterns: string[];
  from_stage: string | null;
  from_archetype: string | null;
  candidates: PoolCandidate[];
  reason: string | null;
}

export interface HouseModelName {
  model_name: string;
  /** Providers carrying it that the pool can pick. */
  providers: string[];
  /** Providers carrying it that it cannot, each with the reason in brackets. */
  other_providers?: string[];
  in_pool?: boolean;
  /** Why it is not in the pool: "not shared", "paused", "no key", "no host". */
  reason?: string | null;
}

export type StageResultSource = 'ad_hoc' | 'manual' | 'bench';

export interface StageModelResult {
  id: number;
  stage: string;
  archetype: string | null;
  model_name: string;
  metric: string;
  score: number;
  sample_size: number | null;
  notes: string | null;
  source: StageResultSource;
  /** Stage-bench counts behind `score` (clean_runs, harm, tokens, seconds, fixture, version, measured). */
  detail?: Record<string, unknown> | null;
  recorded_at: string | null;
}

/** One model's stage-bench measurement, pooled across the job's fixtures. */
export interface BenchAdviceRow {
  model: string;
  n: number;
  clean_runs: number;
  harm: number;
  tokens: number;
  seconds: number;
  /** Quality indistinguishable from the best. */
  tied: boolean;
  /** The suggestion: cheapest of the tied. */
  pick: boolean;
  /** The version's rules here let the pool pick it. */
  allowed: boolean;
  /** Some shared, enabled provider can run it. */
  in_pool: boolean;
  /** Where the measurement ran, e.g. ["Ollama Cloud"]; empty when not recorded. */
  providers?: string[];
  /** Every metric for this model, by name (see `metric_defs`). */
  metrics: Record<string, number>;
}

/** One metric from the bench's registry (`jcseo/bench/metrics.py`). */
export interface BenchMetricDef {
  name: string;
  label: string;
  better: 'higher' | 'lower';
  kind: 'quality' | 'cost';
  description: string;
}

/** A metric the named model(s) did best on — often not the overall pick. */
export interface BenchStandout {
  metric: string;
  label: string;
  better: 'higher' | 'lower';
  kind: 'quality' | 'cost';
  value: number;
  leaders: string[];
}

export type BenchAdviceStatus = 'no_data' | 'nothing_clean' | 'held' | 'on_pick' | 'mixed' | 'switch';

export interface BenchAdvice {
  status: BenchAdviceStatus;
  pick: string | null;
  held_back: string | null;
  rows: BenchAdviceRow[];
  /** Allowed here but measured below the pick — the pool rotates onto them. */
  worse_allowed: string[];
  /** Allowed patterns that match no benched model. */
  unmeasured_patterns: string[];
  fixtures: string[];
  measured: string | null;
  /** The metric columns this job reports, in registry order. */
  columns: string[];
  standouts: BenchStandout[];
  metric_defs: BenchMetricDef[];
}

/** A stage-bench results file (`main-bench.py export` or Settings ▸ Download). */
export interface BenchResultsFile {
  format: string;
  exported_at?: string;
  entries: unknown[];
}

export type BenchEntryStatus = 'new' | 'replaces' | 'older' | 'duplicate' | 'invalid';

export interface BenchEntryPreview {
  index: number;
  stage: string | null;
  archetype: string | null;
  model_name: string | null;
  fixture: string | null;
  version: number | null;
  measured: string | null;
  n: number | null;
  clean_runs: number | null;
  tokens: number | null;
  status: BenchEntryStatus;
  reason: string | null;
  /** Prod has a shared, enabled provider for the model. */
  in_pool: boolean;
}

export interface BenchPreview {
  counts: Partial<Record<BenchEntryStatus, number>>;
  entries: BenchEntryPreview[];
}

/** A model policy copied as text (Settings ▸ Model policy ▸ Copy). */
export interface CopiedPolicy {
  format: string;
  name?: string;
  rules: unknown[];
}

/** same: this server matches the same models · differs: other models ·
 *  missing: no model here · invalid: cannot be stored here. */
export type PastedRuleStatus = 'same' | 'differs' | 'missing' | 'invalid';

export interface PastedRule {
  index: number;
  stage: string | null;
  archetype: string | null;
  model_pattern: string | null;
  enabled: boolean;
  status: PastedRuleStatus;
  reason: string | null;
  /** Models the rule matched where it was copied. */
  there: string[];
  /** Models it matches on this server. */
  here: string[];
}

export interface PastePreview {
  name: string;
  counts: Partial<Record<PastedRuleStatus, number>>;
  rules: PastedRule[];
}

export interface StageModelResultIn {
  stage: string;
  archetype?: string | null;
  model_name: string;
  metric: string;
  score: number;
  sample_size?: number | null;
  notes?: string | null;
}

// ============= Seat bench =============

/** One model's measured behaviour on one seat's fixture. */
export interface SeatBenchRow {
  model: string;
  /** Runs behind the rate. 5 separates 0%-from-100%; only 15 resolves anything closer. */
  n: number;
  /**
   * Turns that did the whole job. READ THIS AS A RATE: the room runs a seat ONCE per
   * turn, so 0.93 means roughly one turn in fifteen does not.
   */
  clean: number;
  /** Acts that made the board worse — invented figures, unverified competitors. */
  harm: number;
  tokens: number;
  seconds: number;
  /** Not distinguishable from the best here, so picking it is a cost decision. */
  tied_for_best: boolean;
}

export interface Seat {
  archetype: string;
  label: string;
  blurb: string;
  /** What the fixture rewards, so a reader can judge whether it matches their job. */
  measures: string;
  /** Bare model name — matched against `results[].model`. Never render this. */
  current_model: string | null;
  /** What the user named the setting — render this. Never match on it. */
  current_model_label?: string | null;
  current_llm_settings_id: number | null;
  /** True when this seat has its own row; false when it rides the shared one. */
  pinned: boolean;
  pinned_stage_id: number | null;
  source: string;
  recommended_model: string | null;
  results: SeatBenchRow[];
}

export interface SeatListResponse {
  stage: StageEnum;
  items: Seat[];
  /** When the numbers were taken, so a stale report reads as stale. */
  measured: string;
}

export type StageCapability = 'text' | 'tools' | 'vision' | 'image_generation';

export interface StageEnumOption {
  /** How many sub-seats this stage can be configured per. 0 for most stages. */
  seat_count?: number;
  value: string;
  label: string;
  // Model requirement for this stage (from jcseo.stage_metadata).
  capability: StageCapability;
  // The LLMSettings boolean flag a model needs to satisfy `capability`
  // (e.g. 'supports_vision'); null for plain-text stages.
  capability_flag: keyof LLMSettingsOption | null;
  hint: string;
  /** Which app this job belongs to (`jcseo.stage_metadata.STAGE_PRODUCTS`).
   * Null/absent = shared, the rungs every app inherits from. A non-admin is
   * only served the apps they hold. */
  product?: string | null;
  // Grouping (from jcseo.stage_metadata). `group` is the stage this one falls
  // back to before DEFAULT — every chat agent points at CHAT_AGENT — and
  // `is_group` marks the shared stage itself: configurable, never billed.
  group: string | null;
  group_label: string | null;
  is_group: boolean;
  // Where this stage's model comes from when it has no row of its own, most
  // specific first (its group, then DEFAULT) — straight from the backend's
  // `stage_metadata.fallback_chain`, so the UI never re-derives the order.
  fallback: StageFallbackRung[];
}

/** One row a stage can inherit its model from, as the resolver orders them. */
export interface StageFallbackRung {
  stage: string;
  label: string;
}

export interface StageEnumListResponse {
  stages: StageEnumOption[];
}

// ============= Recommendation Types =============
export type RecommendationStatus =
  'PENDING' | 'PARTIALLY_APPROVED' | 'APPROVED' | 'DISMISSED';

export type IdeaSource =
  | 'paste_match'
  | 'store_match'
  | 'ai_suggestion'
  | 'google_suggestion'
  | 'agent_curated'
  | 'agent_generated';

export interface RecTopicKeyword {
  id: number;
  recommendation_id: number;
  topic_id: number;
  keyword_id: number;
  keyword_value: string;
  relevance_score: number | null;
  reason: string | null;
  approved_at: string | null;
  topic_keyword_id: number | null;
  avg_monthly_searches: number | null;
  avg_cpc: number | null;
  competition_level: string | null;
  serp_count: number;
  latest_serp_date: string | null;
  latest_serp_engine: string | null;
  has_pending_serp_request: boolean;
  source: IdeaSource | null;
  dismissed_at: string | null;
  dismiss_reason: string | null;
  fit_score: number | null;
  endorsed_at: string | null;
  rejected_at: string | null;
  reject_reason: string | null;
  created_at: string | null;
}

export interface DiscoveryIdeaAcceptResponse {
  topic_keyword_id: number;
  created: boolean;
}

export type DiscoveryIdeaStatusFilter = 'pending' | 'approved' | 'dismissed' | 'all';
export type DiscoveryIdeaSort =
  'fit' | 'relevance' | 'avg_monthly_searches' | 'created_at';

export interface Recommendation {
  id: number;
  topic_id: number | null;
  topic_name: string | null;
  category_name: string | null;
  project_name: string | null;
  title: string | null;
  strategy: string | null;
  description: string | null;
  status: RecommendationStatus | null;
  hil_approved_at: string | null;
  total_keywords: number;
  approved_keywords: number;
  pending_keywords: number;
  created_at: string;
  updated_at: string;
}

export interface RecommendationDetail extends Recommendation {
  rec_topic_keywords: RecTopicKeyword[];
}

export interface RecommendationPaginatedResponse {
  items: Recommendation[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface RecommendationApproveRequest {
  rec_topic_keyword_ids: number[];
}

export interface RecommendationApproveResponse {
  approved_count: number;
  created_topic_keyword_ids: number[];
}

export interface RecommendationDismissRequest {
  rec_topic_keyword_ids?: number[];
}

export interface RecommendationDismissResponse {
  dismissed_count: number;
}

export interface RecTopicKeywordPaginatedResponse {
  items: RecTopicKeyword[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

// One keyword that could seed a Google Keyword Planner run, for the
// seed-preview popover on "Refresh Google Ideas".
export type GoogleSeedGroup = 'accepted' | 'endorsed' | 'yours' | 'google';

export interface GoogleSeedCandidate {
  keyword_id: number;
  value: string;
  group: GoogleSeedGroup | string;
  source?: string | null;
  already_sent: boolean;
  default_selected: boolean;
}

// ============= Conversation Types =============
export type LLMEntityType =
  'SYSTEM' | 'HUMAN' | 'AI_STEP' | 'AI_RESULT' | 'COMPACTED' | 'TOOL' | 'FILE';
export type LLMConversationStatus =
  'ACCEPTED' | 'WAITING_FOR_USER' | 'REJECTED_NEEDS_FIX' | 'REJECTED_ABANDONED';

export interface ToolCall {
  id?: string;
  type?: string;
  function?: {
    name?: string;
    arguments?: string; // JSON-encoded args string
  };
}

export interface ConversationMessage {
  id: number;
  message_number: number;
  entity_type: LLMEntityType;
  name: string | null;
  content: string;
  notes: string | null;
  tool_calls: ToolCall[] | null;
  tool_call_id: string | null;
  processed_at: string | null;
  created_at: string;
}

export interface Conversation {
  id: number;
  managed_page_id: number;
  status: LLMConversationStatus | null;
  messages: ConversationMessage[];
  created_at: string;
  updated_at: string;
}

// ============= Citation Provenance Types =============
export type CitationVerdict = 'SUPPORTED' | 'NOT_SUPPORTED' | 'UNRESOLVED' | null;

export interface ProvenanceRow {
  section: string;
  claim: string;
  fact_id: string;
  point: string;
  source_url?: string | null;
  source_title?: string | null;
  verdict?: CitationVerdict;
  issue?: string | null;
}

export interface ProvenanceSummary {
  total: number;
  SUPPORTED: number;
  NOT_SUPPORTED: number;
  UNRESOLVED: number;
}

export interface ProvenanceResponse {
  version_id: number;
  has_structured_content: boolean;
  summary: ProvenanceSummary;
  rows: ProvenanceRow[];
}

export interface EditableBodyResponse {
  version_id: number;
  body: string; // markdown with <cite id="…">…</cite> tags when has_citations
  has_citations: boolean;
}

/** How a cited claim reaches the reader. Mirrors services/publish_options.py. */
export type CitationStyle = 'off' | 'footnote' | 'link';

export interface PageBodyPreviewResponse {
  version_id: number;
  /** The published body, rendered by the same builder the deployers run. */
  html: string;
  /** The style that produced this render — may be a trial. */
  citation_style: CitationStyle;
  /** What the site is actually set to, so a trial can say it is one. */
  site_citation_style: CitationStyle;
  /** False for a version built before citations were tracked, or citing none. */
  has_citations: boolean;
  site_type: ManagedSiteType | null;
}

// ============= SEO Analysis Types =============
export type SEOCheckStatus = 'good' | 'warning' | 'problem' | 'not_applicable';

export interface SEOCheck {
  id: string;
  title: string;
  status: SEOCheckStatus;
  message: string;
  priority: number; // 1=high, 2=medium, 3=low
}

// A citation in a stage-diff delta (added / removed / verdict-flipped).
export interface StageCitation {
  fact_id: string;
  text: string;
  verdict?: string | null;
  source_url?: string | null;
  from_verdict?: string | null;
  to_verdict?: string | null;
}

export interface StageDiffResponse {
  from_stage: string;
  to_stage: string;
  from_present: boolean;
  to_present: boolean;
  from_markdown: string;
  to_markdown: string;
  citation_delta: {
    added: StageCitation[];
    removed: StageCitation[];
    verdict_changed: StageCitation[];
  };
}

// One SEO edit the phase made (committed) or proposed but rejected (suggestion).
export interface SEOCorrection {
  target: string; // title | description | slug | keyword_in_intro | keyword_in_headings
  kind: string; // frontmatter | section | heading
  before?: string | null;
  after?: string | null;
  // No `committed` flag — which list the entry is in (corrections vs
  // suggestions) is what committed means.
  reason?: string;
}

export interface SEOAnalysisRequest {
  keyword: string;
  title?: string;
  description?: string;
  content?: string;
  slug?: string;
}

export interface SEOAnalysisResponse {
  overall_score: number; // 0-100
  overall_status: SEOCheckStatus;
  checks: SEOCheck[];
  summary: string;
}

// ============= Content Refinement Types =============
export interface RefineContentRequest {
  feedback: string;
}

export interface RefineContentResponse {
  managed_page_id: number;
  content: string;
  front_matter?: string | null;
  content_length: number;
}

// ============= WebSocket Types =============
export interface WebSocketTokenResponse {
  token: string;
  url: string;
  user_channel: string;
  system_channel: string;
}

export type WebSocketEventType =
  | 'page_build'
  | 'page_refine'
  | 'conversation'
  | 'serp_requested'
  | 'serp_completion'
  | 'page_facts_extracted'
  | 'relevance_score'
  | 'rate_limit'
  | 'network_disabled'
  | 'google_ads_auth_expired'
  | 'google_ads_not_configured'
  | 'google_ads_demand_down'
  | 'growth_agent_llm'
  | 'keyword_recommendations_ready'
  | 'terms_regrouped'
  | 'link_map_relinked'
  | 'discovery_idea_added'
  | 'discover_keywords_chat'
  | 'discover_keywords_list_focus'
  | 'boardroom'
  | 'credits'
  | 'page_builder_chat'
  | 'knowledge_proposal_added'
  | 'knowledge_agent_status'
  | 'knowledge_plan'
  | 'structure_plan'
  | 'trend_brief'
  | 'image_generated'
  | 'image_optimized'
  | 'image_metadata_extracted'
  | 'watch_alert'
  | 'watch_run';

export interface StructurePlanWebSocketEvent {
  event_type: 'structure_plan';
  project_id: number;
  plan_id: number;
  // null = project-level plan; set = a category-scoped session. Panels
  // filter by this so they don't react to events from a sibling scope.
  category_id: number | null;
  // 'tree_updated' fires per mutating tool batch DURING a turn (the proposal
  // panel refetches so a long round shows progress); 'applied' fires when the
  // proposal is materialized into real categories/topics, which is the signal
  // for the project / category list views underneath the drawer to refresh.
  status: 'started' | 'tree_updated' | 'replied' | 'failed' | 'applied' | string;
  message?: string;
}

export interface TrendBriefWebSocketEvent {
  event_type: 'trend_brief';
  // Addressed by the FEED's id, not the brief's: the card is rendered before a
  // brief exists, so this is the only id the page already knows.
  trending_search_id: number;
  brief_id?: number;
  status: 'started' | 'ready' | 'failed' | string;
  message?: string;
}

export interface PageBuildEvent {
  event_type: 'page_build';
  page_id: number;
  // The phased builder UI's per-phase re-runs add `<phase>_started` and
  // `<phase>_completed` variants alongside the legacy full-build statuses.
  status:
    | 'started'
    | 'outline_ready'
    | 'research_started'
    | 'research_completed'
    | 'framing_started'
    | 'framing_completed'
    | 'write_started'
    | 'write_completed'
    | 'ground_started'
    | 'ground_completed'
    | 'completed'
    | 'failed'
    | 'error'
    | 'cancelled';
  keyword: string;
  title?: string;
  message?: string;
  content_preview?: string;
  // Carries the outline JSON ready to drop into the editor. Only the
  // non-persisting outline path (react.generate_outline_only, now reachable
  // just from src/ad_hoc tooling) sets it; builds and per-phase outline runs
  // emit outline_ready without it when the draft version is saved.
  outline?: string;
}

export interface PageRefineEvent {
  event_type: 'page_refine';
  page_id: number;
  status: 'completed' | 'failed';
  message?: string;
  content_length?: number;
}

export interface ConversationWebSocketEvent {
  event_type: 'conversation';
  page_id: number;
  conversation_id: number;
  action: 'message_added' | 'archived' | 'file_accepted';
  message?: string;
}

export interface SerpRequestedEvent {
  event_type: 'serp_requested';
  keyword_ids: number[];
}

export interface SerpCompletionEvent {
  event_type: 'serp_completion';
  keyword_ids: number[];
}

export interface PageFactsExtractedEvent {
  event_type: 'page_facts_extracted';
  page_id: number;
  facts: string[];
  ai_summary: string | null;
  // true only on the final event for a page — extraction finished and
  // processing_state holds its settled value.
  done?: boolean;
  processing_state?: number | null;
}

export interface RelevanceScoreEvent {
  event_type: 'relevance_score';
  topic_keyword_ids: number[];
}

export interface RateLimitWebSocketEvent {
  event_type: 'rate_limit';
  llm_name: string;
  limit_type: string; // "RPD", "TPD", "RPM", "TPM"
  message: string;
}

export interface NetworkDisabledWebSocketEvent {
  event_type: 'network_disabled';
  reason: string; // "no_proxies" | "bad_settings" | "unconfigured_llm" | "error"
  message: string;
}

export interface GoogleAdsAuthExpiredWebSocketEvent {
  event_type: 'google_ads_auth_expired';
}

/** Sent to admins: Google Ads demand pulls keep failing, so demand analysts
 *  are held to one test lookup per dig until it works again. */
export interface GoogleAdsDemandDownWebSocketEvent {
  event_type: 'google_ads_demand_down';
  message: string;
}

export interface GoogleAdsNotConfiguredWebSocketEvent {
  event_type: 'google_ads_not_configured';
  message: string;
}

export interface GrowthAgentLLMEvent {
  event_type: 'growth_agent_llm';
  project_id: number;
  session_id?: number;
  status: 'started' | 'reasoning' | 'tool_call' | 'session_complete' | 'failed';
  message?: string;
  tool_name?: string;
  tool_summary?: string;
  pages_built?: number;
  recs_accepted?: number;
  pages_promoted?: number;
  pages_refined?: number;
}

export interface AgentSessionEntry {
  id: number;
  entry_type: 'reasoning' | 'tool_call' | 'error' | 'summary';
  tool_name: string | null;
  message: string;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

export interface AgentSession {
  id: number;
  status: 'running' | 'completed' | 'failed';
  created_at: string;
  completed_at: string | null;
  pages_built: number;
  recs_accepted: number;
  recs_rejected: number;
  pages_promoted: number;
  pages_refined: number;
  entries: AgentSessionEntry[];
}

export interface KeywordRecommendationsReadyEvent {
  event_type: 'keyword_recommendations_ready';
  recommendation_id: number;
  topic_id: number;
  status: 'ready' | 'skipped' | 'no_config';
  message: string;
}

export interface TermsRegroupedEvent {
  event_type: 'terms_regrouped';
  topic_id: number;
  status: 'done' | 'failed';
  grouped: number;
  /** Groupings the run left alone because a person had set them. What stops a
   *  low `grouped` reading as a failure. */
  preserved: number;
  message: string;
}

export interface LinkMapRelinkedEvent {
  event_type: 'link_map_relinked';
  link_map_id: number;
  status: 'done' | 'failed';
  edges_created: number;
  /** Kept because they were locked. This is what stops a legitimate zero
   *  reading as a failure: a run that kept 40 and drew 0 did what was asked. */
  edges_kept_locked: number;
  /** Between different SITES — the number the feature exists for. A run that
   *  drew many links and crossed nothing has technically succeeded. */
  edges_crossing: number;
  pages_considered: number;
  /** The state before this run cleared anything. Relink is a background job,
   *  so unlike every other destructive verb here there is no HTTP response to
   *  carry it — without this the snapshot is taken and unreachable. Null when
   *  the map had no links to begin with. */
  snapshot_id: number | null;
  message: string;
}

export interface DiscoveryIdeaAddedEvent {
  event_type: 'discovery_idea_added';
  topic_id: number;
  recommendation_id: number;
  rec_topic_keyword_ids: number[];
  source: IdeaSource | null;
  count: number;
}

// Discover-keywords chat lifecycle. Per-row writes still emit
// `discovery_idea_added` events as the agent endorses / rejects /
// adds keywords during the turn.
export interface DiscoverKeywordsChatWebSocketEvent {
  event_type: 'discover_keywords_chat';
  topic_id: number;
  chat_id: number;
  // The Recommendation row created for this turn's writes — lets the
  // UI correlate discovery_idea_added rows back to the chat turn.
  recommendation_id: number;
  status: 'started' | 'replied' | 'failed' | 'cancelled' | string;
  message?: string;
}

// Agent-driven filter change on the discovery list so the user's left pane
// jumps to the rows the agent just touched (e.g. flip to 'endorsed' right
// after an endorse batch). Fields are absent when left unchanged.
export interface DiscoverKeywordsListFocusWebSocketEvent {
  event_type: 'discover_keywords_list_focus';
  topic_id: number;
  status?: 'pending' | 'approved' | 'dismissed' | 'all';
  reviewed?: 'all' | 'endorsed' | 'rejected' | 'not_reviewed';
}

// ---------------------------------------------------------------------------
// Discover-keywords chat planner — chat-driven triage of the topic's
// RecTopicKeyword pool. Backed by /discovery-ideas/topics/{id}/chat.
// ---------------------------------------------------------------------------

export interface DiscoverKeywordsChatMessage {
  id: number;
  session_number: number;
  message_number: number;
  // SYSTEM = compaction summary the LLM produced; rendered as a
  // special band in the feed.
  entity_type: 'HUMAN' | 'AI' | 'SYSTEM' | string;
  content: string;
  has_tool_call: boolean;
  created_at: string;
}

export interface DiscoverKeywordsChatResponse {
  id: number;
  topic_id: number;
  status: 'idle' | 'thinking' | 'failed' | string;
  last_error?: string | null;
  current_session_number: number;
  compactable_turn_count: number;
  // Approximate context usage for the meter chip. context_window is null
  // when the model's window is unknown.
  context_tokens: number;
  context_window?: number | null;
  messages: DiscoverKeywordsChatMessage[];
  // Follow-up prompts the agent proposed last turn, rendered as
  // clickable quick-prompt chips. Empty/absent → static starters.
  suggested_prompts?: string[];
  agent_stage: string;
  agent_model_label?: string | null;
}

// ---------------------------------------------------------------------------
// Page-builder concierge chat — chat surface on the Page Builder page that
// orchestrates the existing build phases. The concierge agent triggers
// phase re-runs (research / angle / outline / write / ground) which run
// asynchronously and update the EXISTING phase stepper via the normal
// page_build / synthesize_research / angle_generated events. The chat
// lifecycle itself flows via `page_builder_chat` events.
// Backed by /page-builder-chat/pages/{page_id}.
// ---------------------------------------------------------------------------

export interface PageBuilderChatWebSocketEvent {
  event_type: 'page_builder_chat';
  page_id: number;
  chat_id: number;
  status: 'started' | 'replied' | 'failed' | string;
  message?: string;
}

export interface PageBuilderChatMessage {
  id: number;
  session_number: number;
  message_number: number;
  // SYSTEM = compaction summary the LLM produced; rendered as a
  // special band in the feed.
  entity_type: 'HUMAN' | 'AI' | 'SYSTEM' | string;
  content: string;
  has_tool_call: boolean;
  created_at: string;
}

export interface PageBuilderChatResponse {
  id: number;
  page_id: number;
  status: 'idle' | 'thinking' | 'failed' | string;
  last_error?: string | null;
  current_session_number: number;
  compactable_turn_count: number;
  // Approximate context usage for the meter chip. context_window is null
  // when the model's window is unknown.
  context_tokens: number;
  context_window?: number | null;
  // Follow-up prompts the agent proposed last turn, rendered as
  // clickable quick-prompt chips. Empty/absent → static starters.
  suggested_prompts?: string[];
  agent_stage: string;
  agent_model_label?: string | null;
  messages: PageBuilderChatMessage[];
}

// Knowledge briefs
export type KnowledgeAgentScope = 'project' | 'category' | 'topic';

export type KnowledgeProposalType =
  | 'heuristic'
  | 'misconception'
  | 'expert_voice'
  | 'contrarian'
  | 'fact'
  | 'terminology'
  | 'relationship';

export type KnowledgeProposalStatus = 'accepted' | 'rejected' | 'edited';

export interface KnowledgeProposal {
  id: number;
  brief_id: number;
  proposal_type: KnowledgeProposalType;
  text: string;
  source_urls: string[];
  confidence: number | null;
  status: KnowledgeProposalStatus;
  original_text: string | null;
  human_modified_at: string | null;
  reject_reason: string | null;
  generation_run_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface KnowledgeSourceEntry {
  url: string;
  method: 'crawl' | 'search' | string;
  title?: string | null;
  excerpt?: string | null;
  fetched_at?: string | null;
}

export interface KnowledgeBrief {
  id: number;
  scope: KnowledgeAgentScope;
  scope_id: number;
  bedrock_heuristics: string | null;
  deep_knowledge: string | null;
  deep_knowledge_sources: KnowledgeSourceEntry[];
  version_number: number;
  last_generated_at: string | null;
  last_edited_at: string | null;
  /** ISO datetime — if set, the agent is currently running. */
  current_run_started_at: string | null;
  /** Opaque id for the in-flight run. Null when idle. */
  current_run_id: string | null;
  proposals: KnowledgeProposal[];
}

export type KnowledgeBriefVersionSource =
  'AGENT_RUN' | 'REFINEMENT' | 'MANUAL_EDIT' | 'REVERT';

export interface KnowledgeBriefVersionSummary {
  id: number;
  version_number: number;
  source: KnowledgeBriefVersionSource | null;
  source_description: string | null;
  deep_knowledge_length: number | null;
  created_at: string;
}

export interface KnowledgeBriefVersionListResponse {
  items: KnowledgeBriefVersionSummary[];
  total: number;
}

export interface KnowledgeProposalAddedEvent {
  event_type: 'knowledge_proposal_added';
  brief_id: number;
  proposal_id: number;
  scope: KnowledgeAgentScope;
  proposal_type: KnowledgeProposalType;
  text: string;
  source_urls: string[];
  confidence: number | null;
  run_id: string | null;
}

export interface KnowledgeAgentStatusEvent {
  event_type: 'knowledge_agent_status';
  brief_id: number;
  scope: KnowledgeAgentScope;
  scope_id: number;
  run_id?: string;
  status: 'started' | 'thinking' | 'done' | 'failed';
  message?: string;
  proposals_written?: number;
  deep_knowledge_set?: boolean;
}

export interface SynthesizeResearchEvent {
  event_type: 'synthesize_research';
  page_id: number;
  status: 'started' | 'progress' | 'completed' | 'crawl_requested' | 'failed';
  total_pages?: number;
  pages_ready?: number;
  pages_pending_extraction?: number;
  points_added?: number;
  // Facts deduped against ones already on the page. Distinguishes "the SERPs
  // had nothing extractable" (added 0, skipped 0) from "nothing new, we
  // already have it all" (added 0, skipped > 0).
  points_skipped_existing?: number;
  source_page_id?: number;
  message?: string;
}

export interface ResearchPointAddedEvent {
  event_type: 'research_point_added';
  page_id: number;
  point: ResearchPoint;
}

export interface ResearchPoint {
  id: number;
  managed_page_id: number;
  serp_entry_id: number | null;
  key_point: string;
  source_url: string;
  source_title: string | null;
  source_page_type: string | null;
  serp_rank: number | null;
  is_pinned: boolean;
  is_manual: boolean;
  user_edited_at: string | null;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface PendingSource {
  id: number;
  serp_entry_id: number | null;
  source_title: string | null;
  detail: string | null;
  source_url: string;
  source_page_type: string | null;
  serp_rank: number | null;
  is_manual: boolean;
  extraction_state: string; // extracting | pending | failed
}

export interface ResearchPointListResponse {
  points: ResearchPoint[];
  page_type_counts: Record<string, number>;
  pending_sources?: PendingSource[];
}

export interface ImageGeneratedWebSocketEvent {
  event_type: 'image_generated';
  page_id?: number;
  image_id: number;
  placeholder_key?: string;
  status: string; // "started" | "completed" | "failed"
  image_url?: string | null;
  message?: string | null;
}

export interface ImageOptimizedWebSocketEvent {
  event_type: 'image_optimized';
  image_id: number;
  status: string; // "completed" | "failed" | "skipped"
  variant_count?: number;
  primary_url?: string | null;
  message?: string | null;
}

export interface ImageMetadataExtractedWebSocketEvent {
  event_type: 'image_metadata_extracted';
  image_id: number;
  status: string; // "completed" | "failed" | "skipped"
  title?: string | null;
  alt_text?: string | null;
  description?: string | null;
  message?: string | null;
}

export type WebSocketEvent =
  | PageBuildEvent
  | CreditsWebSocketEvent
  | PageRefineEvent
  | ConversationWebSocketEvent
  | SerpRequestedEvent
  | SerpCompletionEvent
  | PageFactsExtractedEvent
  | RelevanceScoreEvent
  | DocumentProcessingEvent
  | RateLimitWebSocketEvent
  | NetworkDisabledWebSocketEvent
  | GoogleAdsAuthExpiredWebSocketEvent
  | GoogleAdsNotConfiguredWebSocketEvent
  | GoogleAdsDemandDownWebSocketEvent
  | GrowthAgentLLMEvent
  | KeywordRecommendationsReadyEvent
  | TermsRegroupedEvent
  | LinkMapRelinkedEvent
  | DiscoveryIdeaAddedEvent
  | DiscoverKeywordsChatWebSocketEvent
  | DiscoverKeywordsListFocusWebSocketEvent
  | BoardroomWebSocketEvent
  | PageBuilderChatWebSocketEvent
  | KnowledgeProposalAddedEvent
  | KnowledgeAgentStatusEvent
  | KnowledgePlanWebSocketEvent
  | StructurePlanWebSocketEvent
  | TrendBriefWebSocketEvent
  | SynthesizeResearchEvent
  | ResearchPointAddedEvent
  | AngleGeneratedEvent
  | ImageGeneratedWebSocketEvent
  | ImageOptimizedWebSocketEvent
  | ImageMetadataExtractedWebSocketEvent
  // A watch found something, and a check ran. Defined at the end of this file with the rest
  // of Watch. They are separate because one is a feed row with a tier and the other is a
  // transient fact about a probe — including the common case of finding nothing.
  | WatchAlertEvent
  | WatchRunEvent;
export interface DocumentProcessingEvent {
  event_type: 'document_processing';
  document_id: number;
  status: 'processed' | 'error';
  fact_count: number;
}

// ============= Content Version Types =============

export type ContentVersionSource =
  'BUILD' | 'REFINE' | 'MANUAL_EDIT' | 'ACCEPT_FILE' | 'REVERT';

export interface ContentVersionSummary {
  id: number;
  version_number: number;
  source: ContentVersionSource | null;
  source_description: string | null;
  title: string | null;
  content_length: number | null;
  created_at: string;
  created_by_name: string | null;
  is_current: boolean;
  build_duration_seconds: number | null;
}

export interface ContentVersionDetail extends ContentVersionSummary {
  content: string | null;
  content_generation_output: string | null;
  slug: string | null;
  description: string | null;
  outline: string | null;
}

export interface ContentVersionListResponse {
  items: ContentVersionSummary[];
  total: number;
}

export interface ContentVersionCompareResponse {
  version_a: ContentVersionDetail;
  version_b: ContentVersionDetail;
}

export interface SwitchVersionResponse {
  switched_to_version_id: number;
  switched_to_version_number: number;
  page: ManagedPage;
}

export interface DeleteVersionResponse {
  deleted_version_id: number;
  deleted_version_number: number;
  new_current_version_id: number | null;
  new_current_version_number: number | null;
}

// Legacy alias for backwards compatibility
export type RevertResponse = SwitchVersionResponse;

// ============= Trending Keywords Types =============
export interface MonthlyDataPoint {
  month: string;
  year: number;
  searches: number;
}

export interface TopicLink {
  topic_id: number;
  topic_name: string;
  /** The node's parent — null for a root. Since migration 108 a "category" IS
   *  a topic with no parent, so this is absent for any keyword filed at the
   *  top of a subject. */
  category_id: number | null;
  project_id: number;
}

export interface RecommendedTopicLink {
  rec_topic_keyword_id: number;
  topic_id: number;
  topic_name: string;
  /** Null for a root node — see TopicLink.category_id. */
  category_id: number | null;
  project_id: number;
}

export interface TrendingKeyword {
  keyword_id: number;
  keyword_value: string;
  avg_monthly_searches: number | null;
  competition_level: string | null;
  avg_cpc: number | null;
  recent_avg: number;
  older_avg: number;
  growth_pct: number | null;
  monthly_data: MonthlyDataPoint[];
  topics: TopicLink[];
  recommended_for_topics: RecommendedTopicLink[];
  has_managed_page: boolean;
  managed_page_id: number | null;
  managed_page_status: string | null;
  semantic_similarity: number | null;
  serp_count: number;
  latest_serp_date: string | null;
  latest_serp_engine: string | null;
  has_pending_serp_request: boolean;
  // When these numbers were last pulled from Google Ads. Drives the freshness
  // window and the age chip — see pages/trendFreshness.ts.
  collected_at: string | null;
  /** Per engine, most winnable first. Empty until a SERP is measured. */
  serp_gaps?: SerpGap[];
}

export interface TrendingKeywordPaginatedResponse {
  items: TrendingKeyword[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

// Google Trends "spiking today" — a different dataset from TrendingKeyword above.
// That one is Google Ads growth for keywords this account already tracks; this is
// whatever the whole country is suddenly searching, tracked or not.
export interface TrendingNewsItem {
  title: string;
  url: string;
  source: string;
  picture: string;
}

export interface SpikingSearch {
  term: string;
  rank: number;
  approx_traffic: string | null; // Google's own bucket, e.g. "2000+"
  news: TrendingNewsItem[];
  picture: string | null;
  first_seen_at: string;
  last_seen_at: string;
  trending_for_hours: number;
  keyword_id: number | null; // set when the term is already tracked
}

export interface SpikingSearchesResponse {
  geo: string;
  items: SpikingSearch[];
  age_seconds: number | null;
  stale: boolean;
  error: string | null;
}

export interface TrackSpikingTermResponse {
  keyword_id: number;
  value: string;
  created: boolean;
}

// ============= Trend Brief Types =============
// What we went and found out about one spiking term. The brief itself is
// global — the demand numbers and the stories are facts about the world — and
// only `proposals` are per-user, because deciding a page is worth writing is
// personal in a way that "what does Google say about this word" is not.

// Uppercase on the wire, like ArtifactState and NewsBriefingStatus — the
// backend's StrEnum members ARE their values, and auto() yields the NAME.
export type TrendBriefStatus = 'PENDING' | 'ENRICHING' | 'READY' | 'FAILED';

export interface TrendBriefKeyword {
  keyword_id: number | null;
  keyword: string;
  monthly_searches: number | null;
  competition: string | null;
  growth_pct: number | null;
  // The only number here about MONEY: what somebody pays for one visitor.
  top_bid: number | null;
}

export interface TrendBriefArticle {
  news_article_id: number;
  title: string;
  url: string;
  source_name: string | null;
  image_url: string | null;
  published_at: string | null;
}

export interface TrendProposal {
  id: number;
  title: string;
  angle: string;
  rationale: string | null;
  keyword_id: number;
  keyword: string;
  monthly_searches: number | null;
  competition: string | null;
  // Set once built. Drives "open" vs "create", which is what stops a proposal
  // being built twice.
  managed_page_id: number | null;
}

export interface TrendBrief {
  id: number | null;
  status: TrendBriefStatus;
  enriched_at: string | null;
  last_error: string | null;
  // The difference between "we could not ask Google" and "nobody searches
  // this" — two states that look identical on the card without these.
  ads_skipped_reason: string | null;
  news_skipped_reason: string | null;
  keywords: TrendBriefKeyword[];
  articles: TrendBriefArticle[];
  proposals: TrendProposal[];
  /** What kind of story this is, from a fixed list. Empty for briefs researched
   *  before tagging existed, until the backfill reaches them. */
  tags: string[];
}

export interface TrendStory {
  trending_search_id: number;
  term: string;
  geo: string;
  rank: number;
  approx_traffic: string | null;
  picture: string | null;
  news: TrendingNewsItem[];
  first_seen_at: string;
  last_seen_at: string;
  trending_for_hours: number;
  // Still in Google's latest poll, or a term that has since dropped out. Old
  // rows are kept so research already done does not vanish with the spike —
  // but they are labelled and ranked below the live ones.
  is_current: boolean;
  keyword_id: number | null;
  // null when nothing has been researched yet — the card renders from the feed
  // alone and offers a "Research this" button.
  brief: TrendBrief | null;
}

export interface TrendTag {
  id: number;
  value: string;
  label: string;
  hint: string;
  status: 'ACTIVE' | 'SUGGESTED' | 'DISMISSED' | string;
  times_suggested: number;
}

export interface TrendStoriesResponse {
  geo: string;
  items: TrendStory[];
  age_seconds: number | null;
  stale: boolean;
  error: string | null;
  enriching_ids: number[];
  /** Tag -> cards carrying it in this window, counted BEFORE any tag filter is
   *  applied so the numbers describe the window rather than the selection. */
  tag_counts: Record<string, number>;
  /** The active vocabulary, so a chip shows a label rather than a token. */
  tags_available: TrendTag[];
  /** Buckets the model asked for and does not have, commonest first. */
  tags_wanted: TrendTag[];
}

export interface TrendBriefRequestResponse {
  trending_search_id: number;
  brief_id: number | null;
  status: string;
  queued: boolean;
}

export interface CreatePageFromProposalResponse {
  managed_page_id: number;
  created: boolean;
  research_sources_added: number;
}

export interface StartSubjectFromKeywordsResponse {
  project_id: number;
  plan_id: number;
  planner_started: boolean;
  keywords_seeded: number;
}

// ============= GitHub Integration Types =============

export type GitHubRepoRole = 'PRIMARY' | 'CONFIG' | 'CONTENT' | 'THEME';

export interface GitHubInstallation {
  id: number;
  installation_id: number;
  account_login: string;
  account_type: string;
  account_id: number;
  is_active: boolean;
  suspended_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface GitHubRepo {
  id: number;
  installation_id: number;
  repo_id: number;
  full_name: string;
  name: string;
  owner: string;
  private: boolean;
  default_branch: string;
  clone_url: string;
  html_url: string;
  description: string | null;
  last_synced_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface GitHubInstallationDetail extends GitHubInstallation {
  repos: GitHubRepo[];
}

export interface ManagedSiteGitHubRepo {
  id: number;
  managed_site_id: number;
  github_repo_id: number;
  role: GitHubRepoRole;
  branch: string | null;
  path_prefix: string | null;
  auto_sync: boolean;
  last_commit_sha: string | null;
  last_pulled_at: string | null;
  created_at: string;
  updated_at: string;
  repo: GitHubRepo | null;
}

export interface LinkRepoToSiteRequest {
  github_repo_id: number;
  role?: GitHubRepoRole;
  branch?: string;
  path_prefix?: string;
  auto_sync?: boolean;
}

// ============= Document Types =============
export interface DocumentInfo {
  id: number;
  filename: string;
  file_type: string;
  file_size_bytes: number;
  processing_state: string;
  fact_count: number;
  project_id: number | null;
  category_id: number | null;
  topic_id: number | null;
  topic_keyword_id: number | null;
  created_at: string;
}

export interface DocumentFact {
  id: number;
  document_id: number;
  chunk_index: number;
  content: string;
  created_at: string;
}

// ============= Growth Agent Types =============
export interface GrowthAgentConfig {
  id: number;
  project_id: number;
  niche_description: string | null;
  seed_labels: string[];
  seed_urls: string[];
  is_enabled: boolean;
  growth_agent_interval_minutes: number;
  last_run_at: string | null;
  max_categories_per_run: number;
  max_topics_per_category: number;
  max_keywords_per_topic: number;
  seed_keywords_per_topic: number;
  max_builds_per_session: number;
  max_reviews_per_session: number;
  allow_modify_categories: boolean;
  allow_modify_topics: boolean;
  allow_modify_keywords: boolean;
  allow_build_pages: boolean;
  allow_review_pages: boolean;
  // StageEnum the autonomous loop runs on. Used to deep-link the model
  // chip to LLM settings for that stage.
  agent_stage: string;
  // Resolved label of the model bound to `agent_stage`, e.g. "Claude
  // Sonnet 4.5". null when no model is configured.
  agent_model_label: string | null;
}

export interface GrowthAgentConfigUpdate {
  niche_description?: string | null;
  seed_labels?: string[];
  seed_urls?: string[];
  is_enabled?: boolean;
  growth_agent_interval_minutes?: number;
  max_categories_per_run?: number;
  max_topics_per_category?: number;
  max_keywords_per_topic?: number;
  seed_keywords_per_topic?: number;
  max_builds_per_session?: number;
  max_reviews_per_session?: number;
  allow_modify_categories?: boolean;
  allow_modify_topics?: boolean;
  allow_modify_keywords?: boolean;
  allow_build_pages?: boolean;
  allow_review_pages?: boolean;
}
export interface DocumentDetail extends DocumentInfo {
  raw_content: string;
}

// ============= Image Types =============
export interface ImageVariant {
  id: number;
  label: string;
  width: number;
  height: number | null;
  image_url: string;
  file_size: number | null;
  is_primary: boolean;
}

export interface ImageInfo {
  id: number;
  title: string | null;
  alt_text: string | null;
  description: string | null;
  image_url: string | null;
  placeholder_key: string | null;
  source: string;
  project_id: number | null;
  category_id: number | null;
  topic_id: number | null;
  page_id: number | null;
  generation_error?: string | null;
  used_on_pages?: number;
  variants?: ImageVariant[];
  created_at: string;
}

// ============= Dashboard Types =============
export interface RecentPageSummary {
  id: number;
  title: string | null;
  keyword_value: string | null;
  status: string | null;
  topic_name: string | null;
  updated_at: string;
}

export interface DashboardStats {
  project_count: number;
  category_count: number;
  topic_count: number;
  keyword_count: number;
  pages_by_status: Record<string, number>;
  total_pages: number;
  pending_rec_keyword_count: number;
  pending_rec_topic_count: number;
  top_growing_grow: TrendingKeyword[];
  top_growing_optimize: TrendingKeyword[];
  keywords_without_pages: number;
  recent_pages: RecentPageSummary[];
}

// ============= Autocomplete Types =============
export interface AutocompleteRequest {
  context: string;
  current_value: string;
  additional_context?: Record<string, string>;
  count?: number;
  exclude?: string[];
}

export interface AutocompleteResponse {
  suggestions: string[];
}

// ============= Proxy Settings Types =============
export interface ProxySettings {
  id: number;
  /** The list this proxy belongs to. Every proxy is the network's — lists are admin-run. */
  proxy_list_id: number | null;
  server: string;
  port: number;
  username: string | null;
  /** Always true now; kept for older readers. */
  is_system: boolean;
  is_enabled: boolean;
  /** The proxy itself failed (a 502, refused connections): skipped for every site, briefly. */
  cooling_down: boolean;
  /** Sites it's resting from after being walled or timing out there; it serves every other site. */
  resting_for: { host: string; seconds_left: number }[];
  captcha_encountered_count: number;
  success_count: number;
  last_captcha_encountered_at: string | null;
  created_at: string;
  updated_at: string;
}

/** The fetch methods a proxy list can serve. A method no list serves fetches direct. */
export type ProxyMethod = 'crawl4ai' | 'http_pages' | 'http_documents';

export interface ProxyList {
  id: number;
  name: string;
  is_enabled: boolean;
  uses: ProxyMethod[];
  notes: string | null;
  proxy_count: number;
  enabled_count: number;
  /** Proxies that are down (resting from every site). */
  cooling_count: number;
  /** Proxies resting from at least one site; still serving the rest. */
  resting_somewhere_count: number;
  created_at: string;
  updated_at: string;
}

export interface ProxyListBody {
  name?: string;
  is_enabled?: boolean;
  uses?: ProxyMethod[];
  notes?: string | null;
}

export interface ProxyUpdateRequest {
  is_enabled?: boolean;
  server?: string;
  port?: number;
  username?: string | null;
  password?: string | null;
  is_system?: boolean;
  proxy_list_id?: number;
}

export interface ProxyCreateRequest {
  proxy_list_id: number;
  server: string;
  port: number;
  username?: string | null;
  password?: string | null;
  is_system?: boolean;
}

export interface ProxySettingsListResponse {
  items: ProxySettings[];
}

export interface ProxyBulkAddResponse {
  added: number;
  errors: string[];
}

export interface ProxyTestResponse {
  ok: boolean;
  status_code: number | null;
  response_time_ms: number | null;
  test_url: string;
  error: string | null;
}

// ============= Image Storage Settings Types =============
// S3-compatible object storage for AI-generated page images. The secret key
// is write-only — responses never include it (only `has_secret`).
export interface ImageStorageSettings {
  id: number;
  user_id: number | null;
  name: string | null;
  endpoint_url: string | null;
  region: string | null;
  bucket: string;
  access_key_id: string;
  public_base_url: string | null;
  is_enabled: boolean;
  is_system: boolean;
  has_secret: boolean;
  optimize_uploads: boolean;
  image_format: string;
  image_quality: number;
  variant_widths: number[] | null;
  created_at: string;
  updated_at: string;
}

export interface ImageStorageSettingsCreate {
  name?: string | null;
  endpoint_url?: string | null;
  region?: string | null;
  bucket: string;
  access_key_id: string;
  secret_access_key: string;
  public_base_url?: string | null;
  is_enabled?: boolean;
  is_system?: boolean;
  optimize_uploads?: boolean;
  image_format?: string;
  image_quality?: number;
  variant_widths?: number[];
}

export interface ImageStorageSettingsUpdate {
  name?: string | null;
  endpoint_url?: string | null;
  region?: string | null;
  bucket?: string;
  access_key_id?: string;
  // Omit to keep the stored secret; send a non-empty string to replace it.
  secret_access_key?: string;
  public_base_url?: string | null;
  is_enabled?: boolean;
  optimize_uploads?: boolean;
  image_format?: string;
  image_quality?: number;
  variant_widths?: number[];
}

export interface ImageStorageSettingsListResponse {
  items: ImageStorageSettings[];
}

export interface ImageStorageTestResponse {
  ok: boolean;
  latency_ms: number;
  public_readable?: boolean;
  public_url?: string | null;
  error?: string | null;
}

// ============= Network Settings Types =============
/** One selectable engine, and whether it works on the CURRENTLY selected downloader. */
export interface EngineOption {
  engine: string;
  supported: boolean;
  note: string;
}

export interface NetworkSettings {
  id: number;
  is_network_enabled: boolean;
  search_engine: string;
  /** The pool actually searched. Empty means "just `search_engine`". */
  search_engines: string[];
  /** SPREAD deals queries across the pool; BROADCAST asks them all and merges. */
  engine_mode: string;
  engine_options: EngineOption[];
  /** Engine availability for every search source, so the form re-lists them as the source changes. */
  engine_options_by_source: Record<string, EngineOption[]>;
  browser_type: string;
  /** Where SEARCH RESULTS come from. Pages go through `effective_page_rungs`. */
  downloader_type: string;
  /** 'modern' = ladders; 'legacy' = `downloader_type` does everything, no fallback. */
  network_mode: 'modern' | 'legacy';
  /** The rungs pages / searches may climb, as saved (null = all of them). */
  page_rungs: string[] | null;
  serp_rungs: string[] | null;
  /** What pages / searches climb right now, cheapest first (rungs that can't run left out). */
  effective_page_rungs: string[];
  effective_serp_rungs: string[];
  /** Fetchers switched off altogether (kept set up, used by nothing). */
  disabled_fetchers: string[];
  /** Engine × fetcher pairs switched off under what's possible, e.g. "Crawl4ai:Bing". */
  engine_fetcher_off: string[];
  /** The value in use comes from the HOUSE_* env vars because none is saved. */
  crawler_host_from_env: boolean;
  serper_key_from_env: boolean;
  crawler_host: string | null;
  batch_size: number;
  max_captcha_encounters_before_suspension: number;
  sleep_seconds_after_captcha: number;
  sleep_seconds_between_batches: number;
  desired_serp_count: number;
  use_cache: boolean;
  recrawl_after_days: number | null;
  fetch_timeout_seconds: number;
  shared_block_hours: number;
  fetch_adapters: Record<string, AdapterChoice>;
  browser_render_pages: boolean;
  extract_page_facts: boolean;
  serper_api_key_set: boolean;
  serper_country: string | null;
  serper_language: string | null;
  serper_location: string | null;
  created_at: string;
  updated_at: string;
}

export interface AmazonAffiliateSettings {
  id: number | null;
  user_id: number | null;
  is_enabled: boolean;
  associate_tag: string | null;
  access_key_id: string | null;
  has_secret: boolean;
  adserver_account_id: string | null;
  adserver_synced_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface AmazonAffiliateSettingsUpdate {
  is_enabled?: boolean;
  associate_tag?: string | null;
  access_key_id?: string | null;
  // Omit to keep the stored secret; empty string clears it.
  secret_access_key?: string;
}

export interface NetworkSettingsUpdate {
  is_network_enabled?: boolean;
  search_engine?: string;
  search_engines?: string[];
  engine_mode?: string;
  browser_type?: string;
  downloader_type?: string;
  network_mode?: 'modern' | 'legacy';
  page_rungs?: string[] | null;
  serp_rungs?: string[] | null;
  disabled_fetchers?: string[];
  engine_fetcher_off?: string[];
  crawler_host?: string | null;
  batch_size?: number;
  max_captcha_encounters_before_suspension?: number;
  sleep_seconds_after_captcha?: number;
  sleep_seconds_between_batches?: number;
  desired_serp_count?: number;
  use_cache?: boolean;
  recrawl_after_days?: number | null;
  fetch_timeout_seconds?: number;
  shared_block_hours?: number;
  fetch_adapters?: Record<string, AdapterChoice>;
  browser_render_pages?: boolean;
  extract_page_facts?: boolean;
  serper_api_key?: string;
  serper_country?: string | null;
  serper_language?: string | null;
  serper_location?: string | null;
}

// ============= Network Request Report Types =============
export interface NetworkRequestTimeseriesBucket {
  label: string;
  request_count: number;
  success_count: number;
  captcha_count: number;
  error_count: number;
  avg_response_time: number | null;
}

export interface NetworkRequestTimeseries {
  buckets: NetworkRequestTimeseriesBucket[];
  period_start: string | null;
}

export interface NetworkRequestReportRow {
  search_engine: string | null;
  request_count: number;
  success_count: number;
  captcha_count: number;
  error_count: number;
  avg_response_time: number | null;
}

export interface NetworkRequestReport {
  rows: NetworkRequestReportRow[];
  period_start: string | null;
  total_requests: number;
  total_successes: number;
  total_captchas: number;
  total_errors: number;
}

export interface NetworkRequestRow {
  id: number;
  created_at: string;
  url: string;
  status_code: number | null;
  response_time: number | null;
  error: string | null;
  captcha_encountered: boolean;
  proxy: string | null;
  search_engine: string | null;
  browser_type: string | null;
  downloader_type: string | null;
}

export interface NetworkRequestRowsResponse {
  rows: NetworkRequestRow[];
  total_count: number;
}

// External API usage (Google Ads, GNews)

/** `''` = your own usage. An admin may also pass a user id, or 'all'. */
export type UsageUserScope = string;

export interface ApiUsageReportRow {
  provider: string;
  operation: string;
  call_count: number;
  success_count: number;
  error_count: number;
  rate_limited_count: number;
  /** null when no call in the group reported one — unknown, not zero. */
  result_count: number | null;
  avg_response_time: number | null;
}

export interface ApiUsageUserRow {
  user_id: number | null;
  user_label: string;
  call_count: number;
  success_count: number;
  error_count: number;
}

export interface ApiUsageReport {
  rows: ApiUsageReportRow[];
  /** Only populated when the report is scoped to every user. */
  by_user: ApiUsageUserRow[];
  period_start: string | null;
  total_calls: number;
  total_successes: number;
  total_errors: number;
  total_rate_limited: number;
}

export interface ApiUsageTimeseriesBucket {
  label: string;
  call_count: number;
  success_count: number;
  error_count: number;
  rate_limited_count: number;
  avg_response_time: number | null;
}

export interface ApiUsageTimeseries {
  buckets: ApiUsageTimeseriesBucket[];
  period_start: string | null;
}

export interface ApiUsageCallRow {
  id: number;
  created_at: string;
  provider: string;
  operation: string;
  user_id: number | null;
  user_label: string | null;
  succeeded: boolean;
  status_code: number | null;
  error: string | null;
  response_time: number | null;
  result_count: number | null;
  rate_limited: boolean;
  detail: string | null;
}

export interface ApiUsageCallsResponse {
  rows: ApiUsageCallRow[];
  total_count: number;
}

// Page-style content specs (per-user override of built-in defaults)

export interface PageStyleSpec {
  min_words: number;
  target_words: number;
  max_words: number;
  min_h2_sections: number;
  max_h2_sections: number;
  expects_faq: boolean;
  expects_table: boolean;
  expects_toc: boolean;
  meta_title_max: number;
  meta_description_min: number;
  meta_description_max: number;
  // Whether we can author this style at all. Gates SELECTION only — the Angle
  // phase won't propose a style that's off, and the authoring pickers hide it.
  // Pages already built in a disabled style keep resolving their spec.
  can_be_generated: boolean;
}

// Sparse override — null means "use the built-in default for this field".
export interface PageStyleSpecOverridePayload {
  min_words?: number | null;
  target_words?: number | null;
  max_words?: number | null;
  min_h2_sections?: number | null;
  max_h2_sections?: number | null;
  expects_faq?: boolean | null;
  expects_table?: boolean | null;
  expects_toc?: boolean | null;
  meta_title_max?: number | null;
  meta_description_min?: number | null;
  meta_description_max?: number | null;
  can_be_generated?: boolean | null;
}

export interface PageStyleSpecRow {
  page_style: string;
  builtin: PageStyleSpec;
  override: { [field: string]: number | boolean | null };
  effective: PageStyleSpec;
}

// ========================================================================
// Page-builder research settings (per-user knobs for the agent's research
// depth, iteration ceilings, section-write budget, and compliance retries).
// ========================================================================

export interface ResearchSettings {
  min_serp_pages: number;
  min_facts: number;
  research_max_iter: number;
  framing_max_iter: number;
  outline_max_iter: number;
  write_max_iter: number;
  ground_max_iter: number;
  refine_max_iter: number;
  section_tool_max_iter: number;
  length_topup_max: number;
  max_compliance_fixes: number;
}

export type ResearchSettingsField = keyof ResearchSettings;

export type ResearchSettingsOverridePayload = {
  [K in ResearchSettingsField]?: number | null;
};

export interface ResearchSettingsFieldDef {
  key: ResearchSettingsField;
  label: string;
  min: number;
  max: number;
}

export interface ResearchSettingsFieldGroup {
  key: string;
  label: string;
  description: string;
  fields: ResearchSettingsFieldDef[];
}

/** Your rules for judging keywords: the ★ and when a page-one result is open.
 *  See backend keywords/scoring_settings.py. */
export type KeywordScoringValue = number | boolean | string;

/** What one "Fetch SERPs" press did with each keyword. */
export interface SERPBulkRequestResponse {
  queued: number[];
  pending: number[];
  recent: number[];
  over_limit: number[];
}

export interface KeywordScoringFieldDef {
  key: string;
  label: string;
  kind: 'number' | 'toggle' | 'choice';
  min?: number;
  max?: number;
  options?: string[];
}

export interface KeywordScoringFieldGroup {
  key: string;
  label: string;
  description: string;
  fields: KeywordScoringFieldDef[];
}

export interface KeywordScoringResponse {
  builtin: Record<string, KeywordScoringValue>;
  /** Only what you changed; null means the default. */
  override: Record<string, KeywordScoringValue | null>;
  effective: Record<string, KeywordScoringValue>;
  field_groups: KeywordScoringFieldGroup[];
}

export interface ResearchSettingsResponse {
  builtin: ResearchSettings;
  override: { [K in ResearchSettingsField]: number | null };
  effective: ResearchSettings;
  field_groups: ResearchSettingsFieldGroup[];
}

// Project structure planner — chat-driven category / topic / topic-seed
// proposal that the user iterates on with the LLM and then accepts.

export interface StructurePlanSeedKeyword {
  // Wire stores seeds as plain strings; this alias is just for clarity.
}

/**
 * One proposed node. RECURSIVE: `topics` means "the nodes under this one", so a
 * proposal can be as deep as the subject needs. Trees written before the model
 * had depth simply never nest, which is why the key kept its old name.
 */
export interface StructurePlanNode {
  name: string;
  description?: string | null;
  seed_keywords?: string[];
  topics?: StructurePlanNode[];
}

/** Kept as aliases — the shape is one node type at every level now. */
export type StructurePlanTopicNode = StructurePlanNode;
export type StructurePlanCategoryNode = StructurePlanNode;

export interface StructurePlanTree {
  categories: StructurePlanNode[];
}

export interface StructurePlanMessage {
  id: number;
  session_number: number;
  message_number: number;
  // SYSTEM = compaction summary the LLM produced; rendered as a special
  // band in the feed so the user can see the watermark.
  entity_type: 'HUMAN' | 'AI' | 'SYSTEM' | string;
  content: string;
  has_tool_call: boolean;
  created_at: string;
}

/** Project.site_brief - the answers from the Plan structure interview. */
export interface SiteBrief {
  audience: string;
  goals?: string;
  site_role?: string;
  scope?: string;
  target_pages?: number;
  exclusions?: string;
  notes?: string;
  updated_at?: string;
}

/** What the planner's ask_user tool shows: questions with clickable answers. */
export interface PlannerQuestionCard {
  intro: string;
  questions: { question: string; options: string[]; multiple: boolean }[];
}

export interface StructurePlanResponse {
  id: number;
  project_id: number;
  // null = project-level plan; set = a category-scoped session.
  category_id?: number | null;
  status: 'idle' | 'thinking' | 'failed' | 'accepted' | string;
  // When the running turn started, while status is 'thinking' (ISO time).
  working_since?: string | null;
  // The running turn's tier, while status is 'thinking'.
  working_tier?: 'quick' | 'thorough' | string | null;
  last_error?: string | null;
  working_tree?: StructurePlanTree | null;
  accepted_at?: string | null;
  // Only the current session's messages — older sessions stay in the DB
  // but aren't returned here. `current_session_number` reflects which
  // session this list belongs to (incremented by "Start fresh chat").
  messages: StructurePlanMessage[];
  current_session_number: number;
  compactable_turn_count: number;
  // Approximate context usage for the meter chip. context_window is null
  // when the model's window is unknown.
  context_tokens: number;
  context_window?: number | null;
  // How many auto-saved point-in-time snapshots are available. UI uses
  // this for the "Versions (N)" affordance without a second round-trip.
  snapshot_count: number;
  // Follow-up prompts the agent proposed last turn, rendered as
  // clickable quick-prompt chips. Empty/absent → static starters.
  suggested_prompts?: string[];
  /** The recon room this subject reads, if any. */
  room_name?: string | null;
  /** Who the site is for, from the planner's interview; null before it. */
  site_brief?: SiteBrief | null;
  /** The planner's questions, while it waits for answers. */
  pending_questions?: PlannerQuestionCard | null;
  agent_stage: string;
  agent_model_label?: string | null;
}

export interface StructurePlanSnapshotSummary {
  id: number;
  created_at: string;
  // 'auto'        — saved before the LLM overwrote working_tree.
  // 'pre_restore' — saved as a safety net before restoring an older snapshot.
  // 'pre_rebuild' — the draft as it was before "Rebuild site".
  kind: 'auto' | 'pre_restore' | string;
  category_count: number;
  topic_count: number;
  seed_count: number;
}

export interface StructurePlanSnapshotDetail {
  id: number;
  created_at: string;
  kind: 'auto' | 'pre_restore' | string;
  tree: StructurePlanTree;
}

export interface StructurePlanAcceptResponse {
  categories_created: number;
  topics_created: number;
  seed_keywords_created: number;
  skipped: string[];
}

export interface StructurePlanClearUnusedResponse {
  seed_keywords_removed: number;
  topics_removed: number;
  categories_removed: number;
}

export interface StructurePlanMergeDuplicatesResponse {
  categories_merged: number;
  topics_merged: number;
  keywords_merged: number;
}

// Knowledge planner — chat-driven proposal of bedrock-knowledge
// SUGGESTIONS attached to a KnowledgeBrief. Suggestions live in the
// plan's working set until the user clicks Apply, which materializes
// them as real KnowledgeProposal rows. Backed by the knowledge_plan
// API router.

// Where a suggestion should land when applied. Resolved server-side:
//  - 'current'  = the brief the plan is anchored at.
//  - 'category' = parent category brief (topic-scope plans only).
//  - 'project'  = project brief (category- or topic-scope plans).
// Invalid combinations clamp to 'current' so a stray value never drops
// the suggestion.
export type KnowledgePlanTargetScope = 'current' | 'category' | 'project';

export interface KnowledgeSuggestion {
  id: number;
  // Canonical uppercase value from the KnowledgeProposalType enum
  // ('HEURISTIC', 'FACT', ...). Lowercase for display via
  // `.toLowerCase()` — mirrors the convention used elsewhere for
  // KnowledgeProposal rows.
  proposal_type: string;
  text: string;
  target_scope: KnowledgePlanTargetScope;
  source_urls: string[];
  confidence: number | null;
  added_at: string | null;
  // 'agent' = LLM-proposed; 'user' reserved for future manual entry.
  source: 'agent' | 'user' | string;
}

export interface KnowledgePlanMessage {
  id: number;
  session_number: number;
  message_number: number;
  // SYSTEM = compaction summary the LLM produced; rendered as a special
  // band in the feed.
  entity_type: 'HUMAN' | 'AI' | 'SYSTEM' | string;
  content: string;
  has_tool_call: boolean;
  created_at: string;
}

export interface KnowledgePlanResponse {
  id: number;
  brief_id: number;
  scope: KnowledgeAgentScope;
  scope_id: number;
  status: 'idle' | 'thinking' | 'failed' | string;
  last_error?: string | null;
  suggestions: KnowledgeSuggestion[];
  messages: KnowledgePlanMessage[];
  current_session_number: number;
  // How many older turns the next Compact would fold in.
  compactable_turn_count: number;
  // Approximate context usage for the meter chip. context_window is null
  // when the model's window is unknown.
  context_tokens: number;
  context_window?: number | null;
  // Follow-up prompts the agent proposed last turn, rendered as
  // clickable quick-prompt chips. Empty/absent → static starters.
  suggested_prompts?: string[];
  // Primary LLM stage that drives this chat. The slideout uses this
  // to render a model chip and deep-link to LLM settings.
  agent_stage: string;
  // Resolved model label (e.g. "Claude Sonnet 4.5"). Null means no
  // model is configured for this stage (and no DEFAULT fallback
  // either) — the chip should display "not configured".
  agent_model_label?: string | null;
}

export interface KnowledgePlanApplyRequest {
  // Omit to apply every suggestion in the working set.
  suggestion_ids?: number[];
}

export interface KnowledgePlanApplyResponse {
  proposals_created: number;
  skipped_duplicates: number;
  proposals_by_target_scope: Record<string, number>;
  plan: KnowledgePlanResponse;
}

export interface KnowledgePlanWebSocketEvent {
  event_type: 'knowledge_plan';
  brief_id: number;
  plan_id: number;
  scope: KnowledgeAgentScope;
  scope_id: number;
  status: 'started' | 'replied' | 'failed' | string;
  message?: string;
}

// ============ Browser Plugin ============

export interface PersonalAccessToken {
  id: number;
  name: string | null;
  token_prefix: string | null;
  last_used_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

// Returned ONCE on creation — carries the plaintext token.
export interface PersonalAccessTokenCreated extends PersonalAccessToken {
  token: string;
}

export interface PluginDevice {
  id: number;
  device_id: string;
  name: string | null;
  max_tabs: number;
  last_seen_at: string | null;
  online: boolean;
  /** Shared with all users: it also picks up other users' queued jobs. */
  is_system?: boolean;
  /** Shared, and its console takes other users' signed-in site searches. */
  console_for_others?: boolean;
}

/** A v2 (live-connection) extension device. `online` is Centrifugo presence, live. */
/** The two browser extensions: one that checks in for queued jobs, one that
 *  keeps a live connection. */
export type ExtensionKind = 'queued' | 'connected';

export interface PluginV2Device {
  id: number;
  device_id: string;
  name: string | null;
  max_tabs: number;
  last_connected_at: string | null;
  online: boolean;
  /** Shared with all users: their fetches can run in this browser when they have none of their own. */
  is_system: boolean;
  /** When sharing was turned on. */
  shared_since: string | null;
  /** Shared, and its console takes other users' signed-in site searches. */
  console_for_others?: boolean;
}

/** One fetcher's status card on the admin Network screen (GET /network/fetchers). */
export interface FetcherStatus {
  key: string;
  label: string;
  status: 'ready' | 'needs_setup' | 'unavailable' | 'off';
  detail: string;
  proxy_lists: string[];
  extra: Record<string, number>;
}

export interface NetworkFetchersResponse {
  fetchers: FetcherStatus[];
  /** Page fetches per rung over the last 7 days. */
  rung_mix: Record<string, number>;
}

/** One engine × search-fetcher cell of the admin matrix (GET /network/engines). */
export interface EngineFetcherCell {
  /** Can this ever work (it takes a parser)? A switch can't make an impossible pair work. */
  possible: boolean;
  why_not: string | null;
  /** Switched on by the admins. */
  on: boolean;
  /** Seconds left while the circuit breaker is skipping this pair after repeated failures. */
  skipped_for_s: number | null;
  /** The failed searches that tripped the pause, newest first. */
  skipped_because?: BreakerEvidence[];
}

/** One failed search the breaker kept as the reason it paused a pair. */
export interface BreakerEvidence {
  /** Unix seconds. */
  at?: number;
  query?: string;
  /** "captcha", "no results", a crawler error. */
  reason?: string;
  status?: number | null;
  /** Where the request ended up, when it was redirected. */
  landed?: string | null;
  title?: string | null;
  chars?: number;
  snippet?: string | null;
  /** "challenge page", "needs JavaScript", "nearly empty page". */
  looks?: string | null;
}

export interface EngineMatrixRow {
  engine: string;
  selected: boolean;
  note: string;
  fetchers: Record<string, EngineFetcherCell>;
}

export interface EngineMatrix {
  fetchers: string[];
  disabled_fetchers: string[];
  rows: EngineMatrixRow[];
}

/** The read-only account of the network every user sees (GET /network/status). */
export interface NetworkStatus {
  network_enabled: boolean;
  mode: 'modern' | 'legacy';
  search_source: string;
  page_rungs: string[];
  your_browsers_connected: number;
  shared_browsers_connected: number;
}

export interface AdminSetting {
  key: string;
  /** For a secret key this is a MASK ("••••••••101f"), never the stored value.
   *  Read `is_set` to know whether one exists, and never send this back — the
   *  API rejects a masked write rather than overwriting the real credential. */
  value: string | null;
  is_set: boolean;
  updated_at: string | null;
}

/** A subject that reads a room — just enough to name it and link to it. */
export interface ReaderSubject {
  id: number;
  name: string;
}

/**
 * One stage a screen bills against, resolved to the model that will actually
 * run it. `source` says WHICH rung of the stage/group/DEFAULT chain matched, so
 * the models widget can mark a stage as riding the shared chat model or the
 * account default rather than implying the user picked that model for it.
 */
export interface StageModelInfo {
  stage: string;
  label: string | null;
  stage_label: string;
  source:
    | 'stage'
    | 'group'
    | 'default'
    | 'stage_global'
    | 'group_global'
    | 'default_global'
    | 'none';
  inherited: boolean;
  /** The house model ran on the customer's OWN key (the discounted rate). */
  own_key?: boolean;
  /** Human name of the group that supplied the model ("Chat agents (shared)"),
   * when `source` is a group rung; null for DEFAULT or explicit rows. */
  inherited_from: string | null;
  role: string;
  blurb: string;
  capability: string;
  hint: string;
  /** The cast behind a stage that has one: each seat and the model it runs on. Empty otherwise. */
  seats?: { archetype: string; label: string; model_label: string | null; pinned: boolean }[];
}

// ============ Site parsers (data-driven page parsers) ============

export interface ParserCandidate {
  id: number;
  host: string;
  url_template: string;
  example_urls: string[];
  encounter_count: number;
  status: string; // SUGGESTED | DISMISSED | BUILDING | BUILT
  first_seen?: string | null;
  last_seen?: string | null;
  site_parser_id?: number | null;
}

export interface SiteParser {
  id: number;
  name: string;
  host_suffix: string;
  kind: string;
  status: string; // DRAFT | ACTIVE | QUARANTINED | DISABLED
  spec_version: number;
  approved_for_sources: string[];
  baseline_fill_ratio: Record<string, number>;
  authored_by: string; // HUMAN | LLM
  created_by_user_id?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
  building?: boolean; // an AI (re)build job is in progress
}

export interface SiteParserSample {
  id: number;
  url: string;
  source: string;
  usable_raw: boolean;
  page_id?: number | null;
  captured_at?: string | null;
}

export interface SiteParserDetail extends SiteParser {
  spec: any;
  samples: SiteParserSample[];
}

export interface ParserOpportunityTemplate {
  host: string;
  url_template: string;
  encounter_count: number;
  candidate_id: number;
}

export interface ParserOpportunity {
  candidate_count: number;
  pages_seen: number;
  // An ESTIMATE (avg tokens per extraction x pages seen), not a measured total —
  // `measured_extractions` is the sample size behind the average; 0 means no basis.
  estimated_tokens: number;
  avg_tokens_per_extraction: number;
  measured_extractions: number;
  top: ParserOpportunityTemplate[];
}

export interface SiteParserSpecVersion {
  version: number;
  spec: any;
  authored_by: string;
  note: string | null;
  field_count: number;
  score: number | null;
  is_current: boolean;
  created_at: string;
}

export interface ParserBacktestSourceSummary {
  total: number;
  url_matched: number;
  usable_raw: number;
  extraction_tested: number;
  extracted: number;
  avg_fill_ratio: number;
}

export interface ParserBacktestResult {
  sample_count: number;
  by_source: Record<string, ParserBacktestSourceSummary>;
  samples: any[];
}

export interface BuildCandidateResult {
  site_parser_id: number;
  job_id?: string | null;
  status: string;
}

export interface ParserChatMessage {
  message_number: number;
  entity_type: string; // HUMAN | AI | SYSTEM
  content: string;
  tool_calls?: any[] | null;
}

export interface ParserChat {
  chat_id: number;
  status: string; // idle | thinking | failed
  last_error?: string | null;
  messages: ParserChatMessage[];
  agent_stage?: string;
  agent_model_label?: string | null;
}

// --- Site scaffold (the Hugo site designer on Deliver) ---------------------
//
// A SiteSpec is the small JSON document the designer agent edits: colours,
// fonts, layout, nav. It compiles to a whole Hugo site. Typed loosely on
// purpose — the backend's pydantic model is the authority, and mirroring 45
// leaves here would be a second schema to keep in step for no type safety the
// UI actually uses.

export interface SiteScaffold {
  id: number;
  site_id: number;
  site_name: string;
  site_type: string;
  public_base_url?: string | null;
  spec: any;
  /** Derived server-side: has the draft moved since the last commit? */
  spec_is_ahead: boolean;
  published_commit_sha?: string | null;
  published_at?: string | null;
  repo_full_name?: string | null;
  /** The site this design is being matched to, if any. */
  reference_url?: string | null;
  /** Which brand this design starts from. Null = whichever profile is default. */
  brand_profile_id?: number | null;
  brand_profiles?: SiteScaffoldBrandChoice[];
  /** What each field reverts to. Served by the backend so it cannot drift. */
  default_spec?: any;
}

export interface SiteScaffoldBrandChoice {
  id: number;
  name: string;
  is_default: boolean;
}

export interface SiteScaffoldPreview {
  html: string;
  kind: string;
  /** The page currently on screen, when a link was followed to reach it. */
  path?: string;
  title?: string;
  /** Is this the site's OWN content, or invented sample pages? */
  using_real_pages: boolean;
  /** The deployment plan this was scoped to, when one was named. */
  plan_id?: number | null;
  /** Were pages that will not publish dropped? */
  ready_only?: boolean;
  /** Already published — on the site before this deploy runs. */
  live_count?: number;
  /** READY_TO_DEPLOY: what this deploy adds. */
  deploy_count?: number;
  /** Still being built, so not part of the deploy. Counted even when dropped. */
  not_ready_count?: number;
}

export interface SiteScaffoldFile {
  path: string;
  bytes: number;
  exists: boolean;
}

export interface SiteScaffoldPublishPlan {
  files: SiteScaffoldFile[];
  repo_full_name?: string | null;
  branch?: string | null;
  content_dir: string;
  /** Paths a publish would replace. Non-empty means overwrite is required. */
  collisions: string[];
  blocked_reason?: string | null;
}

export interface SiteScaffoldSpecVersion {
  id: number;
  version: number;
  authored_by: string;
  note?: string | null;
  created_at: string;
  spec: any;
}

export interface SiteScaffoldChatMessage {
  id: number;
  session_number: number;
  message_number: number;
  entity_type: string; // HUMAN | AI | SYSTEM
  content: string;
  has_tool_call: boolean;
  created_at: string;
}

export interface SiteScaffoldChat {
  id: number;
  site_id: number;
  status: string; // idle | thinking | failed
  last_error?: string | null;
  current_session_number: number;
  context_tokens: number;
  context_window?: number | null;
  messages: SiteScaffoldChatMessage[];
  suggested_prompts: string[];
  agent_stage: string;
  agent_model_label?: string | null;
}

// --- News Beats -----------------------------------------------------------
//
// A BEAT is a standing news watch; running one produces a BRIEFING of STORIES
// (clusters of articles sharing one main idea). A THREAD is a story's identity
// ACROSS briefings — what stops a running story being re-reported every night.

export type NewsAttributeRole = 'FILTER' | 'TAG';
export type NewsRereportPolicy = 'EVERY_RUN' | 'COOLDOWN' | 'NEVER';
export type NewsThreadStatus = 'ACTIVE' | 'DORMANT' | 'CLOSED';
export type NewsBriefingStatus =
  | 'QUEUED'
  | 'RUNNING'
  | 'COMPLETED'
  | 'FAILED'
  | 'CANCELLED';

export interface NewsBeatAttribute {
  name: string;
  /** FILTER drops articles that don't match; TAG only labels them. */
  role: NewsAttributeRole;
  /** Allowed values. Empty means open vocabulary. */
  values: string[];
  hint?: string | null;
  sort_order: number;
}

export interface NewsBeatSummary {
  id: number;
  name: string;
  description?: string | null;
  is_active: boolean;
  mode: NewsBeatMode;
  interval_minutes: number;
  rereport_policy: NewsRereportPolicy;
  keyword_count: number;
  attribute_count: number;
  project_id?: number | null;
  project_name?: string | null;
  last_run_at?: string | null;
  last_briefing_id?: number | null;
  last_briefing_status?: NewsBriefingStatus | null;
  last_briefing_at?: string | null;
  last_briefing_stories: number;
  active_thread_count: number;
  created_at: string;
}

export interface NewsBeatDetail {
  id: number;
  name: string;
  description?: string | null;
  /** Standing instructions that are NOT facets — honoured, never used to drop. */
  guidance?: string | null;
  keywords: string[];
  is_active: boolean;
  /** DRAFT results are disposable and re-judgeable; LIVE ones are the beat's
   *  memory. Read-only here — going live has its own endpoint because it
   *  deletes rows. */
  mode: NewsBeatMode;
  interval_minutes: number;
  rereport_policy: NewsRereportPolicy;
  rereport_interval_minutes: number;
  language: string;
  country: string;
  max_articles_per_run: number;
  lookback_minutes?: number | null;
  /** How many judging rounds one run may take before it gives up on the rest. */
  max_passes_per_run: number;
  project_id?: number | null;
  project_name?: string | null;
  last_run_at?: string | null;
  /** Where this beat publishes. Null means it doesn't — the default, and it has
   *  to stay that way: a beat that quietly started writing pages to a live site
   *  would be a bad surprise. */
  publish_plan_id?: number | null;
  publish_plan_name?: string | null;
  publish_site_id?: number | null;
  publish_site_name?: string | null;
  /** Where on the site, e.g. ["briefings","oil"]. */
  publish_path: string[];
  /** Keep a dated copy of every run beside the current one. On by default: it is
   *  what makes overwriting the current page safe. */
  publish_archive: boolean;
  attributes: NewsBeatAttribute[];
  created_at: string;
  updated_at: string;
}

export type NewsBeatMode = 'DRAFT' | 'LIVE';

/** What going live would delete, so the confirm can be honest before the click. */
export interface NewsBeatPromotePreview {
  beat_id: number;
  mode: NewsBeatMode;
  deletes: { briefings: number; stories: number; threads: number };
}

export interface NewsBeatCreate {
  name: string;
  description?: string | null;
  guidance?: string | null;
  keywords?: string[];
  is_active?: boolean;
  interval_minutes?: number;
  rereport_policy?: NewsRereportPolicy;
  rereport_interval_minutes?: number;
  language?: string;
  country?: string;
  /** How many articles one fetch may take. Capped server-side at 250 — GNews
   *  pages are 25 and we will not walk more than ten of them. */
  max_articles_per_run?: number;
  /** Overrides the since-the-last-run window. Null means derive it. */
  lookback_minutes?: number | null;
  max_passes_per_run?: number;
  project_id?: number | null;
  attributes?: NewsBeatAttribute[];
}

export type NewsBeatUpdate = Partial<NewsBeatCreate> & {
  /** Owner-only — these are not tuning fields, and the tuning agent cannot set
   *  them. Explicit null clears the link. */
  publish_plan_id?: number | null;
  publish_path?: string[];
  publish_archive?: boolean;
};

export interface NewsArticleRef {
  id: number;
  title: string;
  url: string;
  source_name?: string | null;
  published_at?: string | null;
  image_url?: string | null;
}

export interface NewsStory {
  id: number;
  headline: string;
  summary: string;
  why_it_matters?: string | null;
  attributes: Record<string, string>;
  article_count: number;
  sort_order: number;
  /** A development on an already-tracked thread rather than a new story. */
  is_update: boolean;
  /** For an update: what changed since the last report. The whole point. */
  new_since_last?: string | null;
  suppressed: boolean;
  suppressed_reason?: string | null;
  thread_id?: number | null;
  thread_title?: string | null;
  thread_digest?: string | null;
  articles: NewsArticleRef[];
}

export interface NewsRejectedArticle {
  article: NewsArticleRef;
  status: string;
  reject_reason?: string | null;
  rejected_by_attribute?: string | null;
}

export interface NewsBriefingSummary {
  id: number;
  beat_id: number;
  status: NewsBriefingStatus;
  trigger: 'SCHEDULED' | 'MANUAL';
  ended_reason?: string | null;
  window_from?: string | null;
  window_to?: string | null;
  started_at?: string | null;
  ended_at?: string | null;
  last_error?: string | null;
  articles_fetched: number;
  articles_filtered: number;
  articles_muted: number;
  stories_written: number;
  updates_written: number;
  created_at: string;
}

export interface NewsBriefingDetail extends NewsBriefingSummary {
  beat_name: string;
  attributes: NewsBeatAttribute[];
  stories: NewsStory[];
  /** Matched an ongoing thread with nothing new to say. Shown collapsed —
   *  never dropped silently, which is what makes the feed trustworthy. */
  muted: NewsStory[];
  rejected: NewsRejectedArticle[];
  /** Fetched but never judged (the run hit its iteration cap or was stopped). */
  unreviewed: NewsArticleRef[];
}

export interface NewsStoryThread {
  id: number;
  title: string;
  slug: string;
  status: NewsThreadStatus;
  digest: string;
  report_count: number;
  article_count: number;
  first_reported_at?: string | null;
  last_reported_at?: string | null;
  last_seen_at?: string | null;
  attributes: Record<string, string>;
}

export interface NewsBeatWebSocketEvent {
  event_type: 'news_beat';
  beat_id: number;
  briefing_id: number;
  status:
    | 'queued'
    | 'started'
    | 'activity'
    | 'story'
    | 'muted'
    | 'rejected'
    | 'progress'
    | 'log'
    | 'finished'
    | 'failed'
    | 'cancelled';
  message?: string;
  story_id?: number;
  thread_id?: number;
  article_id?: number;
  is_update?: boolean;
  articles_fetched?: number;
  stories_written?: number;
  tokens_spent?: number;
  /** On status='log': the persisted run-log step, so the live view appends
   *  exactly the row a reload would fetch. */
  log_step?: NewsRunStep;
  /** On status='progress': ephemeral, coalesced, never stored. */
  progress?: { stage: NewsRunStage; done: number; total: number; pass?: number };
}

export interface NewsBeatChatMessage {
  id: number;
  message_number: number;
  role: 'user' | 'assistant' | 'system';
  content: string | null;
  created_at: string;
}

/** How far through a run each stage is. Every number is DERIVED from columns
 *  the pipeline already writes, so a page opened mid-run rebuilds the whole
 *  picture from one GET — the websocket is an optimisation, not the source. */
export interface NewsRunProgress {
  total: number;
  hydrated: number;
  tagged: number;
  clustered: number;
  judged: number;
  /** Which judging round is running, and out of how many. */
  pass?: number | null;
  of?: number | null;
}

export type NewsRunStage = 'fetch' | 'hydrate' | 'tag' | 'cluster' | 'judge';

/** One step a briefing run took, kept on the briefing after the run ends. */
export interface NewsRunStep {
  phase: 'search' | 'fetch' | 'read' | 'done';
  /** step | warning | elided | story | muted | rejected | finished | failed */
  kind: string;
  at: string;
  message: string;
  data: Record<string, any>;
}

/** Where the text an article was judged on came from. SNIPPET_ONLY means the
 *  body never arrived and everything downstream came from a teaser. */
export type NewsBodySource = 'GNEWS_FULL' | 'FETCHED' | 'SNIPPET_ONLY';

export interface NewsRunLogArticle {
  article_id: number;
  title: string;
  url: string;
  source_name: string | null;
  body_source: NewsBodySource;
  missing_chars: number | null;
  status: string;
  reject_reason: string | null;
  rejected_by_attribute: string | null;
  story_id: number | null;
  cluster_key: number | null;
  /** The who/what/where read out of this article — what decided its cluster. */
  tags: NewsArticleTagRef[];
}

export interface NewsArticleTagRef {
  dimension: string;
  value: string;
}

/** One tag the run produced, counted by what became of the articles carrying
 *  it. Scoped to the briefing — never the global NewsTag.article_count. */
export interface NewsTagStat {
  tag_id: number;
  value: string;
  articles: number;
  in_stories: number;
  dropped: number;
  muted: number;
  unreviewed: number;
  article_ids: number[];
}

export interface NewsBriefingTags {
  briefing_id: number;
  attempt: number;
  dimensions: { dimension: string; label: string; tags: NewsTagStat[] }[];
}

export interface NewsRunLog {
  briefing_id: number;
  status: string;
  /** How many times these articles have been judged. */
  attempt: number;
  phases: { key: string; label: string }[];
  stages: { key: NewsRunStage; label: string }[];
  progress: NewsRunProgress;
  steps: NewsRunStep[];
  body_sources: Partial<Record<NewsBodySource, number>>;
  articles: NewsRunLogArticle[];
}

/** Which tool set a turn runs with. `tune` edits the beat's settings; `comb`
 *  edits one briefing. See jcseo/agents/news_beats_chat/tools.py. */
export type NewsBeatSurface = 'tune' | 'comb';

export interface NewsBeatChat {
  beat_id: number;
  surface: NewsBeatSurface;
  /** idle | thinking | failed */
  status: string;
  last_error?: string | null;
  agent_stage: string;
  suggested_prompts: string[];
  messages: NewsBeatChatMessage[];
}

/** One thing worth knowing about the last run, computed server-side so the
 *  screen and the agent cannot tell two different stories about one run. */
export interface NewsBeatSignal {
  severity: 'blocker' | 'warn' | 'note';
  key: string;
  label: string;
  detail: string;
  fix: string;
}

export interface NewsBeatVerdict {
  beat_id: number;
  briefing_id: number | null;
  counts: {
    fetched: number;
    filtered_out: number;
    stories: number;
    muted_stories: number;
    drops_by_attribute: Record<string, number>;
    keyword_hits: Record<string, number>;
  };
  signals: NewsBeatSignal[];
}

// --- Products: what a user can buy, independently -------------------------------
// Mirrors `jcseo/types.py:Product`. NOT a tier ladder — the three are sold to
// different audiences and a user holds any subset, so nothing here is ordered.
export type Product = 'BEATS' | 'PUBLISH' | 'BOARDROOM';

// --- Boardroom: brainstorms v2 as a work queue -----------------------------------
// Mirrors `jcseo/types.py` Boardroom enums and `jcseo/boardroom/serialize.py`.
// Everything discussed in a room is an ITEM; the buckets on the left are filters
// over item kind + tags; a network lookup is an item a loop services.
export type BoardroomStatus = 'ACTIVE' | 'ARCHIVED';
export type BoardroomItemKind =
  | 'IDEA'
  | 'COMPANY'
  | 'PRODUCT'
  | 'PAIN'
  | 'FEATURE'
  | 'PRICING'
  | 'SOLUTION_TYPE'
  | 'EVIDENCE'
  | 'CRITIQUE'
  | 'VERDICT'
  | 'QUESTION'
  | 'NOTE'
  | 'HUNCH'
  | 'OBSERVATION'
  | 'VIDEO'
  // A story's nouns: a public figure, something that happened on a date, and
  // a question people search that nobody answers well.
  | 'PERSON'
  | 'EVENT'
  | 'ANGLE'
  | 'LOOKUP_SERP'
  | 'LOOKUP_PAGE'
  | 'LOOKUP_DEMAND'
  | 'LOOKUP_TRENDS'
  | 'LOOKUP_NEWS'
  | 'LOOKUP_SOCIAL'
  | 'LOOKUP_CONSOLE';
export type BoardroomItemStatus =
  | 'OPEN'
  | 'RESERVED'
  | 'IN_PROGRESS'
  | 'DONE'
  | 'FAILED'
  | 'KILLED'
  | 'MERGED';
export type BoardroomWorkerKind = 'PERSONA' | 'LOOKUP';
export type BoardroomLinkKind =
  | 'SPAWNED'
  | 'CRITIQUES'
  | 'SUPPORTS'
  | 'CORROBORATES'
  | 'ANSWERS'
  | 'MERGED_INTO'
  | 'VERIFIES'
  | 'INSTANCE_OF'
  | 'SELLS'
  | 'HAS_FEATURE'
  | 'PRICES'
  | 'ADDRESSES'
  | 'DISTINCT_FROM'
  | 'CITES'
  // A PAIN under the COMPANY or PRODUCT it is a complaint about.
  | 'ABOUT';
export type BoardroomVerdictValue = 'HOLDS' | 'DOES_NOT_HOLD' | 'UNVERIFIABLE';
/** The latest VERDICT item on an item; null = nobody has checked it. */
export interface BoardroomVerdictRef {
  verdict: BoardroomVerdictValue | null;
  item_id: number;
  by: number | null;
}
/** WRAP_UP reads results that already landed and stops: it wakes only the
 *  personas holding unread ones, and their lookup tools are withheld so it
 *  cannot put more work on the network. */
export type BoardroomRunIntent = 'WIDER' | 'DEEPER' | 'WRAP_UP';
export type BoardroomRunStatus = 'RUNNING' | 'FINISHED' | 'STOPPED' | 'FAILED';

export interface BoardroomTag {
  id: number;
  item_id: number;
  dimension: string;
  value: string;
  placed_by_persona_id: number | null;
  /** The item (usually EVIDENCE or a DONE lookup) that justifies the tag. */
  receipt_item_id: number | null;
  approved_by_persona_id: number | null;
  approved_at: string | null;
  note: string | null;
  created_at: string;
}

/** A question somebody looked into and found no answer to: "no pricing
 *  published anywhere", "no maker I can identify". Keyed on one item, one job
 *  and one hole, so settling the profiler's pricing question leaves the
 *  customer voice's question about the same item wide open. Shown so it can be
 *  undone — a judgement nobody can reverse is a wall, not a judgement. */
export interface BoardroomClosure {
  id: number;
  item_id: number;
  archetype: string;
  what: string;
  reason: string;
  closed_by_id: number | null;
  created_at: string;
}

export interface BoardroomItem {
  /** A lookup still out: where it is. Null otherwise, or on a live event. */
  lookup_stage?: BoardroomLookupStage | null;
  id: number;
  boardroom_id: number;
  kind: BoardroomItemKind;
  title: string;
  body: string | null;
  payload: Record<string, unknown>;
  status: BoardroomItemStatus;
  worker_kind: BoardroomWorkerKind | null;
  worker_id: string | null;
  attempts: number;
  error: string | null;
  created_by_persona_id: number | null;
  keyword_id: number | null;
  serp_id: number | null;
  page_id: number | null;
  keyword_metrics_run_id: number | null;
  trend_snapshot_id: number | null;
  tags: BoardroomTag[];
  parent_ids: number[];
  verdict: BoardroomVerdictRef | null;
  /** Optional: older cached rows and some event payloads predate it. */
  closures?: BoardroomClosure[];
  /** Where it came from, worked out from its receipts: the page a review,
   *  pain, feature or price was read on. Null when nothing matches. */
  source?: { url: string; via: 'post' | 'own' | 'page' | 'evidence' | 'search' } | null;
  created_at: string;
  updated_at: string;
}

export interface BoardroomItemLinkOut {
  link_kind: BoardroomLinkKind;
  item: BoardroomItem;
}

export interface BoardroomItemDetail extends BoardroomItem {
  children: BoardroomItemLinkOut[];
  /** Everything it led to; `children` lists the first 200 of them. */
  children_total?: number;
  parents: BoardroomItemLinkOut[];
  /** What it rests on: the lookups and evidence stamped on it when it was made. */
  receipts: BoardroomItem[];
}

/** One seat's worklist for one round: words addressed to it, AND the items
 *  lifted to the front of its candidates. The run's `directive` is a broadcast
 *  that never touched the work queue; this is the pair. */
export interface BoardroomAssignment {
  id: number;
  run_id: number;
  round_no: number;
  archetype: string;
  persona_id: number | null;
  what: string | null;
  text: string;
  item_ids: number[];
  /** For the reader, never sent to the persona. */
  reason: string | null;
  status: 'pending' | 'taken' | 'done' | 'superseded' | 'dropped';
  source: 'policy' | 'director' | 'human';
  created_at: string;
}

export interface BoardroomPersona {
  id: number;
  archetype: string;
  name: string;
  model: string | null;
  is_active: boolean;
  tokens_spent: number;
  turns_done: number;
  /** Successful calls by tool name, over this seat's life — `{add_axis: 8}`.
   *  Turns and tokens say it ran; this is the only field that says it worked. */
  writes?: Record<string, number> | null;
  /** The dispatcher's busy lease is held (queued or working). */
  busy: boolean;
  /** queued = on the RQ queue, working = the turn has started,
   *  nothing_left = the dispatcher looked and the board has no work for it. */
  state: 'idle' | 'queued' | 'working' | 'nothing_left';
  busy_since: string | null;
  /** What this seat is being asked to do now, if anything. */
  assignment?: BoardroomAssignment | null;
  /** Staffed by the director during this run rather than by a person. */
  added_by_run_id?: number | null;
}

/** What the room made of itself at the last round boundary. */
export interface BoardroomDirectorNote {
  state: string;
  /** Something wrong with the board's SHAPE that no hole describes. */
  observation?: string | null;
  gap_kind?: 'ORPHANS' | 'DISAGREEMENT' | 'STALLED' | 'THIN' | 'NONE' | null;
  /** The turn count it was written at: a note older than the board is stale. */
  turns_at?: number;
}

/** What an empty board was asked to find out first — the room's opening
 *  hunches — and the searches queued to check them before any seat had a turn.
 *  Every seat reads it in its brief. */
export interface BoardroomResearchPlan {
  status: 'planning' | 'ready' | 'failed';
  summary?: string;
  questions?: string[];
  hunches?: { id: number | null; title: string; check: 'players' | 'pains' | 'demand' }[];
  searches?: {
    check: 'players' | 'pains' | 'demand';
    archetype: string;
    kind: string;
    query: string;
    item_id: number | null;
  }[];
}

export interface BoardroomRun {
  id: number;
  intent: BoardroomRunIntent;
  status: BoardroomRunStatus;
  token_budget: number;
  /** Everything the turns SENT, prompt-cache reads included. */
  tokens_spent: number;
  /** What the cache did not serve — the number the budget is measured against.
   *  On a measured dig 85% of input came back from the cache, so a meter drawn
   *  from `tokens_spent` says a room is nearly out when it has barely read. */
  tokens_billed?: number;
  turns_done: number;
  lookups_done: number;
  /** Tags the sorter placed on this run, and what that cost. The tokens are
   *  inside `tokens_spent` as well — this is the share that went on sorting. */
  tags_placed?: number;
  tagger_tokens?: number;
  idle_ticks: number;
  ended_reason: string | null;
  directive: string | null;
  /** A focused run ("dig into this"): only this archetype is woken, with this item first. */
  focus_archetype: string | null;
  focus_item_id: number | null;
  scope: BoardroomRunScope | null;
  /** The lead the room followed when it started: the dig is inside it. */
  lead_id?: number | null;
  created_at: string;
  ended_at: string | null;
  /** THE DIRECTOR'S OWN TALLY. Kept apart from `tokens_spent`, which is what
   *  was bought for digging — a reader spending from it would make every dig
   *  shallower than the last, invisibly. */
  director_rounds?: number;
  director_tokens?: number;
  director_note?: BoardroomDirectorNote | null;
  /** The opening research plan, written once on an empty board. */
  plan?: BoardroomResearchPlan | null;
  /** Questions this dig ANSWERED, and ones it turned up while answering.
   *  Both, separately — opening holes is the room discovering, and a net count
   *  makes a productive dig and an idle one look identical. */
  holes_closed?: number;
  holes_opened?: number;
}

export interface BoardroomFilterCount {
  key: string;
  label: string;
  count: number;
}

/** Where a lookup is between asked and read. A landed result outlives the
 *  run that asked for it; "landed" is what the next dig will open with. */
/** Where the lookups still out are: waiting for a free fetcher, running, or
 *  back and being parsed. Also filter states in the rail. */
export type BoardroomLookupStage =
  | 'waiting_to_search'
  | 'searching'
  | 'waiting_to_load'
  | 'loading'
  | 'waiting_for_data'
  | 'getting_data'
  | 'parsing'
  | 'in_console';
export type BoardroomLookupStages = Record<BoardroomLookupStage, number>;
/** `queued` and `fetching` are the totals of the stages; the rail lists the stages. */
/** `not_fetched`: the dig ended before it ran. Not a failure, so not counted as one. */
export type BoardroomLookupState = 'queued' | 'fetching' | 'landed' | 'read' | 'failed' | 'not_fetched' | BoardroomLookupStage;
export type BoardroomLookupCounts = Record<'queued' | 'fetching' | 'landed' | 'read' | 'failed', number> &
  Partial<Record<BoardroomLookupStage | 'not_fetched', number>>;

/** What the sorter loop has done and has left to do. Room-wide: sorting is not
 *  narrowed by a lens the way the item counts are. */
/** One claim behind the room's answer. `receipt_ids` are DURABLE board item ids
 *  — never the digest's render-time reference numbers, which are renumbered on
 *  every render. */
export interface BoardroomAnswerClaim {
  key: string;
  text: string;
  side: 'for' | 'against' | string;
  receipt_ids: number[];
}

/** The room's answer to its own question, as of one version.
 *
 *  Versioned like the frame: append-only, id order is version order, the current
 *  one is the latest that has not been dismissed. What a version ERASED is kept
 *  on the version that erased it (`dropped`), because a board you can watch
 *  being rubbed out is the whole point. */
export interface BoardroomAnswer {
  id: number;
  boardroom_id: number;
  previous_id: number | null;
  copied_from_id: number | null;
  stance: string;
  stance_key: string;
  /** What is being chosen ("dentists"), and the verdict on it. Together they are
   *  what makes a reversal checkable rather than a matter of opinion. */
  subject: string;
  direction: 'for' | 'against' | 'open' | string;
  /** The question as this pass read it: description plus focus. */
  question: string | null;
  strength: 'hunch' | 'leaning' | 'likely' | 'confident' | string;
  /** What the model ASKED for, before the board capped it. Both are kept: the
   *  cap is the honesty mechanism, and hiding it would make it unauditable. */
  strength_asked: string;
  /** Why it is not stronger, in counted words: "2 sources, one voice". */
  strength_capped: string | null;
  support: {
    receipts?: number;
    publishers?: number;
    holds?: number;
    does_not_hold?: number;
    against?: number;
    pains?: number;
    ideas?: number;
  };
  claims: BoardroomAnswerClaim[];
  /** What this version no longer says, and why. This is what gets struck out. */
  dropped: { key: string; text: string; why: string }[];
  /** Earlier answers this one says are wrong. The one amber thing on the line. */
  ruled_out: { stance_key: string; stance: string; why: string }[];
  would_change_it: { text: string; kind: string }[];
  movement: 'first' | 'unchanged' | 'sharpened' | 'reversed' | 'widened' | 'narrowed' | string;
  moved_by: string | null;
  /** A pass that read the board and found the answer still stood. Kept so the
   *  trail shows the room checked, and filtered out of the versions list. */
  unchanged: boolean;
  dismissed_at: string | null;
  /** Why the person dismissed it, in their words; the next answer pass reads it. */
  dismissed_reason?: string | null;
  run_id: number | null;
  turns_at: number;
  based_on_lookups_done: number;
  written_by: string;
  created_at: string | null;
}

/** The counters beside Stop. `quiet_turns` is SAID, never acted on: a room that
 *  stopped itself because its answer settled would be a room that gave up early
 *  with nobody to ask. */
export interface BoardroomAnswering {
  versions: number;
  written: number;
  working: boolean;
  quiet_turns: number;
  note: string | null;
}

export interface BoardroomTagging {
  /** Items missing a value on at least one of the room's axes. */
  waiting: number;
  /** Looked at for every axis: `complete` plus `skipped`. */
  sorted: number;
  /** Looked at, with a tag on every axis. */
  complete?: number;
  /** Looked at, with at least one axis nothing on the board answered. */
  skipped?: number;
  /** Tags the current run has placed. */
  placed: number;
  working: boolean;
  axes: string[];
  /** Why it is parked, when it is: out of credits, no axes yet, a failure. */
  note: string | null;
  /** Your daily budget for sorting after a dig ends (per room), and what
   *  this room has spent of it in the last day. */
  after_budget?: { cap: number; spent: number; used_up: boolean } | null;
  /** Items the content sorter has still to read ("what each thing is about"). */
  content_waiting?: number;
  /** Both queues by item kind, for the rail: {"FEATURE": 1065, ...}. */
  waiting_kinds?: Record<string, number>;
  content_waiting_kinds?: Record<string, number>;
  /** A story room: its tables need no tag groups, so nothing is sorted. */
  not_used?: boolean;
}

/** How Appeal reads are getting on in a room (backend boardroom/appeal.status). */
export interface BoardroomAppealStatus {
  companies: number;
  reading: number;
  /** Due: never read, or a page came in since. Read when the next dig ends. */
  waiting: number;
  read: number;
  no_pages: number;
}

// ---- Reports: derived views over the whole board, computed on read ----
export type BoardroomReportKey =
  // Where the market talks, off the posts the room's searches brought back.
  | 'audience'
  // Every dig the room has run, grouped by what it was aimed at.
  | 'recon_runs'
  // Every feature, weighted by how many products ship it.
  | 'feature_cloud'
  | 'who_ranks'
  // The room's keyword pile, searched a batch per dig, and who holds it.
  | 'search_market'
  // One company's searches and who holds them; a card keyed by the company.
  | 'brand'
  // Who goes after whom, across every company whose searches were looked at.
  | 'brand_grid'
  // How each company asks for an email, off the pages already fetched for it.
  | 'email_capture'
  // How to reach each company, off the pages already fetched for it.
  | 'contacts'
  // How each company pitches itself, read by a model from its pages.
  | 'appeal'
  // WHAT THE ROOM TURNED UP THAT IS NOT THE SUBJECT, and where you sort it. A
  // room hunting car washes collects janitorial suppliers and window washers —
  // real finds, wrong question — and this is where they live instead of
  // quietly filling the roster.
  | 'related'
  // ONE THING IN FULL, as a card. Needs `subject` to say which — two profile
  // cards share this kind, the way two `list` cards do.
  | 'profile'
  // WHAT A PERSON CONCLUDED. The board can hold findings and tables; this is
  // the only way it can hold what somebody made of them.
  | 'note'
  // A TABLE THE ROOM WAS ASKED FOR. Every other key here is a shape somebody
  // anticipated; this one's query is on the card, so it needs `subject` to say
  // WHICH card. Two `list` cards are two different questions.
  | 'list'
  // SEVERAL BLOCKS ON ONE PAGE — tables and counts — keyed by `subject` the
  // same way a `list` card is.
  | 'report'
  | 'landscape'
  | 'hunches'
  | 'competitors'
  | 'feature_matrix'
  | 'price_map'
  | 'pain_gap'
  | 'demand'
  | 'opportunity_brief'
  // A story room's tables (`boardroom/story.py`).
  | 'story'
  | 'people'
  | 'angles';

export interface BoardroomMatrixPlan {
  id: number;
  title: string;
  label: string;
  amount: number | null;
  currency: string | null;
  period: string | null;
  unit: string | null;
  observed_at: string | null;
}
export interface BoardroomMatrixProduct {
  /** Phrases of its own that nobody has fitted to a column. An empty cell for
   *  such a product is not a finding — its words may say the thing and simply
   *  never have been read against the sheet. */
  unfitted?: number;
  id: number;
  title: string;
  company: { id: number; title: string } | null;
  verdict: BoardroomVerdictValue | null;
  read: boolean;
  plans: BoardroomMatrixPlan[];
  cheapest_plan_id: number | null;
  ships: number;
}
export interface BoardroomMatrixCell {
  state: 'ships' | 'gated' | 'unread' | 'none';
  plan_id?: number;
  plan?: string;
}
export interface BoardroomMatrixRow {
  feature_id: number;
  title: string;
  verdict: BoardroomVerdictValue | null;
  ships: number;
  field_size: number;
  table_stakes: boolean;
  gated: number;
  cells: Record<string, BoardroomMatrixCell>;
}
export interface BoardroomMatrixGroup {
  key: string;
  solution_type_id: number | null;
  label: string;
  products: BoardroomMatrixProduct[];
  rows: BoardroomMatrixRow[];
  dig: BoardroomTableDig | null;
  /** The type this one is a kind of, when it is a sub-genre. FLAT, not
   *  nested: the client rebuilds groups from this array to regroup by
   *  company, and an indent is all the tree a reader needs. */
  parent_key?: string | null;
  depth?: number;
  /** Broadest first: "Competitive Intelligence Platforms › Report Platforms". */
  path?: { id: number; title: string }[];
  /** The comparison for THIS market's products, scored against them rather
   *  than against every product in the room. */
  specs?: BoardroomSpecRow[];
  /** Products whose most specific filing is this heading itself — nobody has
   *  put them anywhere narrower. */
  unsorted?: number;
  /** When this type was last sorted into kinds by a pass that can be taken
   *  back. A sorting is a judgement, and the only way to judge one is to see
   *  it — which means being able to put it back and ask again. */
  split_at?: string | null;
  /** The share of this table's products that its best-covered column reaches.
   *  Low means they are not being compared on anything, which is what several
   *  markets sharing a shelf looks like. */
  cohesion?: number;
}
/** A hole in the board the report noticed: what is missing, who fills it,
 *  and the directive a deeper dig would carry. */
export interface BoardroomReportMissing {
  item_id: number | null;
  title: string | null;
  what: 'features' | 'pricing' | 'players' | string;
  who: string;
  hint: string;
  directive: string;
}
/** One directive for every hole in a table: send a persona at the table, not a row. */
export interface BoardroomTableDig {
  who: string;
  /** Archetypes that ride along (the cartographer, to sort what lands). */
  also?: string[];
  count: number;
  label: string;
  hint: string;
  directive: string;
}
/** One row of the competitor roster: a company, what it sells, and what the
 *  room does not know about it yet. */
export interface BoardroomCompetitorRow {
  id: number;
  kind: string;
  title: string;
  body: string | null;
  url: string | null;
  verdict: BoardroomVerdictValue | null;
  /** Has anyone read its pages? A roster of unread names is a list of guesses. */
  read: boolean;
  tags: { dimension: string; value: string }[];
  created_by_persona_id: number | null;
  receipts: { id: number; title: string; kind: string }[];
  products: {
    id: number;
    title: string;
    verdict: BoardroomVerdictValue | null;
    read: boolean;
    solution_type: { id: number; title: string } | null;
  }[];
  features: number;
  /** When the room first wrote it down. Sorting on it is how an insertion
   *  reads as an insertion rather than as another glowing row. */
  created_at: string | null;
  /** Cheapest plan across everything it sells, already labelled. */
  cheapest_label: string | null;
  /** The highest price seen across everything it sells, already labelled. */
  top_label?: string | null;
  plans: number;
  pains: { id: number; title: string; how: string }[];
  critiques: number;
  /** What is missing on this row: "site", "company_page", "products"… Shown
   *  as words; the dig for one company lives on the company's own page. */
  gaps: string[];
}

/** A product on the board that no company sells. The table reaches a product
 *  through its maker, so these used to be missing from every row and every
 *  count — a dig that found sixteen of them looked like it had done nothing. */
export interface BoardroomLooseProduct {
  id: number;
  title: string;
  verdict: BoardroomVerdictValue | null;
  read: boolean;
  solution_type: { id: number; title: string } | null;
}

/** Something on the board hanging where no table can reach it: products under
 *  no company, features under no product. Counts, board-wide. */
export interface BoardroomShapeWarning {
  kind: string;
  what: string;
  count: number;
  of: number;
  words: string;
}

export interface BoardroomCompetitors {
  kind: 'competitors';
  scoped: boolean;
  truncated: boolean;
  dig: BoardroomTableDig | null;
  rows: BoardroomCompetitorRow[];
  unattached: BoardroomLooseProduct[];
  shape: BoardroomShapeWarning[];
  missing: BoardroomReportMissing[];
  totals: {
    companies: number;
    products: number;
    unattached: number;
    read: number;
    checked: number;
    /** How many companies carry a tag on each of the room's axes. */
    axes?: { dimension: string; tagged: number }[];
  };
}

/** One COLUMN of the spec sheet, across every product: the comparable view.
 *  A feature title cannot be a row when the value is inside the name — "5g
 *  creatine" and "3g Creapure creatine" are one attribute at two values — so
 *  the row is the attribute and the cells hold the values. */
export interface BoardroomSpecRow {
  attribute: string;
  kind: 'quantity' | 'choice' | 'boolean' | 'text';
  unit: string;
  meaning: string;
  /** False = recorded, but the cartographer has not blessed it as a column. */
  on_sheet: boolean;
  filled: number;
  field_size: number;
  /** product id (as a string) → the values recorded for it. */
  cells: Record<string, string[]>;
  /** The feature phrases folded into this column, by product id. The cell
   *  says "true"; only this says that `API access`, `API delivery` and
   *  `low-latency REST API` were read as one thing. */
  phrases?: Record<string, string[]>;
}

export interface BoardroomFeatureMatrix {
  kind: 'feature_matrix';
  scoped: boolean;
  truncated: boolean;
  dig: BoardroomTableDig | null;
  groups: BoardroomMatrixGroup[];
  /** Rows of the spec sheet. Empty until the room has named its columns. */
  specs?: BoardroomSpecRow[];
  /** How many features are still recorded as a phrase, not as columns. */
  unmapped?: number;
  missing: BoardroomReportMissing[];
  totals: { products: number; features: number; companies: number };
  /** Phrases the CURRENT columns have not been put to — never read at all,
   *  or read against a sheet that has since changed. What the fit covers. */
  unfitted?: number;
  /** Read against these columns and filling none of them. The fit cannot add
   *  a column, so this is the only shape its disagreement can take. */
  fits_none?: number;
}

/** What the whole-set pass would work on: how many loose phrases, how many
 *  fitting calls, and how many columns the sheet already carries. */
export interface BoardroomSheetPile {
  /** Recorded but never read against a column — the cheap fit's job. */
  phrases: number;
  batches: number;
  columns: number;
  /** Every live phrase, filed or not — what naming the columns reads, and
   *  what a re-read of the whole room would cover. */
  population: number;
  refit_batches: number;
}

export interface BoardroomPricePlan extends BoardroomMatrixPlan {
  monthly: number | null;
}
export interface BoardroomPriceProduct {
  id: number;
  title: string;
  company: { id: number; title: string } | null;
  verdict: BoardroomVerdictValue | null;
  plans: BoardroomPricePlan[];
  from_monthly: number | null;
  to_monthly: number | null;
  cheapest_label: string | null;
  /** Somebody looked and there is no published price — you have to ring them.
   *  A FINDING, not a gap, and a different fact from "nobody has checked". */
  pricing_settled: boolean;
}
export interface BoardroomPriceGroup {
  key: string;
  solution_type_id: number | null;
  label: string;
  unit_mix: string[];
  products: BoardroomPriceProduct[];
}
export interface BoardroomPriceMap {
  kind: 'price_map';
  scoped: boolean;
  truncated: boolean;
  dig: BoardroomTableDig | null;
  groups: BoardroomPriceGroup[];
  missing: BoardroomReportMissing[];
  totals: {
    products: number;
    priced: number;
    plans: number;
    /** Checked, and they publish nothing. Not waiting on anyone. */
    unpublished: number;
  };
}

/** A critique, readable in place. */
export interface BoardroomCritiqueRow {
  id: number;
  title: string;
  body: string;
  severity: number | null;
  by: number | null;
  verdict: BoardroomVerdictValue | null;
}
export interface BoardroomPainAddresser {
  product_id: number;
  title: string;
  via_feature_id: number | null;
  via_feature: string | null;
}
/** Where a complaint was said: the page, and the voice it counts as. */
export interface BoardroomPainSource {
  url: string;
  title: string | null;
  quote?: string | null;
  quote_found_in_source?: boolean | null;
  voice: string;
  venue: boolean;
  /** On one of the room's own companies' sites: shown, not counted as a voice. */
  seller?: boolean;
  /** When it was said ("2024-03"), or null when no date is known. */
  said_at?: string | null;
  /** How the date is known: the seat read it off the page ("said"), or it is
   *  the date the page itself declares ("page date"). */
  said_from?: 'said' | 'page date' | null;
}
export interface BoardroomPainRow {
  pain_id: number;
  title: string;
  /** The company it is a complaint about, for grouping; null when it is about
   *  no one player. */
  about?: { id: number; title: string } | null;
  verdict: BoardroomVerdictValue | null;
  evidence: number;
  sources: BoardroomPainSource[];
  /** People, not rows: two quotes off one thread are one voice. */
  independent_sources: number;
  critiques: number;
  critique_rows: BoardroomCritiqueRow[];
  addressed_by: BoardroomPainAddresser[];
  ideas: { id: number; title: string }[];
  gap: boolean;
  /** A gap with enough of the field read (or settled by a seat) to trust
   *  "nobody addresses it". A gap without it means "not looked at enough". */
  gap_confirmed?: boolean;
  /** How many of the products that could answer it had their pages read. */
  field_read?: { read: number; of: number };
  unchecked: boolean;
  /** The first and last date any source says it was said; null when none. */
  first_heard?: string | null;
  last_heard?: string | null;
  /** Independent voices that said it in the last two years. */
  recent_voices?: number;
  /** Sources with no date. */
  undated?: number;
}
/** One term Google was asked about: Ads numbers, the Trends line, what it is about. */
export interface BoardroomDemandTerm {
  keyword: string;
  volume: number | null;
  /** Google Ads' recent-vs-older growth, as a fraction. */
  growth: number | null;
  competition: string | null;
  top_bid: number | null;
  /** Asked for (a seed) rather than offered by Google as related. */
  seeded: boolean;
  lookup_id: number;
  about: { id: number; title: string; kind: BoardroomItemKind }[];
  trend: {
    lookup_id: number;
    interest_recent: number | null;
    interest_a_year_ago: number | null;
    change: number | null;
    rising: string[];
    series: { t: string; v: number | null }[];
    explore_url: string | null;
  } | null;
  /** EVIDENCE items on the board that cite this term. */
  evidence: number;
  /** Searched by the room, and none of the sites it is about came up. */
  other_meaning?: boolean;
  /** ...and no company in the room came up either: left out of the total. */
  off_market?: boolean;
}
export interface BoardroomDemandSubject {
  id: number;
  title: string;
  kind: BoardroomItemKind;
  verdict: BoardroomVerdictValue | null;
  terms: string[];
  /** Seeded terms' searches a month, added up. */
  volume: number;
  best_growth: number | null;
  trend_change: number | null;
}
export interface BoardroomDemand {
  kind: 'demand';
  scoped: boolean;
  truncated: boolean;
  dig: BoardroomTableDig | null;
  terms: BoardroomDemandTerm[];
  subjects: BoardroomDemandSubject[];
  missing: BoardroomReportMissing[];
  totals: { terms: number; seeded: number; with_trend: number; subjects: number; unasked: number; volume: number };
}
/** One guess the room is checking, and what became of it. Derived on read. */
export interface BoardroomHunchRow {
  hunch_id: number;
  title: string;
  check: 'pains' | 'players' | 'demand' | string;
  searches: string[];
  state: 'open' | 'supported' | 'dead';
  findings: { id: number; kind: BoardroomItemKind; title: string }[];
  /** Independent people behind the complaints found (pains hunches only). */
  voices: number;
  ideas: { id: number; title: string }[];
}
export interface BoardroomHunches {
  kind: 'hunches';
  scoped: boolean;
  truncated: boolean;
  dig: BoardroomTableDig | null;
  rows: BoardroomHunchRow[];
  missing: BoardroomReportMissing[];
  totals: { hunches: number; open: number; supported: number; dead: number };
}
export interface BoardroomPainGap {
  kind: 'pain_gap';
  scoped: boolean;
  truncated: boolean;
  dig: BoardroomTableDig | null;
  rows: BoardroomPainRow[];
  missing: BoardroomReportMissing[];
  totals: { pains: number; gaps: number; confirmed_gaps?: number; addressed: number };
}

export interface BoardroomIdeaBrief {
  id: number;
  title: string;
  verdict: BoardroomVerdictValue | null;
  critiques: number;
  critique_rows: BoardroomCritiqueRow[];
  solution_type: { id: number; title: string } | null;
  pains: {
    id: number;
    title: string;
    verdict: BoardroomVerdictValue | null;
    evidence: number;
    addressed_by: string[];
    /** Titles of the idea's own planned features that address this pain. */
    our_features: string[];
  }[];
  demand: { evidence_id: number; title: string; volume: number | null; growth: number | null; verdict: BoardroomVerdictValue | null }[];
  has_demand_lookup: boolean;
  competitors: {
    product_id: number;
    title: string;
    company: { id: number; title: string } | null;
    cheapest: { plan: string; label: string } | null;
    verdict: BoardroomVerdictValue | null;
  }[];
  unaddressed_pains: number;
  /** The strategist's positioning: who it is for, the wedge, why they would switch. */
  angle: string | null;
  /** The shared marketing-angle ids it would lead with (backend boardroom/taxonomy.py). */
  marketing_angles?: string[];
  /** Whether it is a thing built and sold, or pages that earn. Decides which
   *  questions it is scored on. */
  shape?: 'product' | 'content' | null;
  /** How good it is, on the same questions every time. `inverted` says which
   *  way is up; `grounded` is whether it cites anything on the board. */
  scores: {
    key: string;
    label: string;
    score: number;
    why: string | null;
    inverted: boolean;
    grounded: boolean;
    receipt_ids: number[];
  }[];
  /** What the idea would need to ship. `standing` is read off the rivals' links:
   *  no rival has it → ours; most do → table_stakes; some → shared. */
  features: BoardroomIdeaFeature[];
  stands_out: {
    /** {id, title} so the claim can be clicked through to the feature itself. */
    only_ours: { id: number; title: string }[];
    /** Pains the idea answers that no rival addresses. */
    gaps_we_fill: { id: number; title: string }[];
    /** Features most rivals ship that the idea has not planned. */
    table_stakes_missing: { id: number; title: string; rivals: number }[];
    rivals: number;
    /** Why now: the head term's numbers, when the analyst has them. */
    demand: { title: string; volume: number | null; growth: number | null } | null;
    /** Where the buyers behind its pains were found talking: hosts, most-cited first. */
    venues: string[];
    /** The idea's tags on the room's axes, and how many rivals share each cell. */
    placement: { dimension: string; value: string; rivals_sharing: number }[];
  };
  /** The one plan it would sell at, or null. */
  price: BoardroomMatrixPlan | null;
  price_position: {
    band: { from: number; to: number } | null;
    position: 'undercuts' | 'within' | 'premium' | null;
    unit_matches: boolean | null;
    field_units: string[];
    priced_rivals: number;
  };
  /** The claims the angle rests on, as QUESTIONs, and whether anything answers each. */
  assumptions: { id: number; title: string; answered: boolean; answer: { id: number; title: string } | null }[];
  /** The subject this idea became, if someone promoted it. */
  promoted_project_id?: number | null;
}
/** What promoting an idea made. `note` is set when it had already been made. */
export interface BoardroomPromoteResult {
  project_id: number;
  topic_id: number | null;
  keywords_added: number;
  /** A content play's subject is a plan the page builder executes; any other
   *  idea's is a site ABOUT something that does not exist yet. */
  builds_pages: boolean;
  /** Whether research/building actually started. Paused unless asked. */
  is_active: boolean;
  note: string | null;
}
export type BoardroomFeatureStanding = 'ours' | 'shared' | 'table_stakes';
export interface BoardroomIdeaFeature {
  id: number;
  title: string;
  body: string;
  pains: { id: number; title: string }[];
  rivals: string[];
  standing: BoardroomFeatureStanding;
}
export interface BoardroomOpportunityBrief {
  kind: 'opportunity_brief';
  scoped: boolean;
  truncated: boolean;
  dig: BoardroomTableDig | null;
  ideas: BoardroomIdeaBrief[];
  missing: BoardroomReportMissing[];
  totals: { ideas: number; with_pains: number; with_demand: number; positioned: number; priced: number };
}

export interface BoardroomLandscapePlayer {
  id: number;
  title: string;
  kind: BoardroomItemKind;
  /** The column value this player falls in, or null when untagged on it. */
  b: string | null;
  verdict: BoardroomVerdictValue | null;
}
export interface BoardroomLandscapeRow {
  value: string;
  total: number;
  confident: number;
  cells: Record<string, number>;
  ids: number[];
  players: BoardroomLandscapePlayer[];
}
/** Players crossed by two of the room's own axes; the axes come from the frame. */
export interface BoardroomLandscape {
  kind: 'landscape';
  scoped: boolean;
  truncated: boolean;
  dig: BoardroomTableDig | null;
  axes: string[];
  /** The one axis every section is crossed BY. There is no row axis: each
   *  section puts its own axis down the side, so which that is, is which
   *  section you are reading. */
  b: string | null;
  /** ONE ENTRY PER AXIS the frame offers, each carrying its own rows, cols,
   *  gaps and unplaced count. */
  sections: BoardroomLandscapeSection[];
  missing: BoardroomReportMissing[];
  totals: { players: number; placed: number };
}
export interface BoardroomLandscapeSection {
  a: string;
  b: string | null;
  rows: BoardroomLandscapeRow[];
  cols: string[];
  gaps: { a: string; b: string }[];
  unplaced: number;
}
/** One place, channel or page where the market talks (`boardroom/audience.py`). */
export interface BoardroomAudienceSpot {
  url: string;
  name: string;
  /** "Facebook group", "Subreddit", "Forum", "YouTube channel", "Review site" … */
  kind: string;
  /** How to take part there: "Join to post", "Comment and post"; "" to watch. */
  how: string;
  posts: number;
  /** Of those, posted in the last 90 days. */
  recent: number;
  replies: number;
  /** Complaints on the board quoted from it. */
  quoted: number;
  /** The board's companies and products its posts name most. */
  names: string[];
  last_seen: string | null;
  /** The post most replied to: the one to open first. */
  best: { url: string; text: string; replies: number } | null;
  privacy: string;
  members: number | null;
  activity: string;
  /** Where it stands with the room: "the room searches inside it", "waiting for you to join", "you're in it". */
  status: string;
  /** One of the room's own companies' channels or pages. */
  vendor: boolean;
}

export interface BoardroomAudiencePerson {
  name: string;
  site: string;
  posts: number;
  recent: number;
  names: string[];
  places: string[];
  latest: { url: string; text: string; replies: number } | null;
  vendor: boolean;
}

/** Where this market talks, ranked for joining in. */
export interface BoardroomAudience {
  kind: 'audience';
  take_part: BoardroomAudienceSpot[];
  listen: BoardroomAudienceSpot[];
  people: BoardroomAudiencePerson[];
  totals: { take_part: number; listen: number; people: number };
}

export type BoardroomReport =
  | BoardroomAudience
  | BoardroomFeatureCloud
  | BoardroomReconRuns
  | BoardroomWhoRanks
  | BoardroomSearchMarket
  | BoardroomBrandGrid
  | BoardroomEmailCapture
  | BoardroomContacts
  | BoardroomAppeal
  | BoardroomLandscape
  | BoardroomHunches
  | BoardroomCompetitors
  | BoardroomFeatureMatrix
  | BoardroomPriceMap
  | BoardroomPainGap
  | BoardroomDemand
  | BoardroomOpportunityBrief
  | BoardroomRelated
  | BoardroomStory
  | BoardroomPeople
  | BoardroomAngles;

/** Why a story is news now: its trend, and the newest thing that happened. */
export interface BoardroomWhyNow {
  trend: {
    keyword: string;
    change_pct: number | null;
    interest_recent: number | null;
    rising: string[];
    series: { time: number | string; label?: string; value: number }[];
  } | null;
  latest: { id: number; title: string; happened_at: string } | null;
}

export interface BoardroomStoryEvent {
  id: number;
  title: string;
  body: string;
  /** "2019-03", or null when no date is known. */
  happened_at: string | null;
  verdict: BoardroomVerdictValue | null;
  people: { id: number; title: string }[];
  source: { url: string; quote: string | null } | null;
}

/** STORY SO FAR: every event, oldest first, and why it is news now. */
export interface BoardroomStory {
  kind: 'story';
  rows: BoardroomStoryEvent[];
  why_now: BoardroomWhyNow;
  missing: BoardroomReportMissing[];
  totals: { events: number; dated: number; checked: number };
}

export interface BoardroomOnRecord {
  id: number;
  quote: string;
  said_at: string | null;
  url: string | null;
}

/** WHO: the people in the story and what each said on the record. */
export interface BoardroomPeople {
  kind: 'people';
  rows: {
    id: number;
    title: string;
    role: string;
    url: string | null;
    events: number;
    quotes: BoardroomOnRecord[];
    last_said: string | null;
  }[];
  missing: BoardroomReportMissing[];
  totals: { people: number; on_record: number };
}

export interface BoardroomAngle {
  id: number;
  title: string;
  body: string;
  searches: string[];
  /** Monthly searches across them. */
  volume: number;
  why: string;
  verdict: BoardroomVerdictValue | null;
}

/** ANGLES TO WRITE: what people search that nobody answers well. */
export interface BoardroomAngles {
  kind: 'angles';
  rows: BoardroomAngle[];
  missing: BoardroomReportMissing[];
  totals: { angles: number; volume: number };
}

/** The feature cloud: every feature the room has recorded, weighted by how
 *  many of the products in scope ship it. */
export interface BoardroomFeatureCloudWord {
  id: number;
  title: string;
  products: number;
  share: number;
  verdict: BoardroomVerdictValue | null;
  /** A few of the products that ship it. */
  examples: string[];
}
export interface BoardroomFeatureCloud {
  kind: 'feature_cloud';
  rows: BoardroomFeatureCloudWord[];
  totals: { products: number; features: number; shown: number; with_features?: number };
  missing: BoardroomReportMissing[];
}

/** One dig, as Recon Runs lists it. Tokens are billed tokens, the budget's own
 *  measure; `added` is what was found while it ran, by kind. */
export interface BoardroomReconRun {
  id: number;
  at: string | null;
  ended_at: string | null;
  intent: string;
  status: string;
  ended: string | null;
  tokens: number;
  budget: number;
  turns: number;
  lookups: number;
  directive: string | null;
  added: Record<string, number>;
  /** A lens dig's finds the sorter has since placed outside its lens. */
  outside_lens?: number;
  outside_examples?: { id: number; title: string; kind: string }[];
}

/** One feature and its near-copies, for the feature cloud's duplicates panel. */
export interface BoardroomFeatureTwinGroup {
  keep: { id: number; title: string; products: number };
  members: {
    id: number;
    title: string;
    products: number;
    score: number;
    /** Close enough to start ticked. */
    likely: boolean;
    /** How far apart the two names mean, when both have an embedding and
     *  they are close; absent or null otherwise. */
    distance?: number | null;
    /** Found by shared words, by meaning, or both. */
    match?: 'words' | 'meaning' | 'both';
  }[];
}
export interface BoardroomFeatureTwins {
  groups: BoardroomFeatureTwinGroup[];
  /** `embedded`: features whose meaning could be compared; the rest were
   *  grouped by their words alone. */
  totals: { groups: number; copies: number; features: number; embedded?: number };
}

/** Everything one dig found, grouped by kind, biggest group first: a Recon
 *  Runs row opened (`recon_runs.findings`). */
export interface BoardroomRunFindings {
  run_id: number;
  total: number;
  /** More than the list holds; the counts still say how many. */
  truncated: boolean;
  groups: {
    kind: BoardroomItemKind;
    items: { id: number; title: string; status: string }[];
  }[];
}

/** Everything a room's digs were aimed at: an item, a lens, a table, or the
 *  whole room - with how often, what it cost, and what it found. */
export interface BoardroomReconRunsRow {
  key: string;
  kind: 'item' | 'lens' | 'table' | 'room';
  label: string;
  item_id?: number;
  item_kind?: BoardroomItemKind | null;
  tags?: string[];
  tables?: string[];
  count: number;
  tokens: number;
  turns: number;
  last_at: string | null;
  last_ended: string | null;
  running: boolean;
  added: Record<string, number>;
  runs: BoardroomReconRun[];
  /** For a question: its current answer. */
  answer?: { stance: string; strength: string } | null;
}

export interface BoardroomReconRuns {
  kind: 'recon_runs';
  rows: BoardroomReconRunsRow[];
  totals: { runs: number; tokens: number; aims: number };
  missing: BoardroomReportMissing[];
}

/** What kind of market the room is, in its own words, and the axes it sorts on. */
export interface BoardroomFrameAxis {
  dimension: string;
  meaning: string;
}
export interface BoardroomFrameSpec {
  attribute: string;
  kind: string;
  unit: string;
  meaning: string;
  /** On a PROPOSAL only: the columns this one takes the place of, values and
   *  all. Cleared once the rename has been carried out. */
  replaces?: string[];
}
export interface BoardroomFrame {
  id: number;
  vocab: { market: string; player: string; product: string; feature: string; pricing: string };
  axes: BoardroomFrameAxis[];
  /** The spec sheet: the columns this market is compared on. The axes sort
   *  ITEMS; this says what the feature table compares. */
  specs: BoardroomFrameSpec[];
  reason: string | null;
  written_by: string;
  focus_id: number | null;
  run_id: number | null;
  copied_from_id: number | null;
  proposed: boolean;
  dismissed: boolean;
  unchanged: boolean;
  since: string | null;
  /** Who made it official — a second persona ("skeptic") or "human" — and why. On a rejected proposal, who threw it out. */
  ratified_by: string | null;
  ratified_at: string | null;
  ratified_reason: string | null;
  /** On a proposal: only a person can accept it (it removes something). Null when not a proposal. */
  needs_human: boolean | null;
}

// ---- Profile: everything the board knows about one company / product / type ----
export interface BoardroomProfileHead {
  id: number;
  kind: BoardroomItemKind;
  title: string;
  body: string | null;
  url: string | null;
  /** Set when this company is already under a standing watch. Optional because it is an
   *  additive field: an older cached response reads as undefined, which shows the button. */
  watch_subject_id?: number | null;
  verdict: BoardroomVerdictValue | null;
  read: boolean;
  tags: { dimension: string; value: string }[];
  /** Null = a person added it by hand, which is its own source. */
  created_by_persona_id: number | null;
  receipts: BoardroomReceipt[];
  /** Facts a seat recorded about it, newest first, each with its source.
   *  Optional: older cached responses have none. */
  claims?: BoardroomClaim[];
  /** Its other pages (reviews, profiles, docs, news), labelled by the seat. */
  links?: { label: string; url: string }[];
}
/** One fact about a company or product (`record_claims`), with where it was read. */
export interface BoardroomClaim {
  name: string;
  value: string;
  unit: string;
  as_of: string;
  said_by: string | null;
  at: string | null;
  source: { id: number; title: string; url: string | null } | null;
  /** The figure this one replaced, when it changed. */
  previous: { value: string; as_of: string } | null;
}
/** One thing a person said, for or against, in their own words. */
export interface BoardroomVoice {
  id: number;
  title: string;
  quote: string;
  url: string | null;
  /** True: the quote is on the page. False: it is not. Null: nothing to check. */
  quote_verified: boolean | null;
  verdict: BoardroomVerdictValue | null;
  /** The company or product it is about. */
  about: { id: number; title: string };
  /** Everything the same quote is about, when it was filed under several. */
  about_all?: { id: number; title: string }[];
}
export interface BoardroomVoices {
  praise: BoardroomVoice[];
  complaints: BoardroomVoice[];
}
/** A lookup or evidence item an item rests on, named. */
export interface BoardroomReceipt {
  id: number;
  kind: BoardroomItemKind;
  title: string;
  status: string;
  url: string | null;
  keyword: string | null;
  seeds: string[] | null;
}
export interface BoardroomProfileFeature {
  id: number;
  title: string;
  verdict: BoardroomVerdictValue | null;
  /** The cheapest plan that includes it, when plans list features. */
  plan: string | null;
}
export interface BoardroomProfilePain {
  id: number;
  title: string;
  verdict: BoardroomVerdictValue | null;
  evidence: number;
  how: 'addresses' | 'about it' | string;
}
/** Who makes a product, as the board's best-supported claim and how sure it is. */
export interface BoardroomMaker {
  id: number;
  title: string;
  /** confirmed: its own site lists it, or two sites agree. one source: one
   *  outside site. unconfirmed: filed with no recorded source. Optional: an
   *  older cached response has none. */
  level?: 'confirmed' | 'one source' | 'unconfirmed';
  sites?: string[];
  own_site?: boolean;
  /** Other companies also claimed to make it, less well supported. */
  also?: { id: number; title: string; level: string }[];
}
export interface BoardroomProfileProduct extends BoardroomProfileHead {
  company: BoardroomMaker | null;
  solution_type: { id: number; title: string } | null;
  features: BoardroomProfileFeature[];
  plans: BoardroomPricePlan[];
  cheapest_label: string | null;
  pains: BoardroomProfilePain[];
  critiques: number;
  critique_rows: BoardroomCritiqueRow[];
}
/** A picture a company's or product's own site marks for sharing (its
 *  og:image), read off a page the room read. `page` is where it was. */
export interface BoardroomSiteImage {
  url: string;
  page: string | null;
  read_id: number | null;
}

/** A video that shows a company or product, checked against the search it
 *  was found in. Played on its page; nothing loads until it is clicked. */
export interface BoardroomVideo {
  id: number;
  title: string;
  provider: 'youtube' | string;
  video_id: string;
  url: string | null;
  kind: 'demo' | 'walkthrough' | 'tour' | 'review' | 'other' | string;
  snippet: string;
  /** The channel it is on, as YouTube's search page showed it. */
  channel?: string | null;
  /** The age YouTube showed ("7mo ago"), and the rough day that makes it. */
  age?: string | null;
  published?: string | null;
}
export interface BoardroomCompanyProfile {
  /** Videos that show it - a demo, a tour, a review - newest first. */
  videos?: BoardroomVideo[];
  /** The pictures its own site marks for sharing, off pages the room read. */
  images?: BoardroomSiteImage[];
  /** The terms on the room's search market this ranks for, best first. */
  rankings?: BoardroomRanking[];
  /** Who is going after its searches, in a few numbers. Null for a non-company. */
  brand?: BoardroomBrandSummary | null;
  /** What the watch has found, once one exists. Mutually exclusive with `watch_preview`. */
  watch_live?: WatchLive | null;
  /** What a watch WOULD already find, from pages the room has crawled — so the button can say
   *  "3 open roles including an SEO Lead" rather than promising a future nobody can see. Only the
   *  server populates it, and only for a company or a product with a url. */
  watch_preview?: WatchPreview | null;
  kind: 'profile';
  item: BoardroomProfileHead;
  products: BoardroomProfileProduct[];
  /** Terms Google was asked about for this item, joined with Trends. */
  demand: BoardroomDemandTerm[];
  pains: BoardroomProfilePain[];
  /** What people said for and against it and its products. */
  voices?: BoardroomVoices;
  /** Products filed under it that another company is better supported as
   *  making — kept apart, so a rival's product never reads as its own. */
  also_claimed?: { id: number; title: string; maker: BoardroomMaker | null }[];
  missing: BoardroomReportMissing[];
  /** Everything this item lacks, as one directive — the dig at the top of
   *  the page. Null when it lacks nothing. */
  dig: BoardroomTableDig | null;
}
export interface BoardroomProductProfile {
  /** Videos that show it - a demo, a tour, a review - newest first. */
  videos?: BoardroomVideo[];
  /** The pictures its own site marks for sharing, off pages the room read. */
  images?: BoardroomSiteImage[];
  /** The terms on the room's search market this ranks for, best first. */
  rankings?: BoardroomRanking[];
  /** What the watch has found, once one exists. Mutually exclusive with `watch_preview`. */
  watch_live?: WatchLive | null;
  /** What a watch WOULD already find, from pages the room has crawled — so the button can say
   *  "3 open roles including an SEO Lead" rather than promising a future nobody can see. Only the
   *  server populates it, and only for a company or a product with a url. */
  watch_preview?: WatchPreview | null;
  kind: 'profile';
  item: BoardroomProfileProduct;
  demand: BoardroomDemandTerm[];
  siblings: { id: number; title: string; verdict: BoardroomVerdictValue | null }[];
  /** What people said for and against it. */
  voices?: BoardroomVoices;
  missing: BoardroomReportMissing[];
  /** Everything this item lacks, as one directive — the dig at the top of
   *  the page. Null when it lacks nothing. */
  dig: BoardroomTableDig | null;
}
export interface BoardroomTypeProfile {
  /** What the watch has found, once one exists. Mutually exclusive with `watch_preview`. */
  watch_live?: WatchLive | null;
  /** What a watch WOULD already find, from pages the room has crawled — so the button can say
   *  "3 open roles including an SEO Lead" rather than promising a future nobody can see. Only the
   *  server populates it, and only for a company or a product with a url. */
  watch_preview?: WatchPreview | null;
  kind: 'profile';
  item: BoardroomProfileHead;
  products: BoardroomProfileProduct[];
  /** Terms Google was asked about for this item, joined with Trends. */
  demand: BoardroomDemandTerm[];
  ideas: { id: number; title: string; verdict: BoardroomVerdictValue | null }[];
  totals: { products: number; features: number; priced: number };
  missing: BoardroomReportMissing[];
  /** Everything this item lacks, as one directive — the dig at the top of
   *  the page. Null when it lacks nothing. */
  dig: BoardroomTableDig | null;
}
/** One link to or from an item, on its page. */
export interface BoardroomItemLinkRow {
  link: string;
  /** "out": from this item to the other. "in": from the other to this one. */
  dir: 'out' | 'in';
  id: number;
  title: string;
  kind: BoardroomItemKind;
  verdict: BoardroomVerdictValue | null;
  /** What a VERDICT row rules. */
  ruling?: string | null;
  at: string | null;
  /** Made during the latest dig aimed at this item. */
  new: boolean;
}
export interface BoardroomOtherProfile {
  /** What the watch has found, once one exists. Mutually exclusive with `watch_preview`. */
  watch_live?: WatchLive | null;
  /** What a watch WOULD already find, from pages the room has crawled — so the button can say
   *  "3 open roles including an SEO Lead" rather than promising a future nobody can see. Only the
   *  server populates it, and only for a company or a product with a url. */
  watch_preview?: WatchPreview | null;
  kind: 'profile';
  item: BoardroomProfileHead;
  demand: BoardroomDemandTerm[];
  children: Record<string, { id: number; title: string; kind: BoardroomItemKind; verdict: BoardroomVerdictValue | null }[]>;
  /** Everything linked to it, both ways, newest first. Added by the server
   *  on the item page; absent on older replies. */
  links?: BoardroomItemLinkRow[];
  /** The latest dig aimed at this item, if there was one. */
  last_dig?: { run_id: number; started: string; ended: string | null; added: number } | null;
  /** Items merged into this one, by their old names, oldest first. */
  merged_in?: BoardroomMergedName[];
  /** A QUESTION's answer, written while a dig is aimed at it; null before one
   *  is. Absent on other kinds of item. */
  answer?: BoardroomAnswer | null;
  /** Its earlier answers that said something different, newest first. */
  answer_versions?: BoardroomAnswer[];
  missing: BoardroomReportMissing[];
  /** Everything this item lacks, as one directive — the dig at the top of
   *  the page. Null when it lacks nothing. */
  dig: BoardroomTableDig | null;
}
/** A product that ships a feature, on the feature's page. */
export interface BoardroomFeatureShipper {
  id: number;
  title: string;
  verdict: BoardroomVerdictValue | null;
  company: { id: number; title: string } | null;
  type: { id: number; title: string } | null;
  /** Its cheapest plan, whatever that plan lists. */
  cheapest: BoardroomPricePlan | null;
  /** The cheapest plan that lists this feature, when one does. */
  plan: BoardroomPricePlan | null;
  /** Whether any of its plans list features at all; if none do, "no plan
   *  lists it" says nothing. */
  plans_list_features: boolean;
}
/** A product of the same kind as the ones that ship a feature, that does not
 *  list it - and why we think so. */
export interface BoardroomFeaturePeer {
  id: number;
  title: string;
  type: { id: number; title: string };
  /** lists_others: its features are recorded and this is not one.
   *  nothing_recorded: read, but no features recorded.
   *  not_read: nobody has read its pages. */
  state: 'lists_others' | 'nothing_recorded' | 'not_read';
}
/** An item merged into another: its old name, and when. */
export interface BoardroomMergedName {
  id: number;
  title: string;
  at: string | null;
}

export interface BoardroomFeatureProfile {
  /** Only a company or a product is watched; always absent here. */
  watch_live?: WatchLive | null;
  watch_preview?: WatchPreview | null;
  kind: 'profile';
  item: BoardroomProfileHead;
  totals: { products: number; shipped_by: number; share: number; peers: number };
  shippers: BoardroomFeatureShipper[];
  peers: BoardroomFeaturePeer[];
  pains: BoardroomProfilePain[];
  /** The room's own ideas that need it. */
  ideas: { id: number; title: string; verdict: BoardroomVerdictValue | null }[];
  /** Findings that rest on it. */
  observations: { id: number; title: string }[];
  /** Found while a seat worked on these, which do not ship it. */
  found_under: { id: number; title: string; kind: BoardroomItemKind }[];
  /** Features with close titles that may be the same thing. */
  near: { id: number; title: string; products: number; score: number }[];
  /** Everything linked to it, both ways, newest first. Added by the server
   *  on the item page; absent on older replies and on board cards. */
  links?: BoardroomItemLinkRow[];
  /** The latest dig aimed at this feature, if there was one. */
  last_dig?: { run_id: number; started: string; ended: string | null; added: number } | null;
  /** Features merged into this one, by their old names, oldest first. */
  merged_in?: BoardroomMergedName[];
  /** Terms Google was asked about for this item. */
  demand: BoardroomDemandTerm[];
  missing: BoardroomReportMissing[];
  dig: BoardroomTableDig | null;
}
export type BoardroomProfile =
  | BoardroomCompanyProfile
  | BoardroomProductProfile
  | BoardroomTypeProfile
  | BoardroomFeatureProfile
  | BoardroomOtherProfile;

/** Every axis counted under the other two — what each click would show. */
export interface BoardroomFacets {
  /** Buckets, under tag + checked. */
  filters: BoardroomFilterCount[];
  item_counts: Record<string, number>;
  /** Tag values, under bucket + checked + every term: what one more term would leave. */
  tag_facets: Record<string, { value: string; count: number }[]>;
  /** Each term of the lens, its category's values counted under the OTHER terms. */
  term_facets?: { key: string; dimension: string; values: { value: string; count: number }[] }[];
  /** Checked chips, under bucket + tag. */
  verified_counts: Record<'HOLDS' | 'DOES_NOT_HOLD' | 'UNVERIFIABLE' | 'UNCHECKED', number>;
  /** Persona items with no receipt, and the rest, under bucket + tag + checked + author. */
  sourced_counts?: Record<'UNSOURCED' | 'SOURCED', number>;
  /** Authors, under bucket + tag + checked: `"human"` or a persona id as a string. */
  by_counts: Record<string, number>;
  /** Lookups per state, under the author only. */
  lookup_counts: BoardroomLookupCounts;
  lookup_stages?: BoardroomLookupStages;
  tagging?: BoardroomTagging;
  /** Null when the room does not have the Appeal table. */
  appeal?: BoardroomAppealStatus | null;
  /** Room-wide, like the room's own copy: what "Process N results" counts. */
  to_process?: { results: number; turns: number; tokens: number };
}

export interface BoardroomArchetype {
  archetype: string;
  name: string;
  blurb: string;
  tools: string[];
}

export interface BoardroomTranscriptMessage {
  n: number;
  role: string;
  content: string;
  tool_calls: unknown;
  created_at: string | null;
}

export interface BoardroomSummary {
  id: number;
  name: string;
  description: string;
  /** What its owner said would make this room worth the spend. Null on rooms
   *  made before the opening interview existed. */
  success_criteria?: string | null;
  /** What kind of question it asks: market, company, person, topic or
   *  interest (`boardroom/room_kinds.py`). Decides which tables fit. */
  room_kind?: string;
  status: BoardroomStatus;
  tokens_spent: number;
  runs_done: number;
  items_created: number;
  /** What cannot move without you: results nobody has read, frame changes to
   *  accept. A room in this state sits there indefinitely. */
  /** `active_at`: the room's last change, so a long-quiet room stops reading as urgent. */
  waiting?: { results: number; proposals: number; active_at?: string | null };
  /** By item kind, unfiltered. */
  item_counts: Record<string, number>;
  /** The active run, or null. One run per room at a time. */
  run: BoardroomRun | null;
  /** Active personas. Only the list sends it: a room with none cannot run. */
  seats?: number;
  created_at: string;
  updated_at: string;
}

export interface BoardroomDetail extends BoardroomSummary {
  personas: BoardroomPersona[];
  /** The room's latest opening plan, whichever dig wrote it. */
  plan?: BoardroomResearchPlan | null;
  filters: BoardroomFilterCount[];
  tag_facets: Record<string, { value: string; count: number }[]>;
  lookup_counts: BoardroomLookupCounts;
  lookup_stages?: BoardroomLookupStages;
  tagging?: BoardroomTagging;
  /** Of the landed-and-unread, how many belong to a persona you crossed out.
   *  Nobody is left to read those, so a wrap-up says so instead. */
  orphaned_results?: number;
  /** Results a staffed seat will read and has not, the turns that takes and
   *  about what it costs in billed tokens: the status bar's "Process N
   *  results". */
  to_process?: { results: number; turns: number; tokens: number };
  /** Find more or Fill the gaps next, and why. */
  next_dig?: { depth: 'WIDER' | 'DEEPER'; reason: string };
  last_item_id: number | null;
  /** What the room is about now, kept as a history; null = no focus set. */
  focus: BoardroomFocus | null;
  focus_history: BoardroomFocus[];
  frame: BoardroomFrame | null;
  frame_proposals: BoardroomFrame[];
  frame_history: BoardroomFrame[];
  /** The room's answer to its own question, and how it got there. */
  answer?: BoardroomAnswer | null;
  answer_history?: BoardroomAnswer[];
  answering?: BoardroomAnswering;
  /** The worklists in play this round: who has been given what. */
  assignments?: BoardroomAssignment[];
  /** The subjects that read this room live. Chosen from the subject. */
  reader_subjects?: ReaderSubject[];
  /** The lead the room is following (with the leads it sits inside), or
   *  null for the whole room. */
  lead?: BoardroomLead | null;
  /** Every lead the room has, most recently followed first. */
  leads?: BoardroomLead[];
}
/** What a lead narrows by: the tables' own lens. */
export interface BoardroomLeadLens {
  tags: string[];
  verified?: string | null;
  by?: string | null;
  sourced?: string | null;
}
/** A LEAD: part of the room someone chose to follow - a lens and a sentence
 *  saying what they are looking for there. Finds still land on the one board. */
export interface BoardroomLead {
  id: number;
  parent_id: number | null;
  title: string;
  text: string;
  lens: BoardroomLeadLens;
  /** The lens in words: "size: small · confident". */
  label: string;
  created_at: string | null;
  followed_at: string | null;
  /** Outermost first, ending with this one. Only on the lead being followed. */
  path?: { id: number; title: string }[];
}
/** A lesson a room agent left for the agents of its kind after it (see
 *  backend `boardroom/notes.py`). A tool note carries into all the owner's
 *  rooms; a room note stays in its room. */
export interface AgentNote {
  id: number;
  archetype: string;
  about: 'tool' | 'room';
  tool: string | null;
  text: string;
  applies_when: string;
  evidence: { id: number; title: string; kind: string; room_id: number | null; room: string }[];
  written_by: string | null;
  written_in: { id: number | null; name: string };
  created_at: string | null;
  updated_at: string | null;
  dropped_at: string | null;
  dropped_because: string | null;
}

export interface BoardroomLeadBlock {
  lead: BoardroomLead | null;
  leads: BoardroomLead[];
  /** The answer that goes with the lead now followed. */
  answer?: BoardroomAnswer | null;
  answer_history?: BoardroomAnswer[];
  answering?: BoardroomAnswering;
}
export interface BoardroomFocus {
  id: number;
  text: string;
  since: string | null;
}

// --- Recipes --------------------------------------------------------------
//
// How a room was set up, saved so the next one can start there. Shape carries
// (tables, seats, first questions); wording does not — the axes arrive as a
// proposal the interview re-words for the new subject.

export interface BoardroomRecipe {
  id: number;
  name: string;
  description: string;
  /** What kind of subject it suits, in its saver's words. Free text. */
  family: string;
  /** The subject it came from — what makes "running shoes" find it. */
  subject: string;
  extends_id: number | null;
  source_boardroom_id: number | null;
  /** Shipped with the product rather than saved by this account. */
  shipped: boolean;
  used_count: number;
  last_used_at: string | null;
  deliverables: string[];
  axes: string[];
  seats: string[];
  questions: string[];
  success_criteria: string;
  /** How close its subject is to the one asked about; null when unranked. */
  distance: number | null;
  created_at: string | null;
}

// --- Deliverables ---------------------------------------------------------
//
// What a room promises to hand over. `active` is what the SEATS work toward;
// `pinned`/`position`/`size` are where the USER is looking. Two different
// things, and only the first is any agent's business.

/** How full one table is, as a person reads it. Never a percentage: a room
 *  cannot know how many competitors exist, so "12 rows, 4 missing pricing" is
 *  the honest number. */
export interface BoardroomDeliverableFill {
  rows: number;
  holes: number;
  /** What it is short of, deduped, as the room's keys: ["pricing", "company_page"]. */
  what: string[];
  /** The same in plain words, for the screen: ["pricing", "home page"]. */
  words?: string[];
}

/** A card's dashboard numbers and chart, off what its table already worked
 *  out. Mirrors `jcseo/boardroom/deliverables.py:glance_for`. Raw numbers. */
export interface BoardroomGlanceStat {
  label: string;
  value: number;
  /** "12 of 41": the whole this value is a part of. */
  of?: number;
}
export type BoardroomGlanceChart =
  | {
      type: 'bars';
      unit?: string;
      /** Bars measured against this, not the biggest bar (a funnel's steps). */
      max?: number;
      bars: { label: string; value: number }[];
    }
  | { type: 'split'; unit?: string; parts: { label: string; value: number }[] };
export interface BoardroomGlance {
  stats: BoardroomGlanceStat[];
  chart: BoardroomGlanceChart | null;
}

/** What the observer noticed looking over the whole board, and nobody has
 *  marked seen. Mirrors `jcseo/boardroom/looking.py:tray`. A finding is what
 *  the board adds up to; a data problem or duplicate is something on it that
 *  is wrong. `cites` are the items it rests on (a merged one shown as the row
 *  it was merged into). */
export interface BoardroomObservation {
  id: number;
  title: string;
  body: string | null;
  sort: 'finding' | 'data_problem' | 'duplicate';
  rows_counted: number | null;
  created_at: string;
  cites: { id: number; title: string; kind: BoardroomItemKind }[];
  question: { id: number; title: string; status: string } | null;
}

/** What the room spotted in its own tags and links, waiting to be looked at.
 *  Noticed → brought onto the board → pinned. */
export interface BoardroomNotice {
  key: string;
  title: string;
  /** The count behind it, so the judgement stays the reader's. */
  why: string;
  /** What taking it would put on the board. `b` is the axis a landscape is
   *  crossed by. */
  card: { kind: string; subject: string; title: string; lens: BoardroomCardLens | null; b?: string };
  verb: string;
}

/** A noticing set aside as not interesting. `still` when the detectors still
 *  find it, so it can be looked at and taken; otherwise only its key and the
 *  title it had. */
export type BoardroomDismissedNotice =
  | (BoardroomNotice & { still: true })
  | { key: string; title: string; still: false };

/** The cut a card is of — the same lens shape the feed and reports take. */
export interface BoardroomCardLens {
  tags?: string[];
  verified?: string | null;
  by?: string | null;
  sourced?: string | null;
}

export interface BoardroomDeliverable {
  id: number;
  kind: string;
  /** Which one: "" for the plain table, an item id for a dossier, a slug for a
   *  cut. A card is a kind plus a subject. */
  subject: string;
  /** The cut this card is of, or null for the whole table. */
  lens: BoardroomCardLens | null;
  /** A note card's text. */
  body: string | null;
  title: string;
  answers: string;
  group: string;
  entities: string[];
  spec: Record<string, any>;
  active: boolean;
  pinned: boolean;
  position: number;
  size: 'tile' | 'half' | 'full';
  created_by: string;
  status: string;
  fill: BoardroomDeliverableFill;
  /** The table's own first rows — what the card shows at card size. Only for
   *  pinned ones: drawing a table nobody is looking at costs the same as
   *  drawing one they are. */
  preview: { title: string; note: string }[];
  /** Only for cards on the list; null where the table has none. */
  glance?: BoardroomGlance | null;
  /** "What this means" for the room's question. Null for a table that does
   *  not get one (a cut, a list, a profile). */
  summary?: BoardroomTableSummary | null;
  /** A kept page: what it is a page OF - COMPANY, PRODUCT, PAIN... */
  subject_kind?: string | null;
  created_at: string | null;
  updated_at: string | null;
}

/** A table's "What this means" paragraph, the same one the shared report
 *  shows under the table's section. */
export interface BoardroomTableSummary {
  text: string | null;
  written_at: string | null;
  /** The table's numbers have changed since it was written. */
  stale: boolean;
  /** A new one is being written now. */
  writing: boolean;
  /** False while the table has too little in it to say anything about. */
  possible: boolean;
  /** Too little behind the numbers to conclude: what is missing, shown instead. */
  thin?: string | null;
}

/** A table this room could show but has not chosen. */
export interface BoardroomCatalogueEntry {
  kind: string;
  title: string;
  answers: string;
  group: string;
  entities: string[];
  size: 'tile' | 'half' | 'full';
  chosen: boolean;
  /** False for a derived table: nothing a seat could go and fill, so it is
   *  never offered as work. */
  workable: boolean;
  /** Whether it answers this kind of room's question (`room_kinds.fits`). */
  fits?: boolean;
}

export interface BoardroomDeliverables {
  deliverables: BoardroomDeliverable[];
  catalogue: BoardroomCatalogueEntry[];
  /** The lens it was drawn in, as the page asked for it; null for the whole room. */
  lens_key?: string | null;
}

/** One kind of thing the room has found, and whether it is the subject.
 *
 *  Relevance is a property of the KIND, not of each row: one answer moves
 *  everything filed under it, which is five judgements instead of a hundred
 *  and fifty. */
export interface BoardroomKindRow {
  id: number;
  title: string;
  body: string | null;
  relevance: 'core' | 'related';
  products: number;
  companies: number;
}

export interface BoardroomRelated {
  kind: 'related';
  scoped: boolean;
  /** Every kind the room has found, set-aside ones first. */
  kinds: BoardroomKindRow[];
  /** The companies currently off the tables, drawn as the roster draws them —
   *  these are leads for another question, not rubbish. */
  rows: BoardroomCompetitorRow[];
  missing: BoardroomReportMissing[];
}

/** One domain the room's own searches keep turning up. */
export interface BoardroomWhoRanksRow {
  domain: string;
  /** How many of the room's searches it appeared in — the number that says a
   *  site owns a topic, as opposed to how many of its pages did. */
  searches: number;
  results: number;
  avg_position: number | null;
  best_position: number | null;
  queries: string[];
}

/** Every tag category in a room, for the Tags panel. */
export interface BoardroomTagList {
  /** Companies, products, ideas and types: what an axis is sorted over. */
  taggable: number;
  categories: {
    dimension: string;
    /** axis: what the room compares on · content: what it is about · label: added to count by */
    source: 'axis' | 'content' | 'label';
    meaning: string | null;
    values: { value: string; count: number }[];
    more: number;
    /** How many of the taggable things carry this category. */
    tagged: number;
    /** An axis's starting values that no item carries yet. */
    unused?: string[];
    /** An axis's items looked at and left empty: nothing on them answers it. */
    skipped?: number;
  }[];
  /** The sorter's counts, for the Recalculate button. */
  sorting?: BoardroomTagging;
  /** The room's taxonomy library, as the person wrote it. */
  taxonomy?: string;
}

/** What the sorter still has to do to an item: place it on an axis, or read it for content. */
/** The Appeal rows in the panel, each narrowing the feed to the companies behind it. */
export type AppealQueue = 'appeal_reading' | 'appeal_waiting' | 'appeal_read' | 'appeal_no_pages';
/** Work the feed can be narrowed to: the sorter's two queues, or an Appeal row. */
export type SortingWaiting = 'axes' | 'content' | AppealQueue;
/** The feed narrowed to one sorting queue, and maybe one kind of item in it. */
export interface SortingWaitingPick {
  queue: SortingWaiting;
  kind: BoardroomItemKind | null;
}

/** The items the sorter skipped on one axis, newest first, with its reason. */
export interface BoardroomAxisSkipped {
  dimension: string;
  total: number;
  items: { id: number; kind: string; title: string; reason: string }[];
}

/** One axis from the Tags panel: new, or more starting values for one the room has. */
export interface BoardroomAxisWrite {
  dimension: string;
  meaning?: string;
  within?: string;
  values?: string[];
}

/** What an item is filed under, and who filed each, in which dig, from what. */
export interface BoardroomFilings {
  item_id: number;
  written_by: { id: number; name: string; archetype: string } | null;
  filings: {
    link_id: number;
    kind: string;
    parent: { id: number; title: string; kind: string };
    at: string | null;
    /** False for links made before who-filed-it was recorded. */
    tracked: boolean;
    by: { id: number; name: string; archetype: string } | null;
    run: { id: number; intent: string; directive: string } | null;
    via: { id: number; kind: string; title: string; url: string | null } | null;
  }[];
}

export interface BoardroomWhoRanks {
  kind: 'who_ranks';
  scoped: boolean;
  rows: BoardroomWhoRanksRow[];
  /** The denominators, so a thin table reads as thin. */
  searches: number;
  /** Searches per engine: most are not Google. */
  engines?: Record<string, number>;
  results: number;
  domains: number;
  shown: number;
}

/** One page-one result for a term, matched against the board. */
export interface BoardroomSearchHit {
  position: number;
  domain: string;
  url: string | null;
  title: string;
  company_id: number | null;
  company: string | null;
  product_id: number | null;
  product: string | null;
  /** Estimated monthly clicks: volume × the usual share for this position. */
  clicks: number;
}

/** A term as a count opens it: where it ranks, and what that is worth. */
export interface BoardroomSearchTermHit {
  keyword: string;
  /** For the SERP viewer. Null until the term has been searched. */
  keyword_id: number | null;
  position: number;
  volume: number | null;
  clicks: number;
}

/** One term on the room's keyword pile. */
export interface BoardroomSearchTerm {
  keyword: string;
  /** A company's name that means something else (the room searched it, and its
   *  own site was not on page one): listed, not counted. */
  off_market?: boolean;
  /** For the SERP viewer. Null until the term has been searched. */
  keyword_id: number | null;
  volume: number | null;
  growth_pct: number | null;
  top_bid: number | null;
  competition: string | null;
  /** seed: the demand analyst asked about it; related: Google offered it. */
  source: 'seed' | 'related';
  /** waiting: nobody has searched it yet — a later dig will. */
  status: 'searched' | 'pending' | 'failed' | 'waiting';
  engine: string | null;
  checked_at: string | null;
  top: BoardroomSearchHit[];
  on_board: number;
}

/** A company on the board, by the searches it holds. */
export interface BoardroomSearchPlayer {
  company_id: number;
  company: string;
  domain: string;
  keywords: number;
  top3: number;
  best: number | null;
  clicks: number;
  /** Its share of every estimated click on the terms searched so far. */
  share: number;
  /** Every term it is on page one for, best position first. */
  terms: BoardroomSearchTermHit[];
}

export interface BoardroomSearchMarket {
  kind: 'search_market';
  scoped: boolean;
  rows: BoardroomSearchTerm[];
  players: BoardroomSearchPlayer[];
  /** Sites on page one that are not a company on the board. */
  unclaimed: {
    domain: string;
    keywords: number;
    best: number | null;
    clicks: number;
    share: number;
    terms: BoardroomSearchTermHit[];
  }[];
  totals: { keywords: number; searched: number; waiting: number; volume: number; clicks: number };
  missing: BoardroomReportMissing[];
  /** Under a lens: what it kept and what it left out. Sites that are not a
   *  company on the board cannot carry a tag, so a lens leaves them all out. */
  lens?: { companies: number; companies_all: number; keywords_all: number; off_board: number };
}

// ---- Brand interception: the searches people make about one company ----

export type BoardroomBrandClass = 'branded' | 'commercial' | 'problems' | 'alternatives' | 'comparison' | 'people';

/** One result on page one (or an ad) for one of the company's searches. */
export interface BoardroomBrandHit {
  position: number;
  domain: string;
  url: string | null;
  title: string;
  /** target: the company itself; competitor: another company on the board; third: anyone else. */
  owner: 'target' | 'competitor' | 'third';
  owner_id: number | null;
  owner_name: string | null;
  site_type: string | null;
  /** About the company AS A CHOICE: alternatives to it, it against something, reviews of it. */
  aimed: boolean;
  clicks?: number;
}

export interface BoardroomBrandRow {
  q: string;
  qclass: BoardroomBrandClass;
  intent: string;
  about: string;
  volume: number | null;
  top_bid: number | null;
  growth_pct: number | null;
  keyword_id: number | null;
  status: 'searched' | 'pending' | 'failed' | 'waiting';
  engine: string | null;
  target_position: number | null;
  best_rival: { id: number; name: string; position: number } | null;
  owners: { target: number; competitor: number; third: number };
  top: BoardroomBrandHit[];
  ads: BoardroomBrandHit[];
}

/** A rival (or a third-party site) by the company's searches it appears on. */
export interface BoardroomBrandHolder {
  id?: number;
  name?: string;
  domain?: string;
  site_type?: string | null;
  clicks: number;
  share: number;
  best: number | null;
  keywords: number;
  aimed_pages?: number;
  ads?: number;
  terms: BoardroomSearchTermHit[];
}

export interface BoardroomBrandAimedPage {
  url: string;
  title: string;
  owner: 'competitor' | 'third';
  owner_id: number | null;
  owner_name: string;
  site_type: string | null;
  query: string;
  keyword_id: number | null;
  qclass: BoardroomBrandClass;
  position: number;
  /** A rival's own page aimed at the name; a review site's is the market talking. */
  deliberate: boolean;
}

export interface BoardroomBrandMetrics {
  clicks: number;
  target_share: number;
  interception: number;
  third_party_share: number;
  branded_queries: number;
  number_one: number;
  ownership: number;
  paid_queries: number;
  by_class: Record<string, { target: number; competitor: number; third: number }>;
}

export interface BoardroomBrandProgress {
  queries: number;
  searched: number;
  pending: number;
  failed: number;
  waiting: number;
}

export type BoardroomBrandLevel = 'displacement' | 'interception' | 'emerging' | 'open';

/** One finding, at the evidence level its receipts reach. */
export interface BoardroomBrandFinding {
  level: BoardroomBrandLevel;
  /** How it reads for this reader: a threat to the company, or an opening. */
  reading: 'threat' | 'opportunity';
  headline: string;
  who: { kind: 'competitor' | 'third_party' | 'none'; id: number | null; name: string; site_type?: string | null };
  clicks: number;
  ads: string[];
  receipts: {
    q: string;
    keyword_id: number | null;
    qclass: BoardroomBrandClass;
    volume: number | null;
    /** Which engine this search ran on. */
    engine?: string | null;
    position: number | null;
    target_position: number | null;
    url: string | null;
    title: string | null;
  }[];
}

/** The company's website on the board is missing or is not its own site. */
export interface BoardroomBrandSiteWarning {
  url: string | null;
  site: string | null;
  text: string;
  /** What to check it against: where the address came from, and pages read
   *  that look like the company's own site. */
  evidence?: { id: number; title: string; url: string | null; why: string }[];
}

export interface BoardroomBrand {
  kind: 'brand';
  site_warning?: BoardroomBrandSiteWarning | null;
  /** ours: the reader IS this company; theirs: the reader competes with it. */
  side?: 'ours' | 'theirs';
  findings?: BoardroomBrandFinding[];
  /** The written report for a marketing or executive team, once written. */
  write_up?: { text: string; at: string | null } | null;
  /** Earlier looks, for what moved. */
  history?: { at: string; target_share: number | null; interception: number | null }[];
  started: boolean;
  started_at?: string | null;
  card_id?: number | null;
  target: { id: number; title: string; name: string };
  rows?: BoardroomBrandRow[];
  interceptors?: BoardroomBrandHolder[];
  third_parties?: BoardroomBrandHolder[];
  aimed_pages?: BoardroomBrandAimedPage[];
  paid?: { id: number; name: string; queries: string[] }[];
  metrics?: BoardroomBrandMetrics;
  progress?: BoardroomBrandProgress;
  method?: { engines: string[]; from: string | null; to: string | null; not_measured: string[]; clicks: string };
  missing: BoardroomReportMissing[];
  /** Whether these searches are re-checked by a weekly watch. */
  watch?: BoardroomBrandWatch;
  /** The category searches this company is not top 3 on, biggest first. */
  win?: BoardroomBrandWin;
}

export interface BoardroomBrandWatch {
  watching: boolean;
  subject_id?: number;
  slug?: string;
  searches?: number;
  every_days?: number;
  paused_reason?: string | null;
  last_run_at?: string | null;
}

export interface BoardroomBrandWin {
  rows: {
    q: string;
    keyword_id: number | null;
    volume: number | null;
    top_bid: number | null;
    growth_pct: number | null;
    /** Where this company is on page one, or null when it is not. */
    position: number | null;
    /** Which engine this search ran on: most are not Google. */
    engine?: string | null;
    holders: { id: number; name: string; position: number }[];
    /** How open page one is: a score out of 100, why, and each result read. */
    page_one?: BoardroomPageOne;
  }[];
  more: number;
  searched: number;
  waiting: number;
}

export interface BoardroomPageOne {
  /** Share of the results read that are open, 0 to 100. */
  score: number | null;
  label: 'weak' | 'mixed' | 'strong' | null;
  looked: number;
  /** How many of them are open. */
  open?: number;
  /** The places checked, in order: title · url · h1 · opening · snippet. */
  spots?: string[];
  ads: number;
  reasons: string[];
  results: {
    position: number | null;
    site: string | null;
    company: string | null;
    company_id: number | null;
    title: string | null;
    url: string | null;
    /** company · authority · forum · social · review site · marketplace · video · publisher */
    kind: string;
    home_page: boolean;
    /** Per place: true uses the keyword, false does not, null not read. */
    marks: Record<string, boolean | null>;
    is_open: boolean;
    why: string;
  }[];
}

export interface BoardroomBrandSummary {
  started: boolean;
  site_warning?: BoardroomBrandSiteWarning | null;
  started_at?: string | null;
  card_id?: number | null;
  progress?: BoardroomBrandProgress;
  target_share?: number;
  interception?: number;
  third_party_share?: number;
  ownership?: number;
  number_one?: number;
  branded_queries?: number;
  paid_queries?: number;
  aimed_pages?: number;
  side?: 'ours' | 'theirs';
  readings?: { threat: number; opportunity: number };
  levels?: Record<BoardroomBrandLevel, number>;
  top_interceptors?: { id: number; name: string; share: number }[];
}

/** Who goes after whom: a row per company looked at, a column per rival. */
export interface BoardroomBrandGrid {
  kind: 'brand_grid';
  /** Searches per engine behind the grid: most are not Google. */
  engines?: Record<string, number>;
  rows: {
    id: number;
    title: string;
    own: number;
    rivals: number;
    third_parties: number;
    paid_queries: number;
    searched: number;
    /** Rival id → its share of this company's estimated clicks. */
    cells: Record<string, number>;
  }[];
  cols: { id: number; name: string; targets: number; share: number }[];
  more_cols: number;
  missing: BoardroomReportMissing[];
}

/** Email capture: a row per company, how it asks for an email and on which pages. */
export interface BoardroomEmailCaptureRow {
  id: number;
  title: string;
  website: string | null;
  /** asks: a form or signup script was seen. nothing: its pages were read and ask for
   *  nothing. unread: no website, or none of its pages read since form capture began. */
  status: 'asks' | 'nothing' | 'unread';
  providers: string[];
  provider_keys: string[];
  kinds: string[];
  offer: string | null;
  pages: { url: string; is_home: boolean; where: string; vendors: string[]; offer: string | null }[];
  /** The website's domain, which sign-ups are keyed by. */
  site?: string | null;
  /** The form the sign-up robot would use. Null when there is none to fill. */
  form?: { url: string; kind: string | null; offer: string | null } | null;
  /** What that form makes a visitor want, from the company's appeal read. */
  form_appeal?: { appeal: string | null; pull: string | null } | null;
  /** Spy addresses used here: the first, then the one held back a few days. */
  signups?: BoardroomEmailSignup[];
}

export type BoardroomEmailSignupStatus =
  | 'waiting'
  | 'working'
  | 'signed_up'
  | 'needs_person'
  | 'failed'
  | 'done_by_hand';

export interface BoardroomEmailSignup {
  id: number;
  which: number;
  status: BoardroomEmailSignupStatus;
  address: string | null;
  /** The made-up person the mailer gave this address. Never has a phone number. */
  persona: BoardroomEmailSignupPersona | null;
  available_at: string | null;
  signed_up_at: string | null;
  tries: number;
  note: string | null;
  page_url: string | null;
  fields_asked: { name: string; label: string; required: boolean; filled: boolean | string }[];
  thanks_url: string | null;
  download_url: string | null;
  /** Emails the address has had, from the mailer. Null until checked. */
  mail_count?: number | null;
  first_mail_at?: string | null;
}

export interface BoardroomEmailSignupPersona {
  first_name?: string;
  last_name?: string;
  company?: string;
  job_title?: string;
  country?: string;
}

export interface BoardroomEmailSignupStart {
  started: number;
  skipped: number;
  errors: { site: string | null; error: string | null }[];
}

/** One link to the mailer. Its API key belongs to one mailer organization, so each
 *  connection sends sign-ups to one org. `signups` = sign-ups made through it. */
export interface EmailSpyConnection {
  id: number;
  name: string;
  mailer_url: string | null;
  has_api_key: boolean;
  is_default: boolean;
  /** The mailer org's name, from the last test. */
  organization_name: string | null;
  signups: number;
  updated_at: string | null;
}

export interface EmailSpyConnectionCreate {
  name: string;
  mailer_url: string;
  api_key: string;
  is_default?: boolean;
}

/** Leave out api_key to keep the saved key. */
export type EmailSpyConnectionUpdate = Partial<EmailSpyConnectionCreate>;

/** A room's connection. `connection_id` is the room's own pick (null = follows the default);
 *  `using` is the one it actually uses (null when there are none). */
export interface BoardroomEmailSpyChoice {
  connection_id: number | null;
  using: { id: number; name: string; organization_name: string | null } | null;
}

export interface BoardroomEmailCapture {
  kind: 'email_capture';
  rows: BoardroomEmailCaptureRow[];
  companies: number;
  asking: number;
  asks_nothing: number;
  unread: number;
  providers: { key: string; name: string; companies: number }[];
  missing: BoardroomReportMissing[];
  /** Companies signed up to (by the robot or by hand), ones that need a person, and ones
   *  "Sign up all" would start now. */
  signed_up?: number;
  signup_needs_person?: number;
  signup_due?: number;
  /** Signed up a while ago with no mail yet, and how many "Try again" would start. */
  signup_no_mail?: number;
  signup_retry?: number;
  /** The connection this room uses. `chosen` false means it follows the default. */
  email_spy?: { id: number; name: string; organization_name: string | null; chosen: boolean } | null;
}

/** Contacts: a row per company, how to reach it. */
export interface BoardroomContactsRow {
  id: number;
  title: string;
  website: string | null;
  /** found: a detail was seen. nothing: its pages were read and show none. unread: no
   *  website, or none of its pages read since we started looking. */
  status: 'found' | 'nothing' | 'unread';
  /** Its own domain's addresses first. */
  emails: string[];
  phones: string[];
  contact_pages: string[];
  socials: { network: string; url: string }[];
  /** Chat tool names, and their keys. */
  chat: string[];
  chat_keys: string[];
  address: string | null;
  /** What its contact form asks for, and the page it is on. */
  contact_form: { url: string; fields: { name: string; type: string; required: boolean }[] } | null;
}

/** One page read for a company's appeal. Angle and persuasion ids are the shared ones in
 *  backend boardroom/taxonomy.py (snake_case, e.g. `avoid_wasted_spend`). Reads saved
 *  before the shared taxonomy carry only url, intent and why. */
export interface BoardroomAppealPage {
  url: string;
  home?: boolean;
  intent: 'learning' | 'comparing' | 'buying' | null;
  why: string;
  /** The page's main claim, quoted. */
  message?: string;
  /** Strongest first: the first is what the page leads with. */
  angles?: string[];
  persuasion?: string[];
}

/** One company's pitch, read by a model from its pages (backend boardroom/appeal.py). */
export interface BoardroomAppealRow {
  id: number;
  title: string;
  website: string | null;
  site: string | null;
  /** read | working | due | cannot_tell | failed | no_credits | no_pages | no_site */
  state: string;
  /** No read yet, or a page came in since the last one. */
  due: boolean;
  note: string | null;
  read_at: string | null;
  pages_read: string[];
  /** Who the pages are written for, as a sentence. */
  audience: string | null;
  /** TARGET_AUDIENCE values: the kinds of customer. */
  audiences: string[];
  /** BUYER_ROLE values: the job roles the pages speak to. */
  roles: string[];
  problem: string | null;
  promise: string | null;
  /** MARKETING_ANGLE ids the company's pages lead with, strongest first. */
  angles: string[];
  /** PERSUASION ids shown across its pages. */
  persuasion: string[];
  tone: string | null;
  call_to_action: string | null;
  pages: BoardroomAppealPage[];
  forms: { url: string; appeal: string; angle: string | null }[];
  evidence: string[];
}

/** Companies side by side. Mirrors `jcseo/boardroom/compare.py`. */
export interface BoardroomCompareColumn {
  id: number;
  title: string;
  products: string[];
  price: string | null;
  from_monthly: number | null;
  to_monthly: number | null;
  audiences: string[];
  roles: string[];
  angles: string[];
  proof: string[];
  promise: string | null;
  read: boolean;
  features: number;
  complaints: string[];
  complaint_count: number;
  apart: string[];
}

export interface BoardroomCompare {
  kind: 'compare';
  companies: BoardroomCompareColumn[];
  shared: string[];
  choices: { id: number; title: string }[];
}

/** The Brief: the top lines of the room's tables on one screen. Mirrors
 *  `jcseo/boardroom/summary.py`. */
export interface BoardroomRoomBrief {
  kind: 'room_brief';
  companies: number;
  products: number;
  most_searched: { id: number; title: string; volume: number }[];
  prices: {
    products: number;
    priced: number;
    low: string | null;
    high: string | null;
    examples: { id: number; title: string; label: string }[];
  };
  complaints: {
    count: number;
    unfixed: number;
    top: { id: number; title: string; voices: number; unfixed: boolean; last_heard?: string | null }[];
  };
  selling: {
    read: number;
    top: { angle: string; label: string; companies: number }[];
    unused: string[];
  };
  unknown: { total: number; top: { words: string; count: number }[] };
  /** A story room's top lines: why now, the latest event, the newest quote on
   *  the record, and the best angle to write. */
  story?: {
    why_now: BoardroomWhyNow | Record<string, never>;
    events: number;
    people: { id: number; title: string; role: string }[];
    quote: (BoardroomOnRecord & { by: string; by_id: number }) | null;
    angle: { id: number; title: string; volume: number } | null;
    angles: number;
  };
  /** What each section's numbers rest on: "prices found for 4 of 77". */
  coverage?: {
    searched: BoardroomCoverage | null;
    prices: BoardroomCoverage | null;
    complaints: BoardroomCoverage | null;
    selling: BoardroomCoverage | null;
  };
}

export interface BoardroomCoverage {
  label: string;
  have: number;
  of: number;
}

export interface BoardroomAppeal {
  kind: 'appeal';
  rows: BoardroomAppealRow[];
  companies: number;
  read: number;
  working: number;
  due: number;
  no_pages: number;
  /** How many companies lead with each angle, and each family of angles. */
  angles?: { angle: string; family: string | null; companies: number }[];
  families?: { family: string; label: string; frame: 'avoid' | 'gain'; companies: number }[];
  tactics?: { tactic: string; companies: number }[];
  /** Who the field sells to: companies per TARGET_AUDIENCE value. */
  audiences?: { label: string; companies: number }[];
  /** The whole shared list, used or not, so the page can show the gaps. */
  taxonomy?: {
    families: {
      family: string;
      label: string;
      frame: 'avoid' | 'gain';
      angles: { angle: string; meaning: string }[];
    }[];
    tactics: { tactic: string; meaning: string }[];
  };
  /** Families of angles no company read leads with. Empty until one is read. */
  open_families?: { family: string; label: string }[];
  /** The room's own ideas: the strategist's angle, and the shared angle ids it would lead with. */
  ideas?: { id: number; title: string; angle: string | null; angles?: string[] }[];
  missing: BoardroomReportMissing[];
}

export interface BoardroomContacts {
  kind: 'contacts';
  rows: BoardroomContactsRow[];
  companies: number;
  found: number;
  nothing: number;
  unread: number;
  with_email: number;
  with_phone: number;
  with_chat: number;
  chat_tools: { key: string; name: string; companies: number }[];
  missing: BoardroomReportMissing[];
}

export interface BoardroomBrandStart {
  card_id: number;
  queries: number;
  searches: number;
  reused: number;
  demand_lookups: number;
  /** Find more only: how many searches it added, and why none when none. */
  added?: number;
  note?: string | null;
}

/** A term one company or product ranks for, on its profile. */
export interface BoardroomRanking {
  keyword: string;
  keyword_id: number | null;
  volume: number | null;
  growth_pct: number | null;
  position: number;
  engine: string | null;
  url: string | null;
  clicks: number;
  checked_at: string | null;
}

/** One column of a table the room was asked for. `key` is what the cell is
 *  filed under; `hint` is what it means, for the header's tooltip. */
export interface BoardroomListColumn {
  key: string;
  label: string;
  hint: string;
}

export interface BoardroomListRow {
  id: number;
  cells: Record<string, string>;
  /** When the table has a tags column: each tag with its dimension, so the
   *  page can draw badges that set the lens. */
  tags?: { dimension: string; value: string }[];
}

/** A table whose SPEC is the query — written by the chat from what a person
 *  asked for, rather than by a builder somebody wrote in advance. */
export interface BoardroomListCard {
  kind: 'list';
  scoped: boolean;
  spec: {
    kinds: string[];
    tags: string[];
    verified: string | null;
    columns: string[];
    order: string;
    limit: number;
  };
  columns: BoardroomListColumn[];
  rows: BoardroomListRow[];
  /** Rows matching the spec, which can exceed what `limit` drew. */
  total: number;
  /** Always empty: it reads what was recorded, so it is never short of
   *  anything and never puts work in the queue. */
  missing: unknown[];
  title?: string;
  subject?: string;
}

/** One group in a count block, with the rows in it so a click can list them. */
export interface BoardroomReportBar {
  label: string;
  count: number;
  items: { id: number; title: string }[];
}

interface BoardroomReportBlockBase {
  title: string;
  /** How many rows the block rests on. */
  n: number;
  /** Set when there are too few rows to read much into. */
  note: string | null;
  /** What the block really does, in words — its filters, sort or grouping. */
  shows?: string;
  /** Fields the block asked about that no row has a value for. */
  unknown_fields?: string[];
}

export type BoardroomReportBlock =
  | (BoardroomReportBlockBase & { type: 'table'; data: BoardroomListCard })
  | (BoardroomReportBlockBase & {
      type: 'count';
      spec: { group_by: string; chart: 'bar' | 'none' };
      data: { bars: BoardroomReportBar[]; total: number };
    });

/** A report the room was asked for: several blocks, drawn in order. */
export interface BoardroomReportCard {
  kind: 'report';
  scoped: boolean;
  /** The room has more findings than a report reads at once; the oldest are
   *  left out. */
  truncated?: boolean;
  spec: { blocks: unknown[]; suggested_by?: string; why?: string };
  blocks: BoardroomReportBlock[];
  title?: string;
  subject?: string;
}

// --- The room chat --------------------------------------------------------
//
// One conversation per room, held on an AgentRunState rather than a table of
// its own, so it outlives any single dig.

export interface BoardroomChatMessage {
  id: number;
  message_number: number;
  entity_type: string; // HUMAN | AI | SYSTEM
  content: string;
  has_tool_call: boolean;
  created_at: string | null;
}

/** Something only a person can settle. Derived on read; what gets stored is
 *  the answer, which is what stops it being asked twice. */
export interface BoardroomDecision {
  kind: string;
  subject: string;
  /** One line a person can answer without opening anything else. */
  ask: string;
  /** The numbers behind it, so the answer is theirs rather than ours. */
  why: string;
  options: { key: string; label: string; effect?: string }[];
}

export interface BoardroomChat {
  boardroom_id: number;
  status: string; // idle | thinking | failed
  last_error?: string | null;
  current_session_number: number;
  context_tokens: number;
  context_window?: number | null;
  messages: BoardroomChatMessage[];
  suggested_prompts: string[];
  agent_stage: string;
  agent_model_label?: string | null;
  /** The one thing the room needs settled, if there is one. */
  decision?: BoardroomDecision | null;
}

/** What one chat turn touched, so the page reconciles those parts and no
 *  others. Arrives on the room's own websocket event. */
export interface BoardroomChatChanged {
  /** The question, the success criteria or the focus. */
  room: boolean;
  frame: boolean;
  seats: boolean;
  /** How many questions the turn put on the board. */
  questions: number;
  /** Which tables the room works toward changed — the board's fill and its
   *  "being worked" marks are now stale. */
  tables?: boolean;
  started_run_id: number;
  stopped_run_id: number;
  /** A card the turn just built, to open for the person: "report:<subject>". */
  open_card?: string;
}
/** The reports' lens, carried by a run so personas work only inside it. */
export interface BoardroomRunScope {
  tags: string[];
  verified: string | null;
  by: string | null;
  /** WHICH TABLES this run is filling. The lens above narrows which ITEMS may
   *  be touched; this narrows what the run is working TOWARD, which is the
   *  answer to "I pressed Dig on the price map and competitors filled". Empty
   *  means the room's own tables. */
  tables?: string[];
}

export interface BoardroomPage {
  items: BoardroomSummary[];
  total: number;
}

export interface BoardroomItemPage {
  items: BoardroomItem[];
  /** Cursor for the page AFTER these (older→newer), or null at the end. */
  next_after_id: number | null;
  /** Cursor for the page BEFORE these — what "load more" uses, because the
   *  feed reads newest first. Null when there is no more history. */
  next_before_id: number | null;
}

export interface BoardroomWebSocketEvent {
  event_type: 'boardroom';
  boardroom_id: number;
  status:
    | 'item'
    | 'tag'
    | 'run'
    | 'persona'
    | 'frame'
    | 'director'
    | 'tagging'
    | 'appeal'
    | 'answer'
    | 'summary'
    | 'lead'
    | 'board';
  item?: BoardroomItem;
  tag?: BoardroomTag;
  run?: BoardroomRun;
  persona?: BoardroomPersona;
  frame?: BoardroomFrame;
  /** The sorter's counters after a batch. */
  tagging?: BoardroomTagging;
  /** Null when the room does not have the Appeal table. */
  appeal?: BoardroomAppealStatus | null;
  /** One version of the room's answer to its own question. */
  answer?: BoardroomAnswer;
  /** Its counters, always sent with it — never apart. */
  answering?: BoardroomAnswering;
  /** A `summary` event: which table's paragraph was written (or failed). */
  deliverable_id?: number;
  /** A `lead` event: the lead the room follows and its list of leads. */
  lead?: BoardroomLeadBlock;
  /** A `board` event: the Dashboard as the server rebuilt it while a dig
   *  changed it - the cards and the Noticed and Observed trays. */
  board?: BoardroomDeliverables & {
    notices?: BoardroomNotice[];
    dismissed?: BoardroomDismissedNotice[];
    observations?: BoardroomObservation[];
  };
  /** A director round: the worklists it wrote. */
  assignments?: BoardroomAssignment[];
  tokens_spent?: number;
  /** False on an `item` event that is bookkeeping — a seat reserving or
   *  releasing the row, a re-add that linked nothing new. The row is upserted
   *  but not marked as changed. Absent means news. */
  news?: boolean;
  /** True only where the row was just CREATED. An insertion and a changed cell
   *  both light a table up and want different reactions from a reader, and a
   *  timestamp could never tell them apart — so the one place that knows says
   *  so. Absent means the row already existed. */
  fresh?: boolean;
}

export interface Entitlement {
  product: Product;
  /** Stripe's vocabulary, verbatim: active | trialing | past_due | canceled | … */
  status: string;
  current_period_end?: string | null;
  /** Whether this row currently GRANTS the product. A `past_due` row is
   *  returned but not active, so the UI can say "lapsed" instead of pretending
   *  the product was never bought. */
  active: boolean;
  /** A key of the server's plan table (`jcseo/billing/plans.py`): free |
   *  starter | pro. The server resolves null to "free" before it gets here. */
  plan: PlanKey;
  /** The customer's own terms, for their billing page. */
  monthly_fee_usd?: number | null;
  included_credits?: number;
  overage_policy?: 'STOP' | 'BILL';
}

/** The stored plan keys, which are NOT the labels shown to anyone: `starter`
 *  reads as "Tier 1" and `pro` as "Tier 2". The keys are frozen because they
 *  are written into `entitlements.plan` on live rows and the server reads an
 *  unknown key as free — so renaming one would silently drop whoever held it
 *  to the free allowance. `enterprise` is granted by hand and never sold. */
export type PlanKey = 'free' | 'starter' | 'pro' | 'enterprise';

/** One plan's allowance and limits, from the server's table. `null` caps mean
 *  unlimited. Dollar prices are NOT here — they live in Stripe. */
export interface PlanInfo {
  key: PlanKey;
  label: string;
  monthly_credits: number;
  max_rooms: number | null;
  max_personas_per_room: number | null;
  max_token_budget: number;
  byok_allowed: boolean;
  /** Can a user buy this themselves? False for `free` (where everyone starts)
   *  and `enterprise` (a conversation). A plan with no `stripe_prices` row has
   *  no button regardless — this is the intent, that is the fact. */
  purchasable: boolean;
}

/** The user's credit balance: two buckets. `plan_credits` resets every period,
 *  `bank_credits` (top-ups, comps) never expires. Charges draw plan first. */
export interface CreditsBalance {
  plan_credits: number;
  bank_credits: number;
  balance: number;
  allowance: number;
  period_start: string | null;
  period_end: string | null;
  /** A contract's leave to go past zero, and how far (null = unlimited). */
  overage_allowed: boolean;
  overage_cap: number | null;
  /** How far past zero the account is — what an invoice settles. */
  overage_credits: number;
}

/** Credits per 1k tokens for one model tier. */
export interface LLMRate {
  input_per_1k: number;
  output_per_1k: number;
  cached_input_per_1k: number;
}

export type ModelPriceTier = 'economy' | 'standard' | 'premium';

/** The house rate card, versioned: credits per 1k tokens by tier, credits per
 *  network call by lookup kind, and the fraction charged when the customer's
 *  own key or network did the work. Never what it costs us. */
export interface PriceBook {
  version: number;
  usd_per_credit: number;
  llm_rates: Record<string, LLMRate>;
  network_rates: Record<string, number>;
  byok_llm_multiplier: number;
  byok_network_multiplier: number;
}

/** This period's usage for one product: credits, and the real units beneath. */
export interface ProductUsage {
  credits: number;
  llm_credits: number;
  network_credits: number;
  input_tokens: number;
  output_tokens: number;
  calls: Record<string, number>;
}

/** Who may see and get a product. Nobody is let in implicitly — an admin
 *  grants a private product to themselves like anyone else. */
export type ProductAvailability = 'PUBLIC' | 'PRIVATE' | 'SUNSETTING' | 'RETIRED';

export interface ProductCatalogEntry {
  availability: ProductAvailability;
  sunset_at: string | null;
}

export interface BillingInfo {
  products: Entitlement[];
  /** Every product THIS USER MAY SEE: public ones plus any they hold a row
   *  for. A private product someone else uses is simply absent. Served by the
   *  API so the launcher and the backend cannot drift about what is on offer. */
  available: Product[];
  /** Availability per visible product, for the retiring banner. */
  catalog: Record<string, ProductCatalogEntry>;
  credits: CreditsBalance;
  price_book: PriceBook;
  plans: Record<string, PlanInfo>;
  /** By product key ("house" for work outside any product), this period. */
  usage: Record<string, ProductUsage>;
  /** What can actually be bought right now, from the server's `stripe_prices`
   *  rows. A plan absent from here gets no button: "Boardroom only" is a fact
   *  about the data, not a branch in the page. */
  sellables: Sellable[];
  /** Whether this account has ever been a Stripe customer. The Portal is only
   *  offered when there is something for it to manage. */
  has_stripe_customer: boolean;
}

/** One `stripe_prices` row, as the admin screen sees it. */
export interface StripePriceRow {
  id: number;
  kind: 'SUBSCRIPTION' | 'TOPUP';
  stripe_price_id: string;
  product: Product | null;
  plan: PlanKey | null;
  credits: number | null;
  unit_amount_usd: number | null;
  currency: string;
  interval: string | null;
  label: string | null;
  active: boolean;
  sort_order: number;
}

/** What an admin sends to create one. No dollar amount: the server reads that
 *  back from Stripe so the shown price cannot drift from the charged one. */
export interface StripePriceInput {
  kind: 'SUBSCRIPTION' | 'TOPUP';
  stripe_price_id: string;
  product?: Product | null;
  plan?: PlanKey | null;
  credits?: number | null;
  label?: string | null;
  active?: boolean;
  sort_order?: number;
}

/** One thing on sale. A SUBSCRIPTION carries (product, plan); a TOPUP carries
 *  a credit count. The dollar amount is read back from Stripe when an admin
 *  saves the row, so what is shown is what is charged. */
export interface Sellable {
  stripe_price_id: string;
  kind: 'SUBSCRIPTION' | 'TOPUP';
  product: Product | null;
  plan: PlanKey | null;
  credits: number | null;
  unit_amount_usd: number | null;
  currency: string;
  interval: string | null;
  label: string | null;
}

export interface CreditLedgerRow {
  id: number;
  at: string | null;
  kind: 'GRANT_PLAN' | 'GRANT_TOPUP' | 'CHARGE' | 'ADJUST' | 'EXPIRE';
  action: string | null;
  credits: number;
  usd: number | null;
  /** The receipt: tokens for an LLM action, calls for a network one. */
  units: Record<string, unknown> | null;
  product: string | null;
  byok: boolean;
  price_book_version: number | null;
  llm_usage_id: number | null;
  boardroom_item_id: number | null;
  note: string | null;
}

/** The balance moved (a charge or a grant). Sent to the user's own channel. */
export interface CreditsWebSocketEvent {
  event_type: 'credits';
  balance: number;
  plan_credits: number;
  bank_credits: number;
  period_end?: string | null;
  action?: string | null;
  delta: number;
}

/** What a 402 is asking for. Derived from `ApiError.detail` by
 *  `billing/credits.ts:paywallOf`; rendered by `PaywallNotice`. */
export type Paywall =
  | { kind: 'product'; products: Product[] }
  | { kind: 'retired'; product: Product | null }
  | { kind: 'credits'; balance: number; period_end: string | null }
  | { kind: 'plan'; limit: string; plan: string; max: number | null; product: Product | null };

export interface AdminCatalogRow {
  product: Product;
  availability: ProductAvailability;
  sunset_at: string | null;
  note: string | null;
}

// --- Admin: access + credits ------------------------------------------------------

export type OveragePolicy = 'STOP' | 'BILL';

/** The contract terms an admin may set on one product row. */
export interface EntitlementTerms {
  monthly_fee_usd: number | null;
  allowance_override: number | null;
  rate_multiplier: number;
  overage_policy: OveragePolicy;
  overage_cap_credits: number | null;
  overage_usd_per_credit: number | null;
}

export interface AdminEntitlement extends EntitlementTerms {
  product: Product;
  status: string;
  active: boolean;
  plan: PlanKey;
  /** Stripe owns this row, not an admin. Revoking here does NOT cancel the
   *  subscription — the next webhook writes it back and the customer keeps
   *  being billed — so the panel warns instead of quietly doing the wrong
   *  thing. Cancelling belongs in the Customer Portal or the Stripe dashboard. */
  stripe_backed?: boolean;
  current_period_end?: string | null;
}

export interface AdminEntitlementGrant extends Partial<EntitlementTerms> {
  product: Product;
  status?: string;
  plan?: PlanKey;
  /** Term fields to reset to their defaults. */
  clear?: (keyof EntitlementTerms)[];
}

export interface AdminCredits {
  credits: CreditsBalance;
  allowance: number;
  /** What an overage invoice would say right now. */
  overage_usd: number;
  overage_usd_per_credit: number;
  reconciled: boolean;
  drift: number;
  ledger: (CreditLedgerRow & { cost_usd: number | null })[];
}

export interface AdminPriceBookVersion extends PriceBook {
  active: boolean;
  note: string | null;
  created_at: string | null;
}

export interface AdminPriceBooks {
  versions: AdminPriceBookVersion[];
  active: PriceBook;
  tiers: string[];
  network_kinds: string[];
  defaults: PriceBook;
}

// --- Admin: the margin screen ------------------------------------------------------

export interface MarginBucket {
  label: string;
  billed_usd: number;
  contract_usd: number;
  cost_usd: number;
  cost_llm_usd: number;
  cost_network_usd: number;
  cost_by_basis: Record<string, number>;
  margin_usd: number;
  credits: number;
  input_tokens: number;
  output_tokens: number;
  calls: number;
  /** Charge rows whose model had no cost rates — margin is overstated by them. */
  unpriced: number;
}

export interface MarginBreakdown {
  key: string;
  label: string;
  billed_usd: number;
  cost_usd: number;
  margin_usd: number;
  credits: number;
  tokens: number;
  calls: number;
}

export interface MarginReport {
  period: 'hour' | 'day' | 'month' | 'year';
  period_start: string | null;
  period_label: string;
  buckets: MarginBucket[];
  totals: MarginBucket;
  by_product: MarginBreakdown[];
  by_user: MarginBreakdown[];
  by_model: MarginBreakdown[];
  by_version: MarginBreakdown[];
}

export interface QuotaWindow {
  window: 'day' | 'week' | 'month';
  used_tokens: number;
  quota_tokens: number | null;
  pct: number | null;
}

export interface ProviderQuota {
  provider_id: number;
  name: string;
  cost_basis: CostBasis;
  plan_monthly_usd: number | null;
  windows: QuotaWindow[];
}

export interface WhatIfResult {
  days: number;
  rows: number;
  current_billed_usd: number;
  projected_billed_usd: number;
  cost_usd: number;
  current_margin_usd: number;
  projected_margin_usd: number;
  by_product: Record<string, { current_usd: number; projected_usd: number; cost_usd: number }>;
}

/** Our cost per network call by (lookup kind, downloader) — the cost book. */
export interface AdminNetworkCosts {
  kinds: string[];
  downloaders: string[];
  /** "network_cost.<KIND>.<DOWNLOADER>" -> USD per call */
  costs: Record<string, number>;
}


// --- Artifacts: sharing what the product produced -------------------------------
export type ArtifactKind =
  | 'NEWS_BRIEFING'
  | 'MANAGED_PAGE'
  | 'BOARDROOM_REPORT'
  // One report or table the room chat built, shared on its own.
  | 'BOARDROOM_CARD'
  | 'LANDING_PAGE';

export type ArtifactVisibility = 'PRIVATE' | 'PUBLIC';

export interface ShareLink {
  id: number;
  kind: ArtifactKind;
  token: string;
  /** The absolute URL to send someone, built by the API from `READER_BASE_URL`.
   *  The reader is its own app on its own host, so this origin is NOT ours and
   *  cannot be inferred here. */
  url: string;
  recipient?: string | null;
  visibility: ArtifactVisibility;
  news_briefing_id?: number | null;
  managed_page_id?: number | null;
  boardroom_id?: number | null;
  boardroom_deliverable_id?: number | null;
  /** A room's report shared while following a lead: that lead's page. */
  boardroom_lead_id?: number | null;
  boardroom_lead_title?: string | null;
  expires_at?: string | null;
  revoked_at?: string | null;
  is_live: boolean;
  view_count: number;
  last_viewed_at?: string | null;
  created_at: string;
}

export interface BrandProfile {
  id: number;
  name: string;
  is_default: boolean;
  display_name?: string | null;
  logo_url?: string | null;
  primary_color?: string | null;
  accent_color?: string | null;
  background_color?: string | null;
  text_color?: string | null;
  font_family?: string | null;
  footer_text?: string | null;
  website_url?: string | null;
}

// --- the unified artifact read model --------------------------------------------
export type ArtifactState = 'BUILDING' | 'DRAFT' | 'READY' | 'PUBLISHED' | 'FAILED';

export interface ArtifactSummary {
  kind: ArtifactKind;
  id: number;
  title: string;
  state: ArtifactState;
  updated_at?: string | null;
  project_id?: number | null;
  project_name?: string | null;
  source?: string | null;
  share_link_count: number;
  edit_path?: string | null;
  /** Where it is filed on its site. NULL = this kind has no path at all. */
  path_segments?: string[] | null;
  site_id?: number | null;
  site_name?: string | null;
}

export interface ArtifactListResponse {
  items: ArtifactSummary[];
  state_counts: Record<string, number>;
  kind_counts: Record<string, number>;
  total: number;
  /** Distinct path prefixes over the whole shelf, busiest first. */
  path_counts?: Record<string, number>;
  subject_counts?: Record<string, number>;
  site_counts?: Record<string, number>;
}

export interface MarkReadyBlocked {
  page_id: number;
  title?: string | null;
  reason: string;
}

export interface MarkReadyResponse {
  marked_count: number;
  already_ready_count: number;
  blocked: MarkReadyBlocked[];
}

// ============= Link Builder =============
//
// A link map is a named set of links over PAGES, owned by the user and not by
// a project — it spans subjects and sites, which is the entire reason it is a
// top-level tool rather than a tab inside a subject.

export type LinkMapStatus = 'IDLE' | 'RELINKING' | 'FAILED';

/** Where a link's visible text comes from. Chosen once, per map. */
export type LinkAnchorSource = 'RELINK' | 'TITLE' | 'KEYWORD' | 'TITLE_SNIPPET';

/**
 * `FOLLOW` means emit no rel attribute at all — following is the absence of
 * nofollow, and a literal rel="follow" is ignored by crawlers while looking to
 * a reviewer like the link was deliberately marked.
 */
export type ExternalLinkRel = 'SPONSORED' | 'NOFOLLOW' | 'UGC' | 'FOLLOW';

export interface LinkMapScope {
  id: number;
  project_id: number;
  project_name: string;
  /** Null = the whole subject. */
  root_topic_id: number | null;
  root_topic_name: string | null;
  /**
   * The COLOUR KEY. `linkSourceColors` is order-keyed, and the server never
   * renumbers, so a source keeps its colour when others are added or removed.
   */
  order: number;
}

export interface LinkMap {
  id: number;
  name: string;
  description: string | null;
  status: LinkMapStatus;
  last_error: string | null;
  last_relinked_at: string | null;
  /** Placement is a SET — a theme may read a front-matter list and a reader may
   *  still want a Related block. */
  place_block: boolean;
  place_frontmatter: boolean;
  place_thread: boolean;
  frontmatter_key: string | null;
  anchor_source: LinkAnchorSource;
  scopes: LinkMapScope[];
  edge_count: number;
}

/**
 * What it would take to put a removed source back.
 *
 * The snapshot id ALONE cannot do it. Restoring a snapshot recomputes which
 * pages are in scope from the map's current sources, so with the source gone
 * every edge that touched it is skipped — the restore reports success and
 * changes nothing. The scope has to be re-created first, which is why its
 * identity travels with the snapshot id.
 *
 * `order` is here because source colours are order-keyed and a plain re-add
 * takes max(order)+1: without it, undo returns the source in a new colour.
 */
export interface ScopeUndo {
  snapshot_id: number | null;
  project_id: number;
  root_topic_id: number | null;
  order: number;
  label: string;
  deleted_links: number;
}

/** The map after a source was removed, plus how to reverse it. `undo` is null
 *  when nothing was lost — no links touched the departing source. */
export interface LinkMapScopeRemoved extends LinkMap {
  undo: ScopeUndo | null;
}

export interface UndoScopeRemovalResult {
  restored: number;
  skipped: number;
  message: string;
  map: LinkMap;
}

/** One site in the map's site-level graph. `site_id` null = pages with no
 *  deployment plan yet, which is a real bucket rather than missing data. */
export interface SiteNode {
  site_id: number | null;
  name: string;
  page_count: number;
  outbound: number;
  inbound: number;
}

export interface SiteFlow {
  from_site_id: number | null;
  to_site_id: number | null;
  count: number;
}

export interface SiteGraph {
  /** Sorted biggest-first by the server. The order IS the diagram's ring order
   *  and its colour key — re-sorting on the client silently mislabels it. */
  sites: SiteNode[];
  flows: SiteFlow[];
  total_links: number;
  /** Links joining pages on DIFFERENT sites — the reason to build one map over
   *  several subjects at all. */
  crossing_links: number;
}

export interface SegmentEdge extends LinkEdge {
  /** `LinkEdge` names only the target, because the edge pane always knows the
   *  source. In a segment list the source varies row to row. */
  source_title: string;
}

export interface SiteGraphSegment {
  from_site_id: number | null;
  to_site_id: number | null;
  from_name: string;
  to_name: string;
  edges: SegmentEdge[];
  total: number;
  truncated: boolean;
}

export interface LinkMapUpdate {
  name?: string;
  description?: string;
  place_block?: boolean;
  place_frontmatter?: boolean;
  place_thread?: boolean;
  frontmatter_key?: string;
  anchor_source?: LinkAnchorSource;
}

export interface LinkMapPage {
  id: number;
  title: string;
  keyword: string | null;
  subject_path: string;
  /** Which source header this page sits under — the first scope by `order`
   *  that covers it, so a page in two overlapping scopes appears ONCE. */
  scope_id: number;
  site_id: number | null;
  site_name: string | null;
  /**
   * Has this page ever been publicly deployed? A link POINTING AT a page where
   * this is false is dropped at deploy time, so the tree has to show it or the
   * user draws links that silently never ship.
   */
  is_published: boolean;
  /**
   * Has anyone written this page? A page with no content version is INERT
   * here: the AI is never offered it, and nothing can link to it because it
   * cannot deploy. Hiding these is what turns a list of 59 rows into a list of
   * the handful you can actually act on.
   */
  is_written: boolean;
  /** The href that would actually be emitted today. Null when it cannot be
   *  resolved — no site, or no slug. */
  public_path: string | null;
  outbound_count: number;
}

export interface LinkMapNodes {
  scopes: LinkMapScope[];
  pages: LinkMapPage[];
  total: number;
  /** True when `pages` is a prefix of `total`. Must be surfaced — a list that
   *  quietly stops reads as "these are all my pages". */
  truncated: boolean;
}

export interface LinkEdge {
  id: number;
  page_id: number;
  linked_page_id: number;
  anchor_text: string | null;
  rel: string | null;
  /** Survives Relink, and is excluded from what the AI may propose. */
  locked: boolean;
  position: number;
  reason: string | null;
  /** NULL = a human drew it. Set = the AI did. With `locked`, the three states
   *  the UI must distinguish: hand-drawn, AI-then-locked, AI-loose. */
  generated_at: string | null;
  target_title: string;
  target_href: string | null;
  target_is_published: boolean;
  /** Does the target link back? Rendered as a different glyph, so "these two
   *  point at each other" is legible without hunting for the other row. */
  reciprocal: boolean;
}

/**
 * One placement of an external target on one page.
 *
 * Lives beside the internal edges everywhere, because at deploy both land in
 * the same Related block and a reader cannot tell them apart.
 */
export interface ExternalPlacement {
  id: number;
  page_id: number;
  external_target_id: number;
  url: string;
  title: string | null;
  /** Already resolved against the target's default by the server. */
  anchor_text: string | null;
  rel: ExternalLinkRel;
}

export interface LinkLockResult {
  changed: number;
  locked_total: number;
  edge_total: number;
}

export interface LinkClearResult {
  removed: number;
  /** How many of those a person drew or kept. The number that decides whether
   *  this was a mistake, so it is reported separately. */
  hand_made: number;
  /** Pass to restore to put it all back. Null when there was nothing to clear.
   *  Clearing is only defensible as an offer because this exists. */
  snapshot_id: number | null;
  message: string;
}

export interface LinkMapSnapshot {
  id: number;
  kind: 'AUTO' | 'PRE_RESTORE' | 'PRE_SCOPE_CHANGE' | 'PRE_LOCK_ALL';
  edge_count: number;
  created_at: string;
  note: string | null;
}

export interface ExternalTargetInput {
  url: string;
  title?: string;
  default_anchor?: string;
  rel?: ExternalLinkRel;
  notes?: string;
}

export interface ExternalTarget {
  id: number;
  url: string;
  title: string | null;
  default_anchor: string | null;
  rel: ExternalLinkRel;
  notes: string | null;
  placement_count: number;
}

export interface PlaceExternalResult {
  placed: number;
  /** Reported apart from `placed`: selecting 50 pages and placing 3 means the
   *  selection was mostly redundant, and one combined number would hide that. */
  already_present: number;
  /** How many placements an unplace removed. Its own field, because the count
   *  used to come back in `already_present` — the only spare integer — which
   *  told anyone reading the response the opposite of what had happened. */
  removed: number;
  placement_count: number;
}

export interface LinkMapCandidateNode {
  id: number;
  name: string;
  parent_id: number | null;
  depth: number;
  /** Pages under this node AND everything beneath it — what would actually
   *  arrive if it were added. Shown BEFORE adding, so a branch with none is
   *  not discoverable only by adding it and finding nothing changed. */
  page_count: number;
  /** Of those, how many have been written. Only written pages are offered to
   *  the AI, so a subject of unwritten pages contributes nothing to link. */
  written_count: number;
  site_names: string[];
  already_added: boolean;
}

export interface LinkMapCandidate {
  id: number;
  name: string;
  page_count: number;
  written_count: number;
  site_names: string[];
  already_added: boolean;
  /** Only populated when this subject was expanded. */
  nodes: LinkMapCandidateNode[];
}


// ============= Boardroom → subject =============

/** One part of a linked room's board, with what picking it would give you. */
export interface RoomSection {
  kind: string;
  label: string;
  gives: string;
  count: number;
}

/** The boardroom feeding a subject. Null when none is linked — an ordinary
 *  state for most subjects, not an error. */
export interface LinkedRoom {
  boardroom_id: number;
  name: string;
  runs_done: number;
  items_created: number;
  sections: RoomSection[];
}

/** What a section would seed keyword research with. A PREVIEW: seeding writes
 *  rows onto a node and spends crawl budget, so "74 terms" and "these 74
 *  terms" are different decisions. */
export interface SectionSeeds {
  kind: string;
  label: string;
  terms: string[];
  total: number;
}

export interface SeedResult {
  added: number;
  already_there: number;
  message: string;
}


// ============= What feeds a page build =============

export interface BuildContextSource {
  kind: 'trend' | 'boardroom' | 'knowledge' | 'research' | 'documents' | string;
  label: string;
  detail: string | null;
  // Linked AND has something in it. A room with no findings and no room at all
  // look identical from outside, and call for different responses.
  ready: boolean;
  count: number | null;
  href: string | null;
}

export interface BuildContextResponse {
  managed_page_id: number;
  sources: BuildContextSource[];
}


// ============= Review Queue =============

/** One page waiting on a person before it can publish. */
export interface ReviewItem {
  page_id: number;
  title: string;
  /** The version being reviewed. Sent back on approve so a rebuild between
   *  rendering and clicking cannot be signed off by accident. */
  version_id: number | null;
  site_id: number | null;
  site_name: string | null;
  plan_id: number | null;
  plan_name: string | null;
  path: string | null;
  keyword: string | null;
  /** Approved once, then rebuilt — a second look rather than new work. */
  previously_approved: boolean;
  review_note: string | null;
  updated_at: string | null;
}

export interface ReviewQueueResponse {
  items: ReviewItem[];
  total: number;
  /** Does any site on this account ask for review at all? An empty queue means
   *  "all caught up" or "nobody turned this on", and those need different copy. */
  any_site_requires_review: boolean;
}

export interface ReviewActionResponse {
  page_id: number;
  approved: boolean;
  remaining: number;
}


// ============= Releases =============

export type ReleaseState = 'RUNNING' | 'SUCCEEDED' | 'PARTIAL' | 'FAILED' | 'EMPTY';

/** One deploy of one site. Written when the deploy runs, never edited after —
 *  `DeploymentPlan.last_run_details` held only the LAST run, so deploy 14 erased
 *  deploy 13 and "what changed?" had no answer. */
export interface ReleaseSummary {
  id: number;
  managed_site_id: number;
  plan_name: string | null;
  state: ReleaseState;
  started_at: string;
  finished_at: string | null;
  deployed_count: number;
  error_count: number;
  /** Ready, but held back by review — so a thin release is explicable later. */
  held_count: number;
}

export interface ReleasePageEntry {
  managed_page_id: number | null;
  /** As it was AT DEPLOY TIME. A release that re-read the live page would
   *  rewrite its own history on a rename and go blank on a delete. */
  title: string | null;
  path: string | null;
  success: boolean;
  detail: string | null;
  public_url: string | null;
}

export interface ReleaseDetail extends ReleaseSummary {
  detail: string | null;
  pages: ReleasePageEntry[];
}

// ---------------------------------------------------------------------------
// Watch: standing monitors on competitors, products, industries and your brand.
//
// The signal and probe KINDS are strings rather than unions on purpose: they come
// from the server's catalogues (`jcseo/watch/signals.py`, `probes.py`), which are
// deliberately extensible without a migration. A union here would have to be
// edited every time one is added, and would silently exclude the new one from the
// UI until somebody noticed. `WatchCatalogue` is how the client learns them.
// ---------------------------------------------------------------------------

export type WatchSubjectKind = 'COMPANY' | 'PRODUCT' | 'INDUSTRY' | 'OWN_BRAND';
export type WatchSubjectMode = 'DRAFT' | 'LIVE';
/** Loudest first. IMMEDIATE is the only one that mails outside the digest. */
export type WatchTier = 'IMMEDIATE' | 'SAME_DAY' | 'DIGEST';
export type WatchDigestCadence = 'OFF' | 'DAILY' | 'WEEKLY';

export interface WatchProbe {
  id: number;
  subject_id: number;
  kind: string;
  title: string;
  looks_at: string;
  target: string;
  label: string;
  config: Record<string, unknown>;
  is_active: boolean;
  /** What the user asked for. */
  interval_minutes: number;
  /** What will actually happen — the catalogue floors it per kind. */
  effective_interval_minutes: number;
  /** True when those two differ, so the UI can say why rather than look broken. */
  interval_floored: boolean;
  last_run_at: string | null;
  next_due_at: string | null;
  baselined_at: string | null;
  /** A probe with no baseline is watching but not yet comparing. */
  is_baselined: boolean;
  consecutive_failures: number;
  /** Set when the probe paused itself. Always says why. */
  paused_reason: string | null;
  thresholds: Record<string, number>;
}

export interface WatchSubjectMember {
  id: number;
  name: string;
  kind: WatchSubjectKind;
  domain_key: string | null;
  added_by: string;
}

export interface WatchThreshold {
  key: string;
  value: number | null;
  source: 'catalogue' | 'subject' | 'probe';
  is_override: boolean;
  /** "10 (your setting on this probe)" — written server-side beside the rule. */
  described: string;
}

export type WatchVerdictSeverity = 'blocker' | 'warn' | 'note';

/** One thing worth knowing about a watch, and the control that changes it.
 *
 *  `fix` always names a knob: the server refuses to emit a signal without one,
 *  because a diagnosis nobody can act on is a complaint. Rendered, never
 *  re-worded — the sentence and the rule that produced it live together in
 *  `jcseo/watch/verdict.py` so the settings screen and the tuning chat cannot
 *  give two accounts of the same two weeks. */
export interface WatchVerdictSignal {
  severity: WatchVerdictSeverity;
  key: string;
  label: string;
  detail: string;
  fix: string;
}

export interface WatchVerdict {
  /** Absent on the account-wide reading, which is about every watch rather than one. */
  subject_id?: number;
  window_days: number;
  /** The severity to colour the whole panel with. '' when there is nothing to say. */
  worst: WatchVerdictSeverity | '';
  signals: WatchVerdictSignal[];
}

export interface WatchSubject {
  id: number;
  kind: WatchSubjectKind;
  name: string;
  slug: string;
  home_url: string | null;
  /** The registrable domain. Null for an industry, which has no site. */
  domain_key: string | null;
  aliases: string[];
  description: string | null;
  guidance: string | null;
  is_active: boolean;
  mode: WatchSubjectMode;
  alert_email: boolean;
  muted_kinds: string[];
  thresholds: Record<string, number>;
  /** Override keys matching no signal — a typo that would otherwise be silent. */
  unknown_threshold_keys: string[];
  origin: string;
  boardroom_id: number | null;
  boardroom_item_id: number | null;
  unread: number;
  created_at: string | null;
  probes?: WatchProbe[];
  thresholds_in_play?: WatchThreshold[];
  offerable_probes?: string[];
  members?: WatchSubjectMember[];
}

export interface WatchEvent {
  id: number;
  subject_id: number;
  subject_name: string;
  kind: string;
  title: string;
  family: string;
  /** The kinds of check that raise it ("serp_rank", "sitemap"). */
  probe_kinds?: string[];
  family_title: string;
  tier: WatchTier;
  headline: string;
  /** Why it matters, rendered server-side so feed, email and digest agree. */
  so_what: string;
  payload: Record<string, unknown>;
  materiality: number;
  detected_at: string | null;
  seen_at: string | null;
  dismissed_at: string | null;
  boardroom_item_id: number | null;
  /** The check that raised it; its full page list is `getWatchRunPages`. */
  run_id?: number | null;
}

/** Every page one check found added and removed, by section. `section` null is
 *  the one-page sections folded together. Mirrors `timeline.page_changes`. */
export interface WatchRunPages {
  added: { section: string | null; pages: string[] }[];
  removed: { section: string | null; pages: string[] }[];
  added_count: number;
  removed_count: number;
  compared_at: string | null;
}

/** Every search one rank check read, changed first. Mirrors `timeline.search_changes`. */
export interface WatchRunSearches {
  engine: string;
  compared_at: string | null;
  searches: {
    keyword_id: number;
    query: string;
    /** Positions; null = read the results and they were not in the top ten. */
    was: number | null;
    now: number | null;
    was_url: string;
    url: string;
    /** Who else ranks on it. */
    domains: string[];
    /** False on a search the reading before did not have. */
    compared: boolean;
    changed: boolean;
    /** Searches a month; null where never looked up. */
    monthly_searches: number | null;
    /** Estimated visits a month at each position; null without a search count. */
    visits_was: number | null;
    visits_now: number | null;
  }[];
}

/** One check on a watched subject's timeline. Mirrors `jcseo/watch/timeline.py`. */
export interface WatchTimelinePoint {
  run_id: number;
  probe_id: number;
  at: string | null;
  kind: 'changed' | 'quiet' | 'failed' | 'skipped' | 'baseline' | 'running';
  /** "250 pages": the reading in force after this check. */
  reading: string | null;
  /** How the reading moved since the check before it. */
  delta: number | null;
  /** Parts that could not be read: the reading is partial. */
  partial: string[];
  /** This change was measured against a partial reading taken then. */
  suspect_against: string | null;
  /** Why a check failed or did not run, in words. */
  reason: string | null;
  /** The raw error, for admins. */
  error: string | null;
  events: WatchEvent[];
}

export interface WatchTimeline {
  probes: { id: number; kind: string; title: string; label: string }[];
  points: WatchTimelinePoint[];
}

export interface WatchFeedPage {
  events: WatchEvent[];
  /** Cursor rather than an offset, so a page boundary cannot shift mid-scroll. */
  next_before_id: number | null;
  /** Counted by the server over everything not dismissed, a check at a time, as
   *  the feed shows them. Not narrowed by the family or unread filters. */
  counts?: WatchFeedCounts;
}

export interface WatchFamilyCount {
  family: string;
  title: string;
  count: number;
}

export interface WatchFeedCounts {
  families: WatchFamilyCount[];
  unread: number;
}

export interface WatchRun {
  id: number;
  probe_id: number;
  status: 'QUEUED' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
  trigger: 'SCHEDULED' | 'MANUAL' | 'BACKFILL';
  ended_reason: string | null;
  /** The reason in a person's words. "Nothing changed" is not a fault. */
  outcome: string;
  window_from: string | null;
  window_to: string | null;
  started_at: string | null;
  ended_at: string | null;
  observations_written: number;
  events_written: number;
  last_error: string | null;
  attempt: number;
  run_log?: WatchRunLogStep[];
}

export interface WatchRunLogStep {
  at: string;
  message: string;
  [extra: string]: unknown;
}

export interface WatchObservation {
  id: number;
  target_key: string;
  observed_at: string | null;
  last_seen_at: string | null;
  /** How many consecutive checks saw exactly this. */
  seen_count: number;
  value: Record<string, unknown>;
}

export interface WatchCatalogueSignal {
  kind: string;
  title: string;
  family: string;
  tier: WatchTier;
  threshold_key: string | null;
  default_threshold: number | null;
  derivative: boolean;
}

export interface WatchCatalogueProbe {
  kind: string;
  title: string;
  looks_at: string;
  needs_url: boolean;
  costs_external_quota: boolean;
  default_interval_minutes: number;
  min_interval_minutes: number;
  subject_kinds: WatchSubjectKind[];
  signals: WatchCatalogueSignal[];
}

export interface WatchCatalogue {
  shipped_phase: number;
  probes: WatchCatalogueProbe[];
  families: { key: string; title: string; blurb: string }[];
}

export interface WatchPreferences {
  email_enabled: boolean;
  immediate_tier_floor: WatchTier;
  digest_cadence: WatchDigestCadence;
  digest_hour_utc: number;
  digest_weekday: number;
  digest_last_sent_at: string | null;
  quiet_hours: { from: number; to: number; tz: string } | null;
  muted_families: string[];
  max_immediate_per_day: number;
}

export interface WatchUnread {
  total: number;
  by_subject: Record<string, number>;
}

/** What "Track this" hands back from a boardroom card. */
export interface WatchTrackResult {
  subject_id: number;
  slug: string;
  name: string;
  kind: WatchSubjectKind;
  mode: WatchSubjectMode;
  created: boolean;
  probes_created: number;
  is_active: boolean;
  note: string | null;
  /** Only on the room-level call. */
  members_tracked?: number;
  members_skipped?: number;
}

/** What is already known about a company's site, before anybody watches it. Read from pages the
 *  room has already crawled, so it costs no fetch. */
export interface WatchPreview {
  pages_read: number;
  /** Pages carrying captured structured data. The rest predate capture, which is OUR gap, not the
   *  company's — the copy distinguishes the two. */
  pages_with_markup: number;
  open_roles: number;
  growth_roles: string[];
  role_titles: string[];
  priced_pages: number;
  has_organization: boolean;
  rating: number | null;
  review_count: number | null;
  /** Pages we actually read for signup forms. SMALLER than `pages_read` for any company crawled
   *  before form capture shipped, and the reason `asks_for_nothing` can be trusted — those pages
   *  are not evidence of absence. */
  pages_looked_for_capture: number;
  pages_with_capture: number;
  /** Who sends their mail, as lowercase wire keys ('klaviyo', 'activecampaign'). The server writes
   *  the display name into the sentence; don't prettify these in the UI, they're identifiers. */
  capture_vendors: string[];
  /** 'newsletter' | 'discount' | 'gated_asset' | 'waitlist' | 'contact'. */
  capture_kinds: string[];
  /** What they trade for an address, in the site's own words ("10% off"). Null for a plain
   *  newsletter, or when no form was found. */
  capture_offer: string | null;
  /** The providers as a person writes them ("Klaviyo"), for display. */
  capture_vendor_names: string[];
  /** Pages we saw a signup form on, home page first, at most 3. */
  capture_pages: WatchCapturePage[];
  /** We READ their forms and they ask for an email nowhere — a finding, not a gap, and the one
   *  place in this preview where a zero is an answer. Deliberately outside `has_anything`: it is a
   *  weaker reason to press the button, so the copy renders muted. */
  asks_for_nothing: boolean;
  has_anything: boolean;
  /** "2 open roles including SEO Lead, published prices on 1 page and email capture with Klaviyo
   *  for 10% off". */
  headline: string;
  /** The full sentence under the button. `TrackThis` renders this and nothing else, so a new clause
   *  on the server reaches the profile with no change here. */
  call_to_action: string;
}

/** One page we saw a signup form on. */
export interface WatchCapturePage {
  url: string;
  is_home: boolean;
  /** "footer form", "popup and form in the page", or "signup script" (a provider's script with no
   *  form we could read — usually a popup the crawl stripped). */
  where: string;
  vendors: string[];
  offer: string | null;
}

/** What a watch has actually found, for a company's card in a room. The counterpart to
 *  `WatchPreview`: one argues for a watch, the other reports on one, and the server sends whichever
 *  applies — never both. */
export interface WatchLive {
  subject_id: number;
  name: string;
  mode: WatchSubjectMode;
  /** Active, unpaused checks. */
  watching: number;
  paused: { kind: string; reason: string }[];
  /** Active checks that have not taken a first look, so are not yet comparing anything. */
  awaiting_first_look: number;
  unread: number;
  events: WatchEvent[];
  open_roles: number | null;
  roles_by_family?: Record<string, number>;
  roles_seen_at?: string | null;
  /** One line on the state of the watch, worst thing first. */
  headline: string;
}

/** A check running, and what it found — including "nothing", which is the common case and the
 *  reason this event exists at all. `event_type: 'watch_run'`. */
export interface WatchRunEvent {
  event_type: 'watch_run';
  subject_id: number;
  probe_id: number;
  run_id: number;
  status: 'queued' | 'started' | 'finished';
  ended_reason?: string | null;
  /** The reason in a person's words, rendered server-side so every surface agrees. */
  outcome: string;
  /** Zero is normal, and is what tells the client it need NOT refetch the feed. */
  events_written: number;
  message?: string | null;
}

/** The live nudge when a watch finds something. `event_type: 'watch_alert'`. */
export interface WatchAlertEvent {
  event_type: 'watch_alert';
  subject_id: number;
  subject_name: string;
  event_id: number;
  kind: string;
  family: string;
  tier: WatchTier;
  headline: string;
  so_what: string;
  /** Rides along so the badge is right without a second request. */
  unread: number;
}

// ── Search Console & the page lifecycle (api/search_console.py) ──────────────

export type PageSearchState = 'LIVE' | 'RETARGET_DUE' | 'RETIRE_DUE' | 'RETIRED' | 'KEPT';

export interface SearchConsoleStatus {
  connected: boolean;
  property_url: string | null;
  auth_failed_at: string | null;
  last_synced_at: string | null;
  synced_through: string | null;
}

export interface SearchConsoleProperty {
  site_url: string;
  permission_level: string | null;
}

export interface RetargetSuggestion {
  topic_keyword_id: number;
  keyword: string;
  avg_monthly_searches: number | null;
  competition_level: string | null;
  winnable: boolean;
}

export interface SearchQueuePage {
  id: number;
  title: string | null;
  keyword: string | null;
  path: string | null;
  state: PageSearchState | null;
  state_at: string | null;
  clock_at: string | null;
  days_live: number | null;
  retired_at: string | null;
  suggestions: RetargetSuggestion[];
}

export interface SearchSyncRun {
  id: number;
  status: 'RUNNING' | 'OK' | 'WARN' | 'ERROR' | 'SKIP';
  started_at: string | null;
  ended_at: string | null;
  date_from: string | null;
  date_to: string | null;
  rows_written: number;
  pages_matched: number;
  urls_unmatched: number;
  transitions: Record<string, number> | null;
  detail: string | null;
}

export interface SearchReport {
  connection: SearchConsoleStatus;
  live: number;
  with_impressions_28d: number;
  impressions_28d: number;
  clicks_28d: number;
  retarget_due: number;
  retire_due: number;
  retired: number;
  kept: number;
  dead_share: number | null;
  runs: SearchSyncRun[];
  retarget_queue: SearchQueuePage[];
  retire_queue: SearchQueuePage[];
  retired_pages: SearchQueuePage[];
  retire_blocker: string | null;
  retire_notice: string | null;
}

export interface RetireResult {
  retired: number[];
  redeploying: number[];
  not_redeployed: Record<string, string>;
  plans_started: number[];
  plans_busy: number[];
}

// ───────────── The fetch queue (jcseo/fetching) ─────────────

/** What the admins chose for one adapter. */
export interface AdapterChoice {
  on: boolean;
  types: string[];
  concurrency?: number | null;
  per_host?: number | null;
}

export interface AdapterTypeChoice {
  type: string;
  label: string;
  possible: boolean;
  why_not: string;
  /** Works in code but went badly when measured — shown as a warning, never enforced. */
  caution?: string;
}

export interface AdapterInfo {
  adapter: string;
  label: string;
  on: boolean;
  types: string[];
  concurrency: number | null;
  per_host: number | null;
  set_up: boolean;
  not_set_up_because: string | null;
  room: number;
  busy: number;
  devices: { devices: number; tabs: number; house_devices?: number } | null;
  choices: AdapterTypeChoice[];
}

export interface AdaptersResponse {
  adapters: AdapterInfo[];
  fetch_timeout_seconds: number;
  saved: boolean;
}

export interface FetchQueueFailure {
  id: number;
  type: string;
  url: string | null;
  query: string | null;
  origin: string | null;
  error: string | null;
  completed_at: string | null;
}

// ---- Network: Blocks & proxies, and Trace (api/network_trace.py) ----

export interface ProtectionProxy {
  egress: string;
  label: string;
  list: string | null;
  uses: string[];
  enabled: boolean;
  day_tries: number;
  day_blocked: number;
  week_tries: number;
  week_blocked: number;
  resting: { host: string; seconds: number }[];
  blocked_on: { site: string; adapter: string; ago: string; at: string }[];
}

export interface ProtectionSite {
  site: string;
  tools: string[];
  careful: string[];
  skipped: Record<string, string>;
  blocks: {
    key: string;
    adapter: string;
    egress: string | null;
    blocked_ago: string;
    ok_ago: string | null;
    counts: boolean;
  }[];
  updated_ago: string;
}

export interface ProtectionTool {
  tool: string;
  strict: boolean;
  sites: number;
  sites_with_blocks: number;
  proxies_passed_over: string[];
  careful_now: { group: string; open?: number; next_in_s?: number }[];
}

export interface ProtectionReport {
  warnings: string[];
  proxies: ProtectionProxy[];
  sites: ProtectionSite[];
  tools: ProtectionTool[];
  settings: { block_memory_days: number; shared_block_hours: number };
}

export type TraceLevel = 'ok' | 'info' | 'warn' | 'bad';

export interface TraceStep {
  at: number;
  stage: string;
  text: string;
  level: TraceLevel;
  request_id?: number;
  request?: string;
  data?: Record<string, unknown>;
}

/** A "How it was read" note that opens a page's text (GET /network/trace/page/...). */
export interface TraceReadNote {
  text: string;
  page: number;
  show: TracePageShow;
}

export type TracePageShow = 'content' | 'text' | 'parsed';

export interface TracePageText {
  url: string;
  show: TracePageShow;
  chars: number;
  body: string;
  cut: boolean;
  last_crawled: string | null;
}

export interface TraceMeta {
  id: string;
  kind: 'keyword' | 'url';
  subject: string;
  user_id: number;
  started_at: string;
  top?: number;
}

export interface TraceRequestState {
  id: number;
  what: string;
  status: string;
  adapter: string | null;
  error: string | null;
}

export interface TraceRun {
  meta: TraceMeta;
  steps: TraceStep[];
  requests: TraceRequestState[];
}

export type TraceStartResponse =
  | { mode: 'dry'; steps: TraceStep[] }
  | { mode: 'real'; trace_id: string };

export interface FetchQueueResponse {
  waiting: Record<string, Record<string, number>>;
  waiting_total: number;
  oldest_waiting_s: number | null;
  in_flight: Record<string, number>;
  failures: FetchQueueFailure[];
}

export interface ThroughputHour {
  hour: string;
  tries: number;
  ok: number;
  seconds: number;
  avg_s: number | null;
  success_share: number | null;
  busy_share: number | null;
  wait_s: number | null;
  wait_p90_s: number | null;
}

export interface ThroughputAdapter {
  adapter: string;
  label: string;
  room: number;
  tries: number;
  ok: number;
  hours: ThroughputHour[];
}

export interface ThroughputResponse {
  since: string;
  hours: number;
  adapters: ThroughputAdapter[];
}
